import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  FiUsers, FiActivity, FiCheckCircle, FiXCircle, FiSearch,
  FiRefreshCw, FiPower, FiCalendar, FiClock, FiShield, FiLock,
  FiChevronDown, FiChevronRight, FiMail, FiEye, FiEyeOff, FiLogOut,
  FiTrash2, FiAlertTriangle, FiX, FiGrid, FiSend, FiDownload, FiUpload, FiFilter, FiBarChart2, FiDollarSign,
  FiTrendingUp, FiArchive, FiDatabase
} from 'react-icons/fi';
import FeatureAdoptionTab from '../components/superAdmin/FeatureAdoptionTab';
import InsightsTab from '../components/superAdmin/InsightsTab';
import WhatsappInbox from '../components/superAdmin/WhatsappInbox';
import FeatureRequestsTab from '../components/superAdmin/FeatureRequestsTab';
import SurveyManager from '../components/superAdmin/SurveyManager';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MODULE_GROUPS, ALL_MODULES, ALWAYS_ENABLED_IDS, DEFAULT_DISABLED_IDS, isModuleEnabledForCompany } from '../config/moduleRegistry';
import { SYSTEM_ACCOUNTS } from '../config/tranTypes';
import { colors } from '../theme/colors';
import {
  collection, getDocs, doc, updateDoc, query, orderBy, where, documentId, writeBatch, addDoc, serverTimestamp, setDoc, getDoc, getCountFromServer, deleteDoc, Timestamp, limit, deleteField
} from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions, storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import packageJson from '../../package.json';
import { DEFAULT_SIGNUP_SOURCES } from '../config/signupSources';

// Public Cloud Function that counts a scan then redirects to signup. The printed
// banner QR encodes this URL with the campaign id appended.
const QR_SCAN_BASE = 'https://us-central1-amalerp-29324.cloudfunctions.net/qrScan';

// Session auth key
// Login is now fully Firebase Auth + superAdmins Firestore collection (no hardcoded credentials)

const formatCurrency = (amount) => {
  return 'PKR ' + amount.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// ── Billing cycles (single source of truth) ──────────────────────────────
// How many months each paid plan covers. Drives the next-due date and the
// per-month equivalent used in the revenue figures. Adding a cycle here makes
// it work everywhere (income math, next-due, labels).
const PLAN_MONTHS = { monthly: 1, quarterly: 3, 'half-yearly': 6, yearly: 12 };
const PLAN_LABEL = { trial: 'Trial', monthly: 'Monthly', quarterly: 'Quarterly', 'half-yearly': 'Half-Yearly', yearly: 'Yearly' };
// Word for "per <period>" in the amount label (e.g. Recurring / quarter).
const PLAN_PERIOD_WORD = { monthly: 'month', quarterly: 'quarter', 'half-yearly': '6 months', yearly: 'year' };
// A paid plan is anything with a real billing cycle (not trial / not blank).
const isPaidPlan = (plan) => !!plan && plan !== 'trial' && plan !== 'expired';
// Months a plan covers (0 for trial/unknown).
const planMonths = (plan) => PLAN_MONTHS[plan] || 0;
const planLabel = (plan) => PLAN_LABEL[plan] || (plan ? plan : '—');

// USD formatter for AI cost — shows more precision for small amounts so a
// company with a few cents of spend doesn't read as $0.00.
const formatUSD = (amount) => {
  const n = Number(amount) || 0;
  if (n === 0) return '$0';
  if (n < 0.01) return '<$0.01';
  if (n < 1) return '$' + n.toFixed(2);
  if (n < 100) return '$' + n.toFixed(2);
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const SA_AUTH_KEY = 'amalerp_sa_auth';

// ── COST: company-list session cache ────────────────────────────────────────
// loadCompanies() is the single most expensive routine in this panel: 1 list read
// + per company a full users read and 1 transactions count (was 3 counts + a
// usageStats doc until 2026-10-03). It used to be
// re-fired by every incidental action (suspend/activate, subscription save, shadow
// close, Revenue refresh), so the panel could burn 10k+ reads in a minute.
// We keep the ENRICHED, UNFILTERED list at module scope for 10 minutes; the
// sales-user scoping filter and the stats roll-up are recomputed on every call so
// a cached load produces exactly the same numbers as a fresh one.
// Explicit "Refresh" buttons pass force=true. Mutations patch or drop the cache
// below, so a cached load can never show stale data.
let _companiesCache = null; // { at: number, list: [] }
const COMPANIES_TTL_MS = 10 * 60 * 1000;

// Apply a just-saved change to the cached copy of one company, so the next cached
// load shows the new value without re-reading the whole platform.
const patchCompaniesCache = (companyId, patch) => {
  if (!_companiesCache) return;
  _companiesCache.list = _companiesCache.list.map(c => (c.id === companyId ? { ...c, ...patch } : c));
};

// Drop the cache when a change is too broad (or too structural) to patch safely —
// correctness beats a cache hit. The next load re-reads from Firestore.
const invalidateCompaniesCache = () => { _companiesCache = null; };

const SuperAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try { return sessionStorage.getItem(SA_AUTH_KEY) === 'true'; } catch { return false; }
  });
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [saRole, setSaRole] = useState(() => { try { return sessionStorage.getItem('sa_role') || ''; } catch { return ''; } }); // 'superadmin' | 'sales'
  const [saUser, setSaUser] = useState(null); // { email, displayName, role }
  const isOwner = saRole === 'superadmin'; // true = full access, false = sales view-only
  // Sales-role super admins may use the WhatsApp Inbox (reply to clients) but NOT
  // bulk WhatsApp broadcasts or other owner-only tools.
  const canWhatsappInbox = isOwner || saRole === 'sales';
  // Sales logins may also suspend / re-activate the companies they can see.
  const canToggleStatus = isOwner || saRole === 'sales';
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('companies');
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    suspendedCompanies: 0,
    totalUsers: 0
  });
  // (2026-10-02) Per-company transaction analytics (the 6 count boxes, the
  // Transactions tab, per-user transaction counts) were removed: they read EVERY
  // transaction of a company (up to ~16,000 reads per open) for little value.
  // COST telemetry: approximate Firestore reads the last company-list load cost,
  // shown next to the header Refresh button so the owner can see the panel's own
  // bill. `lastLoadCached` = served from the module cache (zero reads).
  const [lastLoadReads, setLastLoadReads] = useState(null);
  const [lastLoadCached, setLastLoadCached] = useState(false);
  const [sortColumn, setSortColumn] = useState(null); // 'created' | 'lastLogin' | 'lastActivity'
  const [sortDirection, setSortDirection] = useState('desc');

  // Companies list pagination (50 per page; filters/search operate on the full set)
  const [companyPage, setCompanyPage] = useState(1);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'suspended'
  const [subscriptionFilter, setSubscriptionFilter] = useState('all'); // 'all' | 'trial' | 'monthly' | 'yearly' | 'expired'
  const [cityFilter, setCityFilter] = useState('all');
  const [heardFromFilter, setHeardFromFilter] = useState('all'); // marketing attribution filter
  const [showFilters, setShowFilters] = useState(false);

  // Module Matrix tab — two views: by-company (default) and by-module
  const [matrixViewMode, setMatrixViewMode] = useState('by-company'); // 'by-company' | 'by-module'
  const [matrixSearchTerm, setMatrixSearchTerm] = useState('');
  const [matrixSubFilter, setMatrixSubFilter] = useState('all'); // 'all' | 'trial' | 'monthly' | 'yearly' | 'suspended'
  const [matrixCompanyFocus, setMatrixCompanyFocus] = useState('all'); // company id or 'all'
  const [matrixModuleFocus, setMatrixModuleFocus] = useState('all'); // module id or 'all'
  const [matrixInsight, setMatrixInsight] = useState('all'); // 'all' | 'universal' | 'niche' | 'unused'
  const [matrixExpandedRows, setMatrixExpandedRows] = useState(new Set()); // company ids whose drill-down is open

  // Per-company drill-down sub-tab (resets to 'overview' when a different
  // company is expanded). Only one company is expanded at a time so single
  // string is fine.
  const [companyDetailTab, setCompanyDetailTab] = useState('overview');

  // Shadow company state
  const [shadowModal, setShadowModal] = useState(null); // { companyId, companyName }
  const [shadowCreating, setShadowCreating] = useState(false);
  const [shadowProgress, setShadowProgress] = useState('');

  // Usage tab state
  const [usageData, setUsageData] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usagePeriod, setUsagePeriod] = useState('today'); // 'today' | 'week' | 'month'
  const [usageSortColumn, setUsageSortColumn] = useState('today');
  const [usageSortDirection, setUsageSortDirection] = useState('desc');
  const [usageSearchTerm, setUsageSearchTerm] = useState('');
  // Platform DAU / MAU (distinct active users) + DAU/MAU stickiness ratio
  const [activeUserStats, setActiveUserStats] = useState({ dau: 0, wau: 0, mau: 0, ratio: 0 });

  // Clear data modal state
  const [clearModal, setClearModal] = useState(null); // { type: 'transactions'|'master'|'all', companyId, companyName }
  const [clearPassword, setClearPassword] = useState('');
  const [clearConfirmName, setClearConfirmName] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearProgress, setClearProgress] = useState('');
  const [clearError, setClearError] = useState('');

  // Manage Modules modal state
  const [modulesModal, setModulesModal] = useState(null); // { companyId, companyName }
  const [moduleSettings, setModuleSettings] = useState({});
  const [savingModules, setSavingModules] = useState(false);

  // Currency settings modal state
  const [currencyModal, setCurrencyModal] = useState(null); // { companyId, companyName }
  const [currencyForm, setCurrencyForm] = useState({ currencyCode: 'PKR', currencySymbol: 'Rs.', taxLabel: 'GST', brandLogoUrl: '', brandName: '', hidePrintBranding: false });
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // FBR POS config modal state
  const [fbrModal, setFbrModal] = useState(null); // { companyId, companyName }
  const [fbrForm, setFbrForm] = useState({ posId: '', env: 'live', endpoint: '', token: '', hasToken: false });
  // FBR Digital Invoicing config modal state (standalone, separate from FBR POS)
  const [diModal, setDiModal] = useState(null); // { companyId, companyName }
  const [savingDi, setSavingDi] = useState(false);
  const [diForm, setDiForm] = useState({ sellerNtn: '', endpoint: '', saleType: '', token: '', hasToken: false });
  const [savingFbr, setSavingFbr] = useState(false);

  // Sales-user company access (per-company deny list). `salesUsers` = superAdmins
  // with role 'sales'. A company's `salesHidden: [uid]` hides it from those sales
  // users (default: visible to all sales users). Managed per-company by the owner.
  const [salesUsers, setSalesUsers] = useState([]);
  const [salesAccessModal, setSalesAccessModal] = useState(null); // { companyId, companyName }
  const [salesAccessHidden, setSalesAccessHidden] = useState([]); // uids hidden from the modal's company
  const [savingSalesAccess, setSavingSalesAccess] = useState(false);

  // Email modal state
  const [emailModal, setEmailModal] = useState(null); // null | { mode: 'single', company } | { mode: 'bulk' }
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(''); // success/error message
  const [emailStats, setEmailStats] = useState(null); // { sent, error, pending } across mailQueue
  const [emailStatsLoading, setEmailStatsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [emailFilter, setEmailFilter] = useState('all'); // 'all' | 'paid' | 'trial'

  // Bulk WhatsApp modal state (mirrors email; sends approved templates via Meta)
  const [waModal, setWaModal] = useState(null); // null | { mode:'single', company } | { mode:'bulk' }
  const [waTemplates, setWaTemplates] = useState([]); // approved templates from Meta
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  const [waTemplateName, setWaTemplateName] = useState('');
  const [waParams, setWaParams] = useState([]); // variable values (index 0 = {{1}})
  const [waFilter, setWaFilter] = useState('all'); // 'all' | 'paid' | 'trial'
  const [waSending, setWaSending] = useState(false);
  const [waStatus, setWaStatus] = useState('');
  const [waStats, setWaStats] = useState(null); // { sent, error, pending, skipped, delivered, deliveryFailed }
  const [waStatsLoading, setWaStatsLoading] = useState(false);
  const [waFailures, setWaFailures] = useState([]); // recent failed rows + reason
  const [optOutInput, setOptOutInput] = useState('');
  const [optOutMsg, setOptOutMsg] = useState('');
  // Subscription-date targeting (send to fewer → cost less)
  const [waDateField, setWaDateField] = useState('none'); // 'none' | 'next' | 'last'
  const [waFrom, setWaFrom] = useState(''); // yyyy-mm-dd
  const [waTo, setWaTo] = useState('');     // yyyy-mm-dd

  // Subscription modal state
  const [subModal, setSubModal] = useState(null); // { companyId, companyName, current }
  const [subForm, setSubForm] = useState({
    plan: 'trial',
    lastPaymentDate: '',
    firstPaymentDate: '',
    amount: '',
    setupFee: '',
    notes: '',
    maxBranches: '',
    maxUsers: '',
    maxPosCounters: '',
    salesPersonId: '',
    paymentHistory: [],
  });
  // Inline form for adding a new payment-history entry (inside the sub modal)
  const [paymentDraft, setPaymentDraft] = useState({ date: '', amount: '', type: 'renewal', notes: '' });

  // Revenue-tab client table: search, status filter, and column sorting.
  const [revSearch, setRevSearch] = useState('');
  const [revStatusFilter, setRevStatusFilter] = useState('all'); // all | active | overdue | suspended
  const [revSort, setRevSort] = useState({ field: 'nextDueTs', dir: 'asc' }); // default: soonest due first
  const toggleRevSort = (field) => setRevSort(s =>
    s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }
  );
  const sortArrow = (field) => revSort.field === field ? (revSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const [savingSub, setSavingSub] = useState(false);

  // Sales Persons (top-level Firestore collection — used to attribute each
  // company's subscription to whoever sold it). Loaded once after auth.
  const [salesPersons, setSalesPersons] = useState([]);
  const [salesPersonsModal, setSalesPersonsModal] = useState(false);
  const [newSalesPersonName, setNewSalesPersonName] = useState('');
  const [savingSalesPerson, setSavingSalesPerson] = useState(false);
  const [salesPersonFilter, setSalesPersonFilter] = useState('all');

  // Delete company modal state
  const [deleteModal, setDeleteModal] = useState(null); // { companyId, companyName }
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Backup state
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupProgress, setBackupProgress] = useState('');
  const [backupResult, setBackupResult] = useState(null);
  const [includeStorage, setIncludeStorage] = useState(false);

  // Restore state
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreManifest, setRestoreManifest] = useState(null);
  const [restoreManifestError, setRestoreManifestError] = useState('');
  const [restoreMode, setRestoreMode] = useState('append');
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState('');
  const [restoreResult, setRestoreResult] = useState(null);

  // New-company default modules (super-admin-managed template that replaces the
  // hardcoded default set as the source of "what new sign-ups start with").
  const [showDefaultsModal, setShowDefaultsModal] = useState(false);
  const [defaultsSettings, setDefaultsSettings] = useState({});
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [applyingDefaults, setApplyingDefaults] = useState(false);

  // Signup "How did you hear about us?" source list (super-admin-managed).
  // Stored at platformConfig/signupOptions → { heardFromOptions: [...] }.
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [sourceList, setSourceList] = useState([]);
  const [newSource, setNewSource] = useState('');
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [savingSources, setSavingSources] = useState(false);

  // QR marketing campaigns (banner scan tracking).
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCampaigns, setQrCampaigns] = useState([]);
  const [qrImages, setQrImages] = useState({}); // campaignId → PNG data URL
  const [newCampaignName, setNewCampaignName] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  // Header "Manage" dropdown (groups Backup/Restore/Defaults/Sources/QR/Surveys).
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showSurveys, setShowSurveys] = useState(false);

  // Backup scope: full platform, one company, paid-only, or active-only.
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupScope, setBackupScope] = useState('all');
  const [backupCompanyId, setBackupCompanyId] = useState('');

  // Super Admin Login — Firebase Auth + superAdmins collection check
  const handleSuperAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    if (!loginEmail || !loginPassword) {
      setLoginError('Please fill in all fields');
      return;
    }

    setLoginLoading(true);
    try {
      // Step 1: Sign in via Firebase Auth
      const cred = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);

      // Step 2: Check superAdmins collection for this user
      const saRef = doc(db, 'superAdmins', cred.user.uid);
      const saDoc = await getDoc(saRef);

      if (!saDoc.exists() || !saDoc.data().isSuperAdmin || saDoc.data().isActive === false) {
        // Not an authorized SA user — sign out and reject
        await signOut(auth);
        setLoginError('Access denied. You are not authorized for the Super Admin panel.');
        return;
      }

      const saData = saDoc.data();
      const role = saData.role || 'superadmin'; // default to superadmin for existing docs
      setSaRole(role);
      try { sessionStorage.setItem('sa_role', role); } catch {}
      setSaUser({ email: saData.email, displayName: saData.displayName || '', role });
      setIsAuthenticated(true);
      try { sessionStorage.setItem(SA_AUTH_KEY, 'true'); sessionStorage.setItem('sa_uid', cred.user.uid); } catch {}
    } catch (error) {
      console.error('Super Admin login error:', error);
      if (error.code === 'auth/user-not-found') {
        setLoginError('Account not found.');
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setLoginError('Invalid email or password.');
      } else {
        setLoginError('Login failed: ' + error.message);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Super Admin Logout
  const handleSuperAdminLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setIsAuthenticated(false);
    setSaRole('');
    setSaUser(null);
    try { sessionStorage.removeItem(SA_AUTH_KEY); sessionStorage.removeItem('sa_uid'); } catch {}
    try { sessionStorage.removeItem('sa_role'); } catch {}
    setLoginEmail('');
    setLoginPassword('');
    setCompanies([]);
    setSelectedCompany(null);
    setCompanyUsers([]);
    // Never hand one super-admin's cached list to whoever logs in next.
    invalidateCompaniesCache();
    setLastLoadReads(null);
    setLastLoadCached(false);
  };

  // Load all companies
  // COST: pass force=true to bypass the 10-minute module cache (explicit Refresh
  // buttons only). Everything else goes through the cache — see _companiesCache.
  const loadCompanies = useCallback(async (force = false) => {
    // Sales scoping + the stats roll-up run on EVERY call (cached or fresh) against
    // the unfiltered list, so cached data lands in state exactly as a fresh load would.
    const applyCompaniesList = (companiesList) => {
      // userCount === the users snapshot size counted during the real load, so this
      // reproduces the old `totalUsers` accumulator for cached loads too.
      const totalUsers = companiesList.reduce((sum, c) => sum + (c.userCount || 0), 0);

      // Sales-user scoping: a 'sales' super-admin only sees companies that don't
      // list their uid in salesHidden (default-all). Owner sees everything.
      // Read role/uid from session to avoid stale-closure (this callback has [] deps).
      let visibleList = companiesList;
      try {
        const sessRole = sessionStorage.getItem('sa_role');
        const sessUid = sessionStorage.getItem('sa_uid') || auth.currentUser?.uid;
        if (sessRole === 'sales' && sessUid) {
          visibleList = companiesList.filter(c => !(Array.isArray(c.salesHidden) && c.salesHidden.includes(sessUid)));
        }
      } catch {}

      setCompanies(visibleList);
      const trialCount = visibleList.filter(c => !c.subscription || c.subscription.plan === 'trial').length;
      const paidCount = visibleList.filter(c => c.subscription && isPaidPlan(c.subscription.plan)).length;
      setStats({
        totalCompanies: visibleList.length,
        activeCompanies: visibleList.filter(c => c.status !== 'suspended').length,
        suspendedCompanies: visibleList.filter(c => c.status === 'suspended').length,
        totalUsers,
        trialCount,
        paidCount
      });
    };

    try {
      // COST: cache hit — no Firestore call at all.
      if (!force && _companiesCache && (Date.now() - _companiesCache.at) < COMPANIES_TTL_MS) {
        applyCompaniesList(_companiesCache.list);
        setLastLoadCached(true);
        return;
      }

      setLoading(true);
      // COST telemetry: count the reads this load actually pays for.
      let readsUsed = 0;
      const companiesRef = collection(db, 'companies');
      const companiesSnapshot = await getDocs(query(companiesRef, orderBy('createdAt', 'desc')));
      readsUsed += companiesSnapshot.size;

      const companiesList = await Promise.all(
        companiesSnapshot.docs.map(async (companyDoc) => {
          const companyData = { id: companyDoc.id, ...companyDoc.data() };

          // Get user count for this company
          const usersRef = collection(db, 'companies', companyDoc.id, 'users');
          const usersSnapshot = await getDocs(usersRef);
          companyData.userCount = usersSnapshot.size;
          // COST telemetry: this company's share of the bill — its users list,
          // plus the 1 count query below.
          readsUsed += usersSnapshot.size + 1;

          // Get last login (most recent user login to this company)
          // and pull the owner's WhatsApp number for display in Company Information.
          let lastLogin = null;
          let ownerWhatsapp = '';
          let ownerEmail = '';
          let firstUserEmail = '';
          const userSearchParts = [];
          usersSnapshot.docs.forEach(userDoc => {
            const userData = userDoc.data();
            if (userData.lastLogin) {
              const loginDate = new Date(userData.lastLogin);
              if (!lastLogin || loginDate > lastLogin) {
                lastLogin = loginDate;
              }
            }
            if (companyData.ownerId && userDoc.id === companyData.ownerId && userData.whatsappNumber) {
              ownerWhatsapp = userData.whatsappNumber;
            }
            // Capture user login emails so bulk email can reach the actual account
            // holder (the company email field is often blank). Prefer the owner.
            if (userData.email) {
              if (!firstUserEmail) firstUserEmail = userData.email;
              if (companyData.ownerId && userDoc.id === companyData.ownerId) ownerEmail = userData.email;
            }
            // Collect every user's name/email/phone so the company search can match them.
            [userData.displayName, userData.name, userData.email, userData.phone, userData.mobile, userData.whatsappNumber]
              .forEach(v => { if (v) userSearchParts.push(String(v)); });
          });
          companyData.userSearchText = userSearchParts.join(' ').toLowerCase();
          companyData.lastLogin = lastLogin;
          if (ownerWhatsapp && !companyData.whatsappNumber) {
            companyData.whatsappNumber = ownerWhatsapp;
          }
          // Resolved recipient for Email-All: owner's login email first, then any
          // user's login email, then the company email as a last resort.
          companyData.contactEmail = ownerEmail || firstUserEmail || companyData.email || companyData.companyEmail || '';

          // Document count = number of transactions (2026-10-03). It used to also
          // count ledger rows + stock rows — 3 live counts per company, the main
          // cause of the 3–5 minute load and of most of this panel's reads.
          // Transactions alone show a company's activity just as well.
          try {
            const tranCount = await getCountFromServer(collection(db, 'companies', companyDoc.id, 'transactions'));
            companyData.totalDocs = tranCount.data().count;
          } catch {
            companyData.totalDocs = 0;
          }

          // (2026-10-02) "Usage Today" column removed — it cost one extra read
          // per company on every load. Daily usage stays in the Company Usage tab.

          return companyData;
        })
      );

      // COST: cache the UNFILTERED enriched list — the sales scoping filter is
      // re-applied on every call, so one cache serves owner and sales logins alike.
      _companiesCache = { at: Date.now(), list: companiesList };

      applyCompaniesList(companiesList);
      setLastLoadReads(readsUsed);
      setLastLoadCached(false);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load users for selected company
  const loadCompanyUsers = async (companyId) => {
    try {
      const usersRef = collection(db, 'companies', companyId, 'users');
      const usersSnapshot = await getDocs(query(usersRef, orderBy('displayName')));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompanyUsers(usersList);
    } catch (error) {
      console.error('Error loading company users:', error);
    }
  };

  // Load usage data for all companies (last 30 days)
  // ── Firestore READ telemetry (Reads tab) ──────────────────────────────────
  // Per-company daily read counts attributed to the screen that caused them,
  // written by src/utils/readTelemetry.js into companies/{id}/readStats/{date}.
  const [readsData, setReadsData] = useState([]);
  const [readsLoading, setReadsLoading] = useState(false);
  const [expandedReadsCompany, setExpandedReadsCompany] = useState(null);
  // Fleet-wide reads per day (day-on-day, like the Firebase console graph).
  const [readsDaily, setReadsDaily] = useState([]);
  // Header sorting for the reads table.
  const [readsSortColumn, setReadsSortColumn] = useState('week');
  const [readsSortDirection, setReadsSortDirection] = useState('desc');
  // Which day the expanded per-screen breakdown reflects. null = newest day
  // (today); 'week' = the 7-day merged total; otherwise a 'YYYY-MM-DD' string.
  const [expandedReadsDay, setExpandedReadsDay] = useState(null);

  const READS_WINDOW_DAYS = 14; // day-on-day window shown in the chart

  const loadReadsData = useCallback(async () => {
    if (companies.length === 0) return;
    try {
      setReadsLoading(true);
      const today = new Date();
      const dates = []; // newest → oldest
      for (let i = 0; i < READS_WINDOW_DAYS; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        dates.push(d.toISOString().split('T')[0]);
      }
      const todayStr = dates[0];
      const weekDates = new Set(dates.slice(0, 7)); // last 7 days for the "7 Days" column
      const oldestStr = dates[dates.length - 1];

      // Fleet total reads per day (summed across all companies).
      const dailyMap = {};
      dates.forEach((ds) => { dailyMap[ds] = 0; });

      const list = await Promise.all(
        companies.map(async (company) => {
          let todayReads = 0;
          let weekReads = 0;
          const routes = {}; // merged 7-day per-screen counts (for the "7 Days" view)
          const byDayRaw = {}; // { date: { total, routes:{route:count} } } — per-day breakdown
          try {
            const snap = await getDocs(query(
              collection(db, 'companies', company.id, 'readStats'),
              where(documentId(), '>=', oldestStr),
              where(documentId(), '<=', todayStr)
            ));
            snap.docs.forEach((d) => {
              const data = d.data();
              const total = data.total || 0;
              // Fleet day-on-day total (whole window).
              if (dailyMap[d.id] !== undefined) dailyMap[d.id] += total;
              // Per-company today total for the table.
              if (d.id === todayStr) todayReads = total;
              // Per-day per-screen breakdown (whole window) for the expand view.
              byDayRaw[d.id] = { total, routes: data.routes || {} };
              if (weekDates.has(d.id)) {
                weekReads += total;
                Object.entries(data.routes || {}).forEach(([r, n]) => {
                  routes[r] = (routes[r] || 0) + (n || 0);
                });
              }
            });
          } catch (e) { /* company without stats yet */ }

          const topRoutes = Object.entries(routes).sort((a, b) => b[1] - a[1]);
          // Descending-by-date list (newest first) of { date, label, total, routes }.
          const byDay = dates
            .filter((ds) => byDayRaw[ds])
            .map((ds) => {
              const [, m, day] = ds.split('-');
              const rec = byDayRaw[ds];
              return {
                date: ds,
                label: `${day}/${m}`,
                total: rec.total || 0,
                routes: Object.entries(rec.routes).sort((a, b) => b[1] - a[1])
              };
            });
          return {
            id: company.id,
            companyName: company.companyName || 'Unknown',
            today: todayReads,
            week: weekReads,
            topRoute: topRoutes[0] ? topRoutes[0][0] : null,
            topRouteReads: topRoutes[0] ? topRoutes[0][1] : 0,
            routes: topRoutes,
            byDay
          };
        })
      );

      // Build the ascending daily series for the chart.
      const dailySeries = dates
        .slice()
        .reverse()
        .map((ds) => {
          const [, m, day] = ds.split('-');
          return { date: ds, label: `${day}/${m}`, total: dailyMap[ds] || 0 };
        });
      setReadsDaily(dailySeries);

      list.sort((a, b) => b.week - a.week);
      setReadsData(list);
    } catch (error) {
      console.error('Error loading read stats:', error);
    } finally {
      setReadsLoading(false);
    }
  }, [companies]);

  const handleReadsSort = (column) => {
    if (readsSortColumn === column) {
      setReadsSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setReadsSortColumn(column);
      setReadsSortDirection(column === 'company' ? 'asc' : 'desc');
    }
  };

  const sortedReadsData = useMemo(() => {
    const rows = readsData.filter((c) => c.week > 0);
    const dir = readsSortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av; let bv;
      if (readsSortColumn === 'company') { av = a.companyName.toLowerCase(); bv = b.companyName.toLowerCase(); return av < bv ? -dir : av > bv ? dir : 0; }
      if (readsSortColumn === 'today') { av = a.today; bv = b.today; }
      else { av = a.week; bv = b.week; }
      return (av - bv) * dir;
    });
  }, [readsData, readsSortColumn, readsSortDirection]);

  const loadUsageData = useCallback(async () => {
    if (companies.length === 0) return;
    try {
      setUsageLoading(true);
      const today = new Date();
      const dates = [];
      for (let i = 0; i < 30; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        dates.push(d.toISOString().split('T')[0]);
      }
      const todayStr = dates[0];
      const weekDates = dates.slice(0, 7);
      const monthDates = dates.slice(0, 30);

      const usageList = await Promise.all(
        companies.map(async (company) => {
          let todayViews = 0;
          let weekViews = 0;
          let monthViews = 0;
          let lastActivity = null;

          // ONE range query for the company's last 30 days of usage (doc IDs are
          // ISO dates, which sort chronologically). This replaces 30 separate
          // point-reads per company — the old approach fired hundreds of
          // concurrent reads across all companies and could stall the tab.
          let usageDocs = [];
          try {
            const snap = await getDocs(query(
              collection(db, 'companies', company.id, 'usageStats'),
              where(documentId(), '>=', monthDates[monthDates.length - 1]),
              where(documentId(), '<=', todayStr)
            ));
            usageDocs = snap.docs;
          } catch (e) {
            usageDocs = [];
          }

          usageDocs.forEach((usageDoc) => {
            const data = usageDoc.data();
            const views = data.pageViews || 0;
            const dateStr = usageDoc.id;

            if (dateStr === todayStr) todayViews = views;
            if (weekDates.includes(dateStr)) weekViews += views;
            monthViews += views;

            if (data.lastActivity && (!lastActivity || data.lastActivity > lastActivity)) {
              lastActivity = data.lastActivity;
            }
          });

          return {
            id: company.id,
            companyName: company.companyName || 'Unknown',
            today: todayViews,
            week: weekViews,
            month: monthViews,
            totalDocs: company.totalDocs || 0,
            lastActivity
          };
        })
      );

      setUsageData(usageList);

      // ---- Platform DAU / MAU (distinct active users) + stickiness ratio ----
      // Reads the last 30 daily platformActivity docs (uid->timestamp maps).
      try {
        const dauSet = new Set();
        const wauSet = new Set();
        const mauSet = new Set();
        const actSnap = await getDocs(query(
          collection(db, 'platformActivity'),
          where(documentId(), '>=', monthDates[monthDates.length - 1]),
          where(documentId(), '<=', todayStr)
        ));
        actSnap.docs.forEach((snap) => {
          const dateStr = snap.id;
          Object.keys(snap.data().users || {}).forEach(uid => {
            mauSet.add(uid);
            if (weekDates.includes(dateStr)) wauSet.add(uid);
            if (dateStr === todayStr) dauSet.add(uid);
          });
        });
        const dau = dauSet.size;
        const mau = mauSet.size;
        setActiveUserStats({
          dau,
          wau: wauSet.size,
          mau,
          ratio: mau > 0 ? Math.round((dau / mau) * 1000) / 10 : 0 // % with 1 decimal
        });
      } catch (e) {
        console.error('Error computing DAU/MAU:', e);
      }
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  }, [companies]);

  // Toggle company status
  const toggleCompanyStatus = async (companyId, currentStatus) => {
    if (!canToggleStatus) return;
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    const action = newStatus === 'suspended' ? 'suspend' : 'activate';

    if (!window.confirm(`Are you sure you want to ${action} this company?`)) {
      return;
    }

    try {
      const statusPatch = {
        status: newStatus,
        statusChangedAt: new Date().toISOString(),
        statusChangedBy: auth.currentUser?.uid
      };
      await updateDoc(doc(db, 'companies', companyId), statusPatch);

      // COST: patch the one row we just changed into the cached list, then let the
      // refresh below serve from cache instead of re-reading all 426 companies.
      patchCompaniesCache(companyId, statusPatch);

      // Refresh companies list
      loadCompanies();

      if (selectedCompany?.id === companyId) {
        setSelectedCompany(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating company status:', error);
      alert('Failed to update company status');
    }
  };

  // Subscription helpers
  const getSubscriptionStatus = (company) => {
    const sub = company.subscription;
    if (!sub || sub.plan === 'trial') {
      const trialEnd = sub?.trialEndDate ? new Date(sub.trialEndDate) : new Date(new Date(company.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
      const now = new Date();
      const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 0) return { label: 'Trial Expired', color: colors.error.main, daysLeft: 0 };
      return { label: `Trial - ${daysLeft}d left`, color: colors.warning.main, daysLeft };
    }
    if (sub.nextPaymentDate) {
      const due = new Date(sub.nextPaymentDate);
      const now = new Date();
      if (due < now) return { label: `${planLabel(sub.plan)} - Expired`, color: colors.error.main };
    }
    return { label: `${planLabel(sub.plan)} - Active`, color: colors.success.main };
  };

  const openSubModal = (company) => {
    const sub = company.subscription || {};
    setSubModal({ companyId: company.id, companyName: company.companyName, current: sub });
    setSubForm({
      plan: sub.plan || 'trial',
      lastPaymentDate: sub.lastPaymentDate ? sub.lastPaymentDate.substring(0, 10) : '',
      firstPaymentDate: sub.firstPaymentDate ? sub.firstPaymentDate.substring(0, 10) : '',
      amount: sub.amount || '',
      setupFee: sub.setupFee || '',
      notes: sub.notes || '',
      // Package limits — 0 / blank means unlimited.
      maxBranches: company.maxBranches ?? '',
      maxUsers: company.maxUsers ?? '',
      maxPosCounters: company.maxPosCounters ?? '',
      salesPersonId: sub.salesPersonId || '',
      paymentHistory: Array.isArray(sub.paymentHistory) ? sub.paymentHistory.slice() : [],
    });
    setPaymentDraft({ date: '', amount: '', type: 'renewal', notes: '' });
  };

  const saveSubscription = async () => {
    if (!isOwner) return;
    if (!subModal) return;
    setSavingSub(true);
    try {
      let nextPaymentDate = null;
      if (subForm.plan !== 'trial' && subForm.lastPaymentDate) {
        const paid = new Date(subForm.lastPaymentDate);
        // Add the plan's month span (1 / 3 / 6 / 12). JS Date rolls the year over
        // automatically when the month index exceeds 11.
        const months = planMonths(subForm.plan) || 12;
        nextPaymentDate = new Date(paid.getFullYear(), paid.getMonth() + months, paid.getDate()).toISOString();
      }
      // Look up the chosen sales person so we can denormalize the name onto
      // the company doc — saves a join when displaying the list / filtering.
      const chosenSp = salesPersons.find(p => p.id === subForm.salesPersonId);

      // Backfill firstPaymentDate from lastPaymentDate when missing — so
      // legacy companies don't lose their year-1 anchor.
      const firstPaymentDate = subForm.firstPaymentDate
        ? new Date(subForm.firstPaymentDate).toISOString()
        : (subForm.lastPaymentDate ? new Date(subForm.lastPaymentDate).toISOString() : null);

      // Normalize payment history: sort by date, drop empties, ensure ISO date
      const normalizedHistory = (subForm.paymentHistory || [])
        .filter(p => p && p.date && Number(p.amount))
        .map(p => ({
          date: new Date(p.date).toISOString(),
          amount: Number(p.amount),
          type: p.type || 'renewal',
          notes: p.notes || '',
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const subData = {
        plan: subForm.plan,
        trialEndDate: subForm.plan === 'trial'
          ? (subModal.current.trialEndDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
          : (subModal.current.trialEndDate || null),
        lastPaymentDate: subForm.lastPaymentDate ? new Date(subForm.lastPaymentDate).toISOString() : null,
        firstPaymentDate,
        nextPaymentDate,
        amount: Number(subForm.amount) || 0,
        setupFee: Number(subForm.setupFee) || 0,
        notes: subForm.notes || '',
        salesPersonId: subForm.salesPersonId || null,
        salesPersonName: chosenSp ? chosenSp.name : null,
        paymentHistory: normalizedHistory,
      };
      // Package limits live on the company doc itself (not under subscription)
      // so client-side gates can read them without an extra fetch. Blank/0 means
      // unlimited.
      const parseLimit = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : null;
      };
      const companyPatch = {
        subscription: subData,
        maxBranches: parseLimit(subForm.maxBranches),
        maxUsers: parseLimit(subForm.maxUsers),
        maxPosCounters: parseLimit(subForm.maxPosCounters)
      };
      await updateDoc(doc(db, 'companies', subModal.companyId), companyPatch);
      // COST: same fields go into the cached row, so the refresh below is free
      // instead of another ~3,000-read sweep of every company.
      patchCompaniesCache(subModal.companyId, companyPatch);
      setSubModal(null);
      loadCompanies();
    } catch (error) {
      console.error('Error saving subscription:', error);
      alert('Failed to save subscription');
    } finally {
      setSavingSub(false);
    }
  };

  // === Sales Persons CRUD (top-level /salesPersons collection) ===
  const loadSalesPersons = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'salesPersons'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSalesPersons(list);
    } catch (err) {
      console.error('Error loading sales persons:', err);
    }
  }, []);

  // === Sales-user company access ===
  // Load ALL super-admin users (with their role) so the owner can both set who is
  // a 'sales' login and gate which companies each sales login sees.
  const loadSalesUsers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'superAdmins'));
      const list = snap.docs
        .map(d => ({ uid: d.id, ...d.data() }))
        .map(u => ({ ...u, role: u.role || 'superadmin' })); // missing role = legacy owner
      list.sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
      setSalesUsers(list);
    } catch (err) {
      console.error('Error loading super-admin users:', err);
    }
  }, []);

  // Flip a super-admin user between 'sales' (view-only) and 'superadmin' (full).
  const setSalesUserRole = async (uid, role) => {
    if (!isOwner) return;
    const currentUid = (() => { try { return sessionStorage.getItem('sa_uid') || auth.currentUser?.uid; } catch { return auth.currentUser?.uid; } })();
    if (uid === currentUid) { alert("You can't change your own role here."); return; }
    if (role === 'superadmin' && !window.confirm('Give this user FULL super-admin access?')) return;
    try {
      await updateDoc(doc(db, 'superAdmins', uid), { role });
      setSalesUsers(prev => prev.map(u => u.uid === uid ? { ...u, role } : u));
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role. ' + (err?.message || ''));
    }
  };

  const openSalesAccessModal = (company) => {
    if (!isOwner) return;
    setSalesAccessModal({ companyId: company.id, companyName: company.companyName });
    setSalesAccessHidden(Array.isArray(company.salesHidden) ? [...company.salesHidden] : []);
  };

  // Checkbox shows "can see"; checked = NOT hidden. Toggle flips membership in the hidden list.
  const toggleSalesUserAccess = (uid) => {
    setSalesAccessHidden(prev => prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid]);
  };

  const saveSalesAccess = async () => {
    if (!isOwner || !salesAccessModal) return;
    setSavingSalesAccess(true);
    try {
      await updateDoc(doc(db, 'companies', salesAccessModal.companyId), {
        salesHidden: salesAccessHidden,
        updatedAt: new Date().toISOString()
      });
      // Keep local list in sync so the change sticks without a full reload.
      setCompanies(prev => prev.map(c => c.id === salesAccessModal.companyId ? { ...c, salesHidden: salesAccessHidden } : c));
      // salesHidden drives the sales-scoping filter, so the cached list must carry it too.
      patchCompaniesCache(salesAccessModal.companyId, { salesHidden: salesAccessHidden });
      setSalesAccessModal(null);
      setSalesAccessHidden([]);
    } catch (err) {
      console.error('Error saving sales access:', err);
      alert('Failed to save sales access. ' + (err?.message || ''));
    } finally {
      setSavingSalesAccess(false);
    }
  };

  const addSalesPerson = async () => {
    if (!isOwner) return;
    const name = newSalesPersonName.trim();
    if (!name) return;
    // Reject duplicates (case-insensitive) so the dropdown stays clean.
    if (salesPersons.some(p => (p.name || '').toLowerCase() === name.toLowerCase())) {
      alert('A sales person with this name already exists.');
      return;
    }
    // Pre-flight: make sure Firebase Auth still has a current user. The
    // Firestore rule requires `request.auth.uid` to point at a doc in
    // /superAdmins with isSuperAdmin: true — if either is missing, the write
    // will fail with PERMISSION_DENIED.
    if (!auth.currentUser) {
      alert('Your Firebase session expired. Log out and back in, then try again.');
      return;
    }
    setSavingSalesPerson(true);
    try {
      await addDoc(collection(db, 'salesPersons'), {
        name,
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser.uid,
      });
      setNewSalesPersonName('');
      await loadSalesPersons();
    } catch (err) {
      console.error('Error adding sales person:', err);
      const code = err?.code || '';
      let hint = '';
      if (code === 'permission-denied' || /permission/i.test(err?.message || '')) {
        hint = '\n\nFirestore rejected the write. Verify your superAdmins/{uid} doc exists and has `isSuperAdmin: true`. Re-deploy firestore.rules if needed.';
      }
      alert('Failed to add sales person: ' + (err?.message || code || 'unknown error') + hint);
    } finally {
      setSavingSalesPerson(false);
    }
  };

  const deleteSalesPerson = async (id) => {
    if (!isOwner) return;
    if (!window.confirm('Delete this sales person? Companies already tagged to them will keep the saved name on their subscription record.')) return;
    try {
      await deleteDoc(doc(db, 'salesPersons', id));
      await loadSalesPersons();
    } catch (err) {
      console.error('Error deleting sales person:', err);
      alert('Failed to delete sales person: ' + (err?.message || err?.code || 'unknown error'));
    }
  };

  // Create shadow (tax) company
  const createShadowCompany = async () => {
    if (!isOwner) return;
    if (!shadowModal) return;
    const sourceId = shadowModal.companyId;
    setShadowCreating(true);
    setShadowProgress('Creating shadow company...');

    try {
      // 1. Read source company full document
      console.log('SHADOW STEP 1: Reading source company...');
      const sourceDoc = await getDoc(doc(db, 'companies', sourceId));
      if (!sourceDoc.exists()) throw new Error('Source company not found');
      const sourceData = { ...sourceDoc.data() };
      console.log('SHADOW STEP 1: SUCCESS - Source company read');

      // Remove transient fields
      delete sourceData.replicateTo;
      delete sourceData.replicateFrom;

      // 2. Create shadow company document
      console.log('SHADOW STEP 2: Creating shadow company doc...');
      setShadowProgress('Creating shadow company document...');
      const shadowDocRef = await addDoc(collection(db, 'companies'), {
        ...sourceData,
        companyName: sourceData.companyName + ' (Tax)',
        replicateFrom: sourceId,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser?.uid
      });
      const shadowId = shadowDocRef.id;
      console.log('SHADOW STEP 2: SUCCESS - Shadow company created:', shadowId);

      // 3. Link real company to shadow
      console.log('SHADOW STEP 3: Linking real company to shadow...');
      setShadowProgress('Linking companies...');
      await updateDoc(doc(db, 'companies', sourceId), { replicateTo: shadowId });
      console.log('SHADOW STEP 3: SUCCESS');

      // 3.5 Add Super Admin as temporary user in shadow company
      const saUid = auth.currentUser.uid;
      console.log('SHADOW STEP 3.5: Adding SA temp user...', saUid);
      setShadowProgress('Setting up access...');
      await setDoc(doc(db, 'companies', shadowId, 'users', saUid), {
        email: auth.currentUser?.email || '',
        displayName: saUser?.displayName || 'Super Admin',
        role: 'admin',
        isActive: true,
        isTempSuperAdmin: true,
        createdAt: new Date().toISOString()
      });
      console.log('SHADOW STEP 3.5: SUCCESS - SA temp user added');

      // 4. Copy master data collections (same doc IDs for referential integrity)
      const MASTER_COLS = [
        'accounts', 'customers', 'vendors', 'inventoryItems',
        'inventoryCategories', 'inventorySubCategories', 'inventoryLocations',
        'banks', 'employees', 'jobs', 'areas', 'regions',
        'branches', 'roles', 'financialYears', 'documentCounters'
      ];

      for (const colName of MASTER_COLS) {
        console.log(`SHADOW STEP 4: Copying ${colName}...`);
        setShadowProgress(`Copying ${colName}...`);
        try {
          const sourceSnap = await getDocs(collection(db, 'companies', sourceId, colName));
          if (sourceSnap.empty) { console.log(`SHADOW STEP 4: ${colName} is empty, skipping`); continue; }
          const docs = sourceSnap.docs;
          console.log(`SHADOW STEP 4: Read ${docs.length} docs from ${colName}`);
          for (let i = 0; i < docs.length; i += 500) {
            const batch = writeBatch(db);
            const chunk = docs.slice(i, i + 500);
            chunk.forEach(d => {
              batch.set(doc(db, 'companies', shadowId, colName, d.id), d.data());
            });
            await batch.commit();
            console.log(`SHADOW STEP 4: Wrote batch of ${chunk.length} to ${colName}`);
          }
        } catch (stepErr) {
          console.error(`SHADOW STEP 4 FAILED on collection: ${colName}`, stepErr.message);
          throw new Error(`Failed copying ${colName}: ${stepErr.message}`);
        }
      }
      console.log('SHADOW STEP 4: ALL master data copied');

      // 5. Copy users and update their global profiles
      console.log('SHADOW STEP 5: Copying users...');
      setShadowProgress('Copying users...');
      const usersSnap = await getDocs(collection(db, 'companies', sourceId, 'users'));
      for (const userDoc of usersSnap.docs) {
        console.log(`SHADOW STEP 5: Copying user ${userDoc.id}...`);
        try {
          await setDoc(doc(db, 'companies', shadowId, 'users', userDoc.id), userDoc.data());
          console.log(`SHADOW STEP 5: User ${userDoc.id} copied to shadow`);
        } catch (stepErr) {
          console.error(`SHADOW STEP 5 FAILED: copying user ${userDoc.id} to shadow`, stepErr.message);
          throw new Error(`Failed copying user ${userDoc.id}: ${stepErr.message}`);
        }
        try {
          const globalUserRef = doc(db, 'users', userDoc.id);
          const globalUser = await getDoc(globalUserRef);
          if (globalUser.exists()) {
            const userCompanies = globalUser.data().companies || [];
            if (!userCompanies.includes(shadowId)) {
              await updateDoc(globalUserRef, { companies: [...userCompanies, shadowId] });
              console.log(`SHADOW STEP 5: Updated global profile for ${userDoc.id}`);
            }
          }
        } catch (stepErr) {
          console.error(`SHADOW STEP 5 FAILED: updating global profile for ${userDoc.id}`, stepErr.message);
          throw new Error(`Failed updating global profile for ${userDoc.id}: ${stepErr.message}`);
        }
      }
      console.log('SHADOW STEP 5: ALL users copied');

      // 6. Copy transaction data
      const TXN_COLS = ['transactions', 'subTransactions', 'stockFlow'];
      for (const colName of TXN_COLS) {
        console.log(`SHADOW STEP 6: Copying ${colName}...`);
        setShadowProgress(`Copying ${colName}...`);
        try {
          const sourceSnap = await getDocs(collection(db, 'companies', sourceId, colName));
          if (sourceSnap.empty) { console.log(`SHADOW STEP 6: ${colName} is empty, skipping`); continue; }
          const docs = sourceSnap.docs;
          console.log(`SHADOW STEP 6: Read ${docs.length} docs from ${colName}`);
          for (let i = 0; i < docs.length; i += 500) {
            const batch = writeBatch(db);
            const chunk = docs.slice(i, i + 500);
            chunk.forEach(d => {
              batch.set(doc(db, 'companies', shadowId, colName, d.id), d.data());
            });
            await batch.commit();
            console.log(`SHADOW STEP 6: Wrote batch of ${chunk.length} to ${colName}`);
          }
          setShadowProgress(`Copied ${docs.length} ${colName}`);
        } catch (stepErr) {
          console.error(`SHADOW STEP 6 FAILED on collection: ${colName}`, stepErr.message);
          throw new Error(`Failed copying ${colName}: ${stepErr.message}`);
        }
      }
      console.log('SHADOW STEP 6: ALL transaction data copied');

      // 7. Remove temporary Super Admin user if not a real company user
      const isRealUser = usersSnap.docs.some(d => d.id === saUid);
      if (!isRealUser) {
        console.log('SHADOW STEP 7: Removing temp SA user...');
        try { await deleteDoc(doc(db, 'companies', shadowId, 'users', saUid)); } catch (e) { console.log('SHADOW STEP 7: Could not remove temp SA user (ok)', e.message); }
      }
      console.log('SHADOW: Complete!');

      setShadowProgress('Shadow company created successfully!');
      setShadowCreating(false);
    } catch (error) {
      console.error('SHADOW CREATION FAILED:', error);
      setShadowProgress('Error: ' + error.message);
      setShadowCreating(false);
    }
  };

  // View company details (toggle expand)
  const viewCompanyDetails = (company) => {
    if (selectedCompany?.id === company.id) {
      setSelectedCompany(null);
      setCompanyUsers([]);
    } else {
      setSelectedCompany(company);
      setCompanyDetailTab('overview');
      loadCompanyUsers(company.id);
    }
  };

  // Collection definitions for clearing
  const TRANSACTION_COLLECTIONS = [
    'transactions', 'subTransactions', 'stockFlow',
    'documentCounters', 'journalEntries', 'auditLog'
  ];

  const MASTER_DATA_COLLECTIONS = [
    'accounts', 'customers', 'vendors', 'inventoryItems',
    'inventoryLocations', 'inventoryCategories', 'inventorySubCategories',
    'banks', 'employees', 'jobs', 'areas', 'regions'
  ];

  // Delete all documents in a Firestore subcollection (batch limit 500)
  const deleteCollection = async (companyId, collectionName) => {
    const colRef = collection(db, 'companies', companyId, collectionName);
    const snapshot = await getDocs(colRef);
    if (snapshot.empty) return 0;

    let deleted = 0;
    const docs = snapshot.docs;

    for (let i = 0; i < docs.length; i += 500) {
      const batch = writeBatch(db);
      const chunk = docs.slice(i, i + 500);
      chunk.forEach(d => batch.delete(d.ref));
      await batch.commit();
      deleted += chunk.length;
    }

    return deleted;
  };

  // Open clear modal
  const openClearModal = (type, companyId, companyName) => {
    if (!isOwner) return;
    setClearModal({ type, companyId, companyName });
    setClearPassword('');
    setClearConfirmName('');
    setClearError('');
    setClearProgress('');
  };

  // Close clear modal
  const closeClearModal = () => {
    if (clearing) return;
    setClearModal(null);
    setClearPassword('');
    setClearConfirmName('');
    setClearError('');
    setClearProgress('');
  };

  // Execute clear data
  const handleClearData = async () => {
    if (!isOwner) return;
    if (!clearModal) return;

    // Validate password by re-authenticating with Firebase
    try {
      await signInWithEmailAndPassword(auth, auth.currentUser?.email, clearPassword);
    } catch {
      setClearError('Invalid password');
      return;
    }

    // Validate company name (trim + collapse inner whitespace so a stray
    // trailing space or double space — common on copy/paste — doesn't block it).
    const norm = (s) => (s || '').replace(/\s+/g, ' ').trim();
    if (norm(clearConfirmName) !== norm(clearModal.companyName)) {
      setClearError('Company name does not match. Please type it exactly.');
      return;
    }

    setClearing(true);
    setClearError('');
    let totalDeleted = 0;

    try {
      let collectionsToDelete = [];
      if (clearModal.type === 'transactions') {
        collectionsToDelete = TRANSACTION_COLLECTIONS;
      } else if (clearModal.type === 'master') {
        collectionsToDelete = MASTER_DATA_COLLECTIONS;
      } else {
        collectionsToDelete = [...TRANSACTION_COLLECTIONS, ...MASTER_DATA_COLLECTIONS];
      }

      for (let i = 0; i < collectionsToDelete.length; i++) {
        const colName = collectionsToDelete[i];
        setClearProgress(`Clearing ${colName}... (${i + 1}/${collectionsToDelete.length})`);
        const count = await deleteCollection(clearModal.companyId, colName);
        totalDeleted += count;
      }

      // INV-04: every stock movement is gone, so the items' running average
      // cost and stock start again from empty (items are kept).
      if (clearModal.type === 'transactions') {
        setClearProgress('Resetting item average costs...');
        const itemsSnap = await getDocs(collection(db, 'companies', clearModal.companyId, 'inventoryItems'));
        const withCost = itemsSnap.docs.filter(d => {
          const x = d.data();
          return x.costState || Number(x.averageCost) || Number(x.currentStock);
        });
        const resetAt = new Date().toISOString();
        for (let i = 0; i < withCost.length; i += 400) {
          const b = writeBatch(db);
          withCost.slice(i, i + 400).forEach(d => b.update(d.ref, {
            costState: {}, costFy: deleteField(), averageCost: 0, currentStock: 0, costUpdatedAt: resetAt, updatedAt: resetAt,
          }));
          await b.commit();
        }
      }

      setClearProgress(`Completed! Deleted ${totalDeleted} documents.`);

      // Auto-close after 2 seconds
      setTimeout(() => {
        closeClearModal();
      }, 2000);
    } catch (error) {
      console.error('Error clearing data:', error);
      setClearError('Failed to clear data: ' + error.message);
    } finally {
      setClearing(false);
    }
  };

  // Delete Company modal
  const openDeleteModal = (companyId, companyName) => {
    if (!isOwner) return;
    // Its tax-books copy is deleted with it — name it in the warning.
    const co = companies.find(c => c.id === companyId);
    const shadow = co?.replicateTo ? companies.find(c => c.id === co.replicateTo) : null;
    setDeleteModal({ companyId, companyName, shadowId: co?.replicateTo || null, shadowName: shadow?.companyName || '' });
    setDeletePassword('');
    setDeleteConfirmName('');
    setDeleteError('');
    setDeleteProgress('');
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModal(null);
    setDeletePassword('');
    setDeleteConfirmName('');
    setDeleteError('');
    setDeleteProgress('');
  };

  const handleDeleteCompany = async () => {
    if (!isOwner) return;
    if (!deleteModal) return;

    // Validate password by re-authenticating
    try {
      await signInWithEmailAndPassword(auth, auth.currentUser?.email, deletePassword);
    } catch {
      setDeleteError('Invalid password');
      return;
    }

    const normName = (s) => (s || '').replace(/\s+/g, ' ').trim();
    if (normName(deleteConfirmName) !== normName(deleteModal.companyName)) {
      setDeleteError('Company name does not match. Please type it exactly.');
      return;
    }

    setDeleting(true);
    setDeleteError('');
    setDeleteProgress('Deleting company and all data... A large company can take several minutes — keep this window open.');

    try {
      // Up to 9 minutes (the server's limit) — a large company takes longer than the default 70 s.
      const deleteCompanyFn = httpsCallable(functions, 'deleteCompany', { timeout: 540000 });
      const result = await deleteCompanyFn({ companyId: deleteModal.companyId });

      setDeleteProgress(`Done! ${result.data.message}`);

      // Remove company from local state
      // (its tax-books copy too; a real company stops showing "Has Shadow")
      setCompanies(prev => prev
        .filter(c => c.id !== deleteModal.companyId && !(deleteModal.shadowId && c.id === deleteModal.shadowId && c.replicateFrom === deleteModal.companyId))
        .map(c => (c.replicateTo === deleteModal.companyId ? { ...c, replicateTo: null } : c)));
      // A deleted company must never come back from the cache — drop it there too.
      invalidateCompaniesCache();
      if (selectedCompany?.id === deleteModal.companyId) {
        setSelectedCompany(null);
        setCompanyUsers([]);
      }

      // Update stats
      setStats(prev => ({
        ...prev,
        totalCompanies: prev.totalCompanies - 1,
        activeCompanies: prev.activeCompanies - 1,
        totalUsers: prev.totalUsers - (result.data.details?.authUsersDeleted || 0)
      }));

      setTimeout(() => {
        closeDeleteModal();
      }, 2000);
    } catch (error) {
      console.error('Error deleting company:', error);
      setDeleteError('Failed to delete company: ' + (error.message || error)
        + ' — If the company is still listed after a refresh, delete it again: it continues from where it stopped (anything left is also finished automatically overnight).');
    } finally {
      setDeleting(false);
    }
  };

  // ─── New-company module defaults (Super-Admin-managed template) ───
  // Stored at platformConfig/newCompanyDefaults and seeded into each new
  // company's enabledModules at signup. Replaces the hardcoded DEFAULT_DISABLED_IDS
  // as the source of "what new users start with". New modules appear here
  // automatically (rendered from MODULE_GROUPS) and fall back to the code default
  // until the super admin saves.
  const openDefaultsModal = async () => {
    setShowDefaultsModal(true);
    setDefaultsLoading(true);
    try {
      const snap = await getDoc(doc(db, 'platformConfig', 'newCompanyDefaults'));
      const saved = snap.exists() ? (snap.data().modules || {}) : {};
      const initial = {};
      ALL_MODULES.filter(m => !ALWAYS_ENABLED_IDS.has(m.id)).forEach(m => {
        initial[m.id] = saved[m.id] !== undefined ? saved[m.id] !== false : !DEFAULT_DISABLED_IDS.has(m.id);
      });
      setDefaultsSettings(initial);
    } catch (e) {
      console.error('Error loading new-company defaults:', e);
      alert('Failed to load defaults. Check the console / Firestore rules for platformConfig.');
    } finally {
      setDefaultsLoading(false);
    }
  };
  const toggleDefault = (id) => setDefaultsSettings(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleDefaultsGroup = (mods, enable) => setDefaultsSettings(prev => {
    const next = { ...prev };
    mods.forEach(m => { if (!ALWAYS_ENABLED_IDS.has(m.id)) next[m.id] = enable; });
    return next;
  });
  const saveDefaults = async () => {
    if (!isOwner) return;
    setSavingDefaults(true);
    try {
      await setDoc(doc(db, 'platformConfig', 'newCompanyDefaults'), {
        modules: defaultsSettings,
        updatedAt: new Date().toISOString(),
        updatedBy: saUser?.email || saUser?.displayName || 'superadmin',
      }, { merge: true });
      setShowDefaultsModal(false);
    } catch (e) {
      console.error('Error saving new-company defaults:', e);
      alert('Failed to save. Ensure firestore.rules allows superadmin writes to platformConfig (re-deploy rules).');
    } finally {
      setSavingDefaults(false);
    }
  };

  // ─── Signup source list (How did you hear about us?) ───
  const openSourcesModal = async () => {
    setShowSourcesModal(true);
    setSourcesLoading(true);
    setNewSource('');
    try {
      const snap = await getDoc(doc(db, 'platformConfig', 'signupOptions'));
      const list = snap.exists() ? snap.data()?.heardFromOptions : null;
      setSourceList(Array.isArray(list) && list.length > 0 ? list.filter(Boolean) : [...DEFAULT_SIGNUP_SOURCES]);
    } catch (e) {
      console.error('Error loading signup sources:', e);
      setSourceList([...DEFAULT_SIGNUP_SOURCES]);
    } finally {
      setSourcesLoading(false);
    }
  };
  const addSource = () => {
    const v = newSource.trim();
    if (!v) return;
    if (v.toLowerCase() === 'other') { alert('"Other" is always shown automatically — no need to add it.'); return; }
    if (sourceList.some(s => s.toLowerCase() === v.toLowerCase())) { alert('That option already exists.'); return; }
    setSourceList(prev => [...prev, v]);
    setNewSource('');
  };
  const removeSource = (idx) => setSourceList(prev => prev.filter((_, i) => i !== idx));
  const saveSources = async () => {
    if (!isOwner) return;
    if (sourceList.length === 0) { alert('Keep at least one option.'); return; }
    setSavingSources(true);
    try {
      await setDoc(doc(db, 'platformConfig', 'signupOptions'), {
        heardFromOptions: sourceList,
        updatedAt: new Date().toISOString(),
        updatedBy: saUser?.email || saUser?.displayName || 'superadmin',
      }, { merge: true });
      setShowSourcesModal(false);
    } catch (e) {
      console.error('Error saving signup sources:', e);
      alert('Failed to save. Ensure firestore.rules allows superadmin writes to platformConfig (re-deploy rules).');
    } finally {
      setSavingSources(false);
    }
  };

  // ─── QR marketing campaigns ───
  const genQrImage = async (id) => {
    try {
      const url = `${QR_SCAN_BASE}?c=${encodeURIComponent(id)}`;
      const dataUrl = await QRCode.toDataURL(url, { width: 320, margin: 2, errorCorrectionLevel: 'M' });
      setQrImages(prev => ({ ...prev, [id]: dataUrl }));
    } catch (e) { console.warn('QR gen failed for', id, e.message); }
  };
  const openQrModal = async () => {
    setShowQrModal(true);
    setQrLoading(true);
    setNewCampaignName('');
    try {
      const snap = await getDocs(query(collection(db, 'qrCampaigns'), orderBy('createdAt', 'desc')));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setQrCampaigns(list);
      list.forEach(c => genQrImage(c.id));
    } catch (e) {
      console.error('Error loading QR campaigns:', e);
      alert('Failed to load QR campaigns. Ensure firestore.rules allows superadmin access to qrCampaigns (re-deploy rules).');
    } finally {
      setQrLoading(false);
    }
  };
  const createCampaign = async () => {
    if (!isOwner) return;
    const name = newCampaignName.trim();
    if (!name) return;
    setCreatingCampaign(true);
    try {
      const ref = await addDoc(collection(db, 'qrCampaigns'), {
        name,
        scanCount: 0,
        createdAt: new Date().toISOString(),
        createdBy: saUser?.email || saUser?.displayName || 'superadmin',
      });
      setNewCampaignName('');
      setQrCampaigns(prev => [{ id: ref.id, name, scanCount: 0, createdAt: new Date().toISOString() }, ...prev]);
      genQrImage(ref.id);
    } catch (e) {
      console.error('Error creating campaign:', e);
      alert('Failed to create. Ensure firestore.rules allows superadmin writes to qrCampaigns (re-deploy rules).');
    } finally {
      setCreatingCampaign(false);
    }
  };
  const downloadQr = (id, name) => {
    const dataUrl = qrImages[id];
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `QR-${(name || id).replace(/[^\w\-]+/g, '_')}.png`;
    a.click();
  };
  const copyQrLink = (id) => {
    const url = `${QR_SCAN_BASE}?c=${encodeURIComponent(id)}`;
    try { navigator.clipboard.writeText(url); alert('Link copied:\n' + url); }
    catch { window.prompt('Copy this link:', url); }
  };
  // Signups attributed to a campaign = companies stamped with its id at signup.
  const signupsForCampaign = (id) => companies.filter(c => c.qrCampaignId === id).length;
  // Push the current template onto EVERY existing company (explicit, guarded).
  // Overwrites each company's toggleable module on/off flags with the template;
  // does not touch business data. Batched (400 ops) to stay under Firestore limits.
  const applyDefaultsToExisting = async () => {
    if (!isOwner) return;
    const count = companies.length;
    if (count === 0) { alert('No companies loaded — click Refresh first, then reopen this screen.'); return; }
    const ok = window.confirm(
      `Apply the module template above to ALL ${count} existing companies?\n\n` +
      `• It OVERWRITES each company's current module on/off settings with the template.\n` +
      `• Per-company module customizations will be replaced.\n` +
      `• Business data (transactions, masters, balances) is NOT affected — only which menus show.\n\n` +
      `This cannot be undone. Continue?`
    );
    if (!ok) return;
    setApplyingDefaults(true);
    try {
      const chunkSize = 400;
      for (let i = 0; i < companies.length; i += chunkSize) {
        const batch = writeBatch(db);
        companies.slice(i, i + chunkSize).forEach(c => {
          const merged = { ...(c.enabledModules || {}), ...defaultsSettings };
          batch.update(doc(db, 'companies', c.id), { enabledModules: merged });
        });
        await batch.commit();
      }
      setCompanies(prev => prev.map(c => ({ ...c, enabledModules: { ...(c.enabledModules || {}), ...defaultsSettings } })));
      // Every company changed — too broad to patch reliably, so drop the cache.
      invalidateCompaniesCache();
      alert(`Applied the module template to ${count} ${count === 1 ? 'company' : 'companies'}.`);
      setShowDefaultsModal(false);
    } catch (e) {
      console.error('Error applying defaults to existing companies:', e);
      alert('Failed to apply to existing companies. See console for details.');
    } finally {
      setApplyingDefaults(false);
    }
  };

  // Open Manage Modules modal
  const openModulesModal = (company) => {
    const existing = company.enabledModules || {};
    // Build initial state: all non-always-enabled modules
    const initial = {};
    const toggleable = ALL_MODULES.filter(m => !ALWAYS_ENABLED_IDS.has(m.id));
    toggleable.forEach(m => {
      // If key exists, use it; default-disabled modules (e.g. branches) default to OFF
      initial[m.id] = existing[m.id] !== undefined
        ? existing[m.id] !== false
        : !DEFAULT_DISABLED_IDS.has(m.id);
    });
    setModuleSettings(initial);
    setModulesModal({ companyId: company.id, companyName: company.companyName });
  };

  const closeModulesModal = () => {
    if (savingModules) return;
    setModulesModal(null);
    setModuleSettings({});
  };

  const toggleModule = (moduleId) => {
    // Soft warning when disabling Unified Party Picker after it's been in use
    if (moduleId === 'unified_party_picker' && moduleSettings[moduleId]) {
      const proceed = window.confirm(
        'Turn OFF Unified Party Picker?\n\n' +
        'Any transactions already posted using the merged dropdown will stay intact, ' +
        'but Customer pickers will stop showing vendors (and vice-versa) going forward. ' +
        'Continue?'
      );
      if (!proceed) return;
    }
    setModuleSettings(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const toggleGroup = (groupModules, enable) => {
    setModuleSettings(prev => {
      const next = { ...prev };
      groupModules.forEach(m => {
        if (!ALWAYS_ENABLED_IDS.has(m.id)) {
          next[m.id] = enable;
        }
      });
      return next;
    });
  };

  // When POS is switched ON for a company, give them a ready-to-use counter so
  // they can sell immediately instead of landing on an empty counter list.
  // Safeguards: never create a second counter (so toggling POS off/on can't
  // duplicate), and skip rather than create a broken counter if Cash in Hand is
  // missing. Returns a message when it couldn't seed, else null.
  const seedDefaultPosCounter = async (companyId) => {
    try {
      const countersRef = collection(db, 'companies', companyId, 'posCounters');
      const existing = await getDocs(countersRef);
      if (!existing.empty) return null;   // already has a counter — leave alone

      const cashSnap = await getDocs(query(
        collection(db, 'companies', companyId, 'accounts'),
        where('code', '==', SYSTEM_ACCOUNTS.CASH_IN_HAND)
      ));
      if (cashSnap.empty) {
        return 'POS enabled, but no default counter was created: the "Cash in Hand" account is missing. Run Master Data → Chart of Accounts → Repair COA for this company, then re-save modules.';
      }
      const cash = { id: cashSnap.docs[0].id, ...cashSnap.docs[0].data() };

      // Attach the default branch when the company has one (multi-branch off → null).
      let branch = null;
      try {
        const brSnap = await getDocs(collection(db, 'companies', companyId, 'branches'));
        const list = brSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(b => b.isActive !== false);
        branch = list.find(b => b.isDefault) || list[0] || null;
      } catch { /* no branches → single-branch company */ }

      const now = new Date().toISOString();
      await addDoc(countersRef, {
        counterCode: 'POS-1',
        counterName: 'Main Counter',
        branchId: branch?.id || null,
        branchName: branch?.branchName || null,
        cashAccountId: cash.id,
        cashAccountCode: cash.code || null,
        cashAccountName: cash.name || null,
        assignedUserIds: [],   // empty → admins/owner see it; assign cashiers later
        isDefault: true,
        isActive: true,
        createdAt: now,
        createdBy: saUser?.email || saUser?.displayName || 'superadmin',
        seededBy: 'superadmin-module-enable',
      });
      return null;
    } catch (e) {
      console.error('Default POS counter seed failed:', e);
      return `POS enabled, but the default counter could not be created (${e?.message || 'unknown error'}). Create one in Settings → POS Counters.`;
    }
  };

  const saveModules = async () => {
    if (!isOwner) return;
    if (!modulesModal) return;
    setSavingModules(true);
    try {
      // Was POS off before this save and on now? (only then do we seed)
      const before = companies.find(c => c.id === modulesModal.companyId)?.enabledModules || {};
      const posWasOn = isModuleEnabledForCompany('pos_counter_sale', before);
      const posNowOn = moduleSettings.pos_counter_sale === true;

      await updateDoc(doc(db, 'companies', modulesModal.companyId), {
        enabledModules: moduleSettings
      });

      let seedWarning = null;
      if (posNowOn && !posWasOn) {
        seedWarning = await seedDefaultPosCounter(modulesModal.companyId);
      }
      if (seedWarning) alert(seedWarning);
      // Update local companies list
      setCompanies(prev => prev.map(c =>
        c.id === modulesModal.companyId ? { ...c, enabledModules: moduleSettings } : c
      ));
      // Keep the cached copy in step so the Module Matrix can't read a stale value.
      patchCompaniesCache(modulesModal.companyId, { enabledModules: moduleSettings });
      if (selectedCompany?.id === modulesModal.companyId) {
        setSelectedCompany(prev => ({ ...prev, enabledModules: moduleSettings }));
      }
      closeModulesModal();
    } catch (error) {
      console.error('Error saving modules:', error);
      alert('Failed to save module settings');
    } finally {
      setSavingModules(false);
    }
  };

  // Currency settings modal
  const openCurrencyModal = (company) => {
    setCurrencyModal({ companyId: company.id, companyName: company.companyName });
    setCurrencyForm({
      currencyCode: company.currencyCode || 'PKR',
      currencySymbol: company.currencySymbol || 'Rs.',
      taxLabel: company.taxLabel || 'GST',
      brandLogoUrl: company.brandLogoUrl || '',
      brandName: company.brandName || '',
      hidePrintBranding: company.hidePrintBranding === true
    });
  };

  const saveCurrencySettings = async () => {
    if (!isOwner) return;
    if (!currencyModal) return;
    if (uploadingLogo) { alert('Please wait — logo is still uploading.'); return; }
    setSavingCurrency(true);
    try {
      await updateDoc(doc(db, 'companies', currencyModal.companyId), {
        currencyCode: currencyForm.currencyCode,
        currencySymbol: currencyForm.currencySymbol,
        taxLabel: currencyForm.taxLabel,
        brandLogoUrl: currencyForm.brandLogoUrl || null,
        brandName: currencyForm.brandName?.trim() || null,
        hidePrintBranding: currencyForm.hidePrintBranding === true,
        updatedAt: new Date().toISOString()
      });
      const trimmedBrandName = currencyForm.brandName?.trim() || '';
      const hidePrintBranding = currencyForm.hidePrintBranding === true;
      // Update local state
      setCompanies(prev => prev.map(c => c.id === currencyModal.companyId
        ? { ...c, currencyCode: currencyForm.currencyCode, currencySymbol: currencyForm.currencySymbol, taxLabel: currencyForm.taxLabel, brandLogoUrl: currencyForm.brandLogoUrl, brandName: trimmedBrandName, hidePrintBranding }
        : c));
      // Same values into the cached row so a cached load doesn't undo the change.
      patchCompaniesCache(currencyModal.companyId, {
        currencyCode: currencyForm.currencyCode, currencySymbol: currencyForm.currencySymbol,
        taxLabel: currencyForm.taxLabel, brandLogoUrl: currencyForm.brandLogoUrl, brandName: trimmedBrandName, hidePrintBranding
      });
      if (selectedCompany?.id === currencyModal.companyId) {
        setSelectedCompany(prev => ({ ...prev, currencyCode: currencyForm.currencyCode, currencySymbol: currencyForm.currencySymbol, taxLabel: currencyForm.taxLabel, brandLogoUrl: currencyForm.brandLogoUrl, brandName: trimmedBrandName, hidePrintBranding }));
      }
      setCurrencyModal(null);
    } catch (error) {
      console.error('Error saving currency settings:', error);
      alert('Failed to save currency settings: ' + error.message);
    } finally {
      setSavingCurrency(false);
    }
  };

  // ── Data repair (records saved before the 30-Sep-2026 fixes) ──
  // Step 1 PREVIEW (changes nothing) → confirm → Step 2 APPLY.
  const [repairingCompanyId, setRepairingCompanyId] = useState(null);
  const runDataRepair = async (company) => {
    if (!isOwner || repairingCompanyId) return;
    setRepairingCompanyId(company.id);
    try {
      const fn = httpsCallable(functions, 'dataRepair');
      const pre = (await fn({ companyId: company.id, apply: false })).data;
      const lines = [
        `Data repair — ${company.companyName}`,
        '',
        `POS stock lines missing cost: ${pre.posCosts.rows}`,
        `Production orders with unit mix-up: ${pre.production.orders} (${pre.production.rows} stock lines)`,
        `Invoices where returns were counted as paid: ${pre.returns.invoices}`,
        `Posted documents with NO accounting (review manually): ${pre.noLedger.count}`,
        `Items whose average cost will be rebuilt: ${pre.costing?.items || 0}`,
        `Items showing stock with no entries (will be set to 0): ${(pre.costing?.resets || 0) + (pre.costing?.staleStock || 0)}`,
        `Sales with no cost or waiting for one (will be priced): ${pre.costing?.zeroCostRows || 0} lines in ${pre.costing?.zeroCostDocs || 0} documents`,
        `Stock lines with numbers saved as text (will be fixed): ${pre.costing?.textRows || 0}`,
      ];
      // Optional (asked separately): every sale re-costed in date order.
      const rc = pre.costing?.recost || { rows: 0, docs: 0, amount: 0, samples: [] };
      const money = (n) => `Rs.${Math.abs(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
      if (rc.rows > 0) {
        lines.push('', `Re-cost sales in date order (asked next): ${rc.rows} lines in ${rc.docs} documents`,
          `Cost of goods ${rc.amount >= 0 ? 'goes up' : 'goes down'} by ${money(rc.amount)}`, ...(rc.samples || []).slice(0, 5));
      }
      if (pre.production.samples.length) lines.push('', 'Production:', ...pre.production.samples.slice(0, 5));
      if (pre.returns.samples.length) lines.push('', 'Invoices:', ...pre.returns.samples.slice(0, 5));
      if (pre.noLedger.docs.length) lines.push('', 'No accounting:', pre.noLedger.docs.slice(0, 10).join(', '));
      if (pre.costing?.samples?.length) lines.push('', 'Average cost:', ...pre.costing.samples.slice(0, 5));
      const costingFixes = pre.costing?.changed !== undefined
        ? pre.costing.changed
        : (pre.costing?.items || 0) + (pre.costing?.zeroCostRows || 0);
      const fixable = pre.posCosts.rows + pre.production.rows + pre.returns.invoices + costingFixes;
      if (fixable === 0 && !(rc.rows > 0)) {
        window.alert(lines.join('\n') + '\n\nNothing to fix.');
        return;
      }
      if (fixable > 0 && !window.confirm(lines.join('\n') + '\n\nApply these fixes now?')) return;
      let recost = false;
      if (rc.rows > 0) {
        recost = window.confirm(`${fixable > 0 ? '' : lines.join('\n') + '\n\n'}Also re-cost ${rc.rows} sale lines in ${rc.docs} documents in date order?\n\n`
          + `Each sale takes the average cost of its own date (a sale made before its purchase takes that purchase's cost). `
          + `Cost of goods ${rc.amount >= 0 ? 'goes up' : 'goes down'} by ${money(rc.amount)}, so the profit of those past invoices changes by the same amount.\n\n`
          + 'OK = re-cost, Cancel = skip this part.');
        if (!recost && fixable === 0) return;
      }
      const done = (await fn({ companyId: company.id, apply: true, recost })).data;
      const drc = done.costing?.recost;
      window.alert(`Done. Fixed ${done.posCosts.rows} POS lines, ${done.production.rows} production lines, ${done.returns.invoices} invoices. Average cost rebuilt for ${done.costing?.items || 0} items; ${(done.costing?.resets || 0) + (done.costing?.staleStock || 0)} items with no entries set to 0; ${done.costing?.zeroCostRows || 0} no-cost sale lines priced; ${done.costing?.textRows || 0} text numbers fixed.`
        + (drc?.applied ? `\n\nRe-costed ${drc.rows} sale lines in ${drc.docs} documents in date order (cost of goods ${drc.amount >= 0 ? 'up' : 'down'} ${money(drc.amount)}).` : ''));
    } catch (e) {
      console.error('Data repair failed:', e);
      window.alert('Data repair failed: ' + (e?.message || e));
    } finally {
      setRepairingCompanyId(null);
    }
  };

  // ── FBR POS config (Super Admin) ──
  const openFbrModal = async (company) => {
    if (!isOwner) return;
    setFbrModal({ companyId: company.id, companyName: company.companyName });
    setFbrForm({ posId: '', env: 'live', endpoint: '', token: '', hasToken: false });
    try {
      const fn = httpsCallable(functions, 'getFbrPosConfig');
      const res = await fn({ companyId: company.id });
      setFbrForm({ posId: res.data?.posId || '', env: res.data?.env || 'live', endpoint: res.data?.endpoint || '', token: '', hasToken: !!res.data?.hasToken });
    } catch (e) { /* unconfigured — keep blank form */ }
  };

  const saveFbrConfig = async () => {
    if (!isOwner || !fbrModal) return;
    setSavingFbr(true);
    try {
      const fn = httpsCallable(functions, 'saveFbrPosConfig');
      await fn({ companyId: fbrModal.companyId, posId: fbrForm.posId.trim(), env: fbrForm.env, endpoint: fbrForm.endpoint.trim(), token: fbrForm.token.trim() });
      const patch = { fbrPosId: fbrForm.posId.trim(), fbrEnv: fbrForm.env, fbrEndpoint: fbrForm.endpoint.trim() };
      setCompanies(prev => prev.map(c => c.id === fbrModal.companyId ? { ...c, ...patch } : c));
      patchCompaniesCache(fbrModal.companyId, patch);
      if (selectedCompany?.id === fbrModal.companyId) setSelectedCompany(prev => ({ ...prev, ...patch }));
      setFbrModal(null);
      alert('FBR POS configuration saved.\n\nEnable the "FBR POS" module for this company in Manage Modules so the "Send to FBR" button appears on its Sale Invoices.');
    } catch (error) {
      alert('Failed to save FBR config: ' + (error?.message || error));
    } finally {
      setSavingFbr(false);
    }
  };

  // ── FBR Digital Invoicing config (Super Admin) — standalone ──
  const openDiModal = async (company) => {
    if (!isOwner) return;
    setDiModal({ companyId: company.id, companyName: company.companyName });
    setDiForm({ sellerNtn: '', endpoint: '', saleType: '', token: '', hasToken: false });
    try {
      const fn = httpsCallable(functions, 'getDiConfig');
      const res = await fn({ companyId: company.id });
      setDiForm({ sellerNtn: res.data?.sellerNtn || '', endpoint: res.data?.endpoint || '', saleType: res.data?.saleType || '', token: '', hasToken: !!res.data?.hasToken });
    } catch (e) { /* unconfigured — keep blank form */ }
  };

  const saveDiConfig = async () => {
    if (!isOwner || !diModal) return;
    setSavingDi(true);
    try {
      const fn = httpsCallable(functions, 'saveDiConfig');
      await fn({ companyId: diModal.companyId, sellerNtn: diForm.sellerNtn.trim(), endpoint: diForm.endpoint.trim(), saleType: diForm.saleType.trim(), token: diForm.token.trim() });
      const patch = { diSellerNtn: diForm.sellerNtn.trim(), diEndpoint: diForm.endpoint.trim(), diSaleType: diForm.saleType.trim() };
      setCompanies(prev => prev.map(c => c.id === diModal.companyId ? { ...c, ...patch } : c));
      patchCompaniesCache(diModal.companyId, patch);
      if (selectedCompany?.id === diModal.companyId) setSelectedCompany(prev => ({ ...prev, ...patch }));
      setDiModal(null);
      alert('FBR Digital Invoicing configuration saved.\n\nEnable the "FBR Digital Invoicing" module for this company in Manage Modules so the "Send to FBR (Digital Invoice)" button appears on its Sale Invoices.');
    } catch (error) {
      alert('Failed to save DI config: ' + (error?.message || error));
    } finally {
      setSavingDi(false);
    }
  };

  const enabledCount = Object.values(moduleSettings).filter(Boolean).length;
  const totalToggleable = ALL_MODULES.filter(m => !ALWAYS_ENABLED_IDS.has(m.id)).length;

  // Email templates — {{companyName}} gets replaced with actual company name
  const EMAIL_TEMPLATES = [
    {
      id: 'welcome_back',
      name: 'Welcome Back',
      subject: 'We miss you at AmalERP, {{companyName}}!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Hello {{companyName}},</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">We noticed you haven't logged in for a while. Your account is still active and all your data is safe with us.</p>
    <p style="color:#555;font-size:15px;line-height:1.7">Log in today and see what's new — we've been making improvements to help you manage your business better.</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Login to AmalERP</a>
    </div>
    <p style="color:#555;font-size:15px">Need help? Just reply to this email.</p>
    <p style="color:#555;font-size:15px;margin-bottom:0">Best regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'new_features',
      name: 'New Features Announcement',
      subject: 'New features available for {{companyName}} on AmalERP!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Hi {{companyName}},</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">We're excited to share some new features we've added to AmalERP:</p>
    <ul style="color:#555;font-size:15px;line-height:2">
      <li>Improved reporting and analytics</li>
      <li>Faster invoice generation</li>
      <li>Enhanced inventory management</li>
    </ul>
    <p style="color:#555;font-size:15px;line-height:1.7">Log in now to explore these updates and streamline your business operations.</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Explore New Features</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0">Best regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'payment_reminder',
      name: 'Subscription Reminder',
      subject: '{{companyName}}, your AmalERP subscription needs attention',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Dear {{companyName}},</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">This is a friendly reminder regarding your AmalERP subscription. Please ensure your account is up to date to continue enjoying uninterrupted access to all features.</p>
    <p style="color:#555;font-size:15px;line-height:1.7">If you have any questions about your subscription or need assistance, please don't hesitate to reach out.</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Go to AmalERP</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0">Best regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'subscription_activated',
      name: 'Subscription Activated',
      subject: 'Your AmalERP Subscription is Now Active, {{companyName}}!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Congratulations, {{companyName}}! 🎉</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">Your AmalERP subscription has been successfully activated! You now have full access to all premium features.</p>
    <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:20px;margin:20px 0;text-align:center">
      <p style="color:#16a34a;font-size:18px;font-weight:700;margin:0">✅ Subscription Active</p>
      <p style="color:#555;font-size:14px;margin:8px 0 0">All features are now unlocked for your account</p>
    </div>
    <p style="color:#555;font-size:15px;line-height:1.7">Here's what you can do now:</p>
    <ul style="color:#555;font-size:15px;line-height:2">
      <li>Create unlimited invoices & quotations</li>
      <li>Manage inventory across locations</li>
      <li>Add unlimited users & branches</li>
      <li>Access advanced reports & analytics</li>
      <li>Full accounting & ledger management</li>
    </ul>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Start Using AmalERP</a>
    </div>
    <p style="color:#555;font-size:15px">Thank you for choosing AmalERP. We're here to help your business grow!</p>
    <p style="color:#555;font-size:15px;margin-bottom:0">Best regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'trial_expiring',
      name: 'Trial Expiring Soon',
      subject: '{{companyName}}, your AmalERP trial expires in 3 days!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Hi {{companyName}},</h2>
    <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:20px;margin:20px 0;text-align:center">
      <p style="color:#d97706;font-size:18px;font-weight:700;margin:0">⏳ Trial Expires in 3 Days</p>
      <p style="color:#555;font-size:14px;margin:8px 0 0">Don't lose access to your data and features</p>
    </div>
    <p style="color:#555;font-size:15px;line-height:1.7">Your free trial of AmalERP is about to end. After expiry, you will lose access to:</p>
    <ul style="color:#555;font-size:15px;line-height:2">
      <li>Invoice & quotation creation</li>
      <li>Inventory management</li>
      <li>Reports & analytics</li>
      <li>Multi-user access</li>
    </ul>
    <p style="color:#555;font-size:15px;line-height:1.7"><strong>Upgrade now</strong> to keep everything running smoothly. All your data will be preserved when you subscribe!</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#d97706;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Upgrade Now — Don't Lose Access</a>
    </div>
    <p style="color:#555;font-size:15px">Need more time or have questions? Just reply to this email.</p>
    <p style="color:#555;font-size:15px;margin-bottom:0">Best regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'aaj_ki_achi_baat',
      name: 'Aaj Ki Achi Baat',
      subject: '{{companyName}}, Aaj Ki Achi Baat — AmalERP se!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Assalamu Alaikum, {{companyName}}! ✨</h2>
    <div style="background:linear-gradient(135deg,#fdf2f8,#fce7f3);border-radius:12px;padding:24px;margin:20px 0;text-align:center;border:1px solid #f9a8d4">
      <p style="color:#800020;font-size:22px;font-weight:700;margin:0;line-height:1.5">📖 Aaj Ki Achi Baat</p>
      <p style="color:#555;font-size:16px;font-style:italic;margin:16px 0 8px;line-height:1.7">"Kamyabi unhe milti hai jo mehnat mein yaqeen rakhte hain aur kabhi himmat nahi haarte."</p>
      <p style="color:#888;font-size:13px;margin:0">— Success comes to those who believe in hard work and never lose courage</p>
    </div>
    <p style="color:#555;font-size:15px;line-height:1.7">AmalERP ke saath aap apne business ko behtareen tareeqe se manage kar sakte hain. Aap ki mehnat aur humara software — yeh hai kamyabi ka raaz!</p>
    <p style="color:#555;font-size:15px;line-height:1.7"><strong>Aaj ka tip:</strong> Apni daily sales report check karein aur dekhein ke aap ke business mein kya improve ho raha hai. Chhoti chhoti improvements bade results laati hain! 📊</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Apna Dashboard Dekhein</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0">Dua mein yaad rakhein,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'business_tips',
      name: 'Weekly Business Tips',
      subject: '{{companyName}}, 3 tips to grow your business this week!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Hi {{companyName}},</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">Here are 3 quick tips to help your business grow this week:</p>
    <div style="background:#f8fafc;border-left:4px solid #800020;padding:16px 20px;margin:16px 0;border-radius:0 8px 8px 0">
      <p style="color:#800020;font-weight:700;margin:0 0 4px">💡 Tip 1: Review Your Pending Invoices</p>
      <p style="color:#555;font-size:14px;margin:0">Regularly follow up on unpaid invoices. AmalERP's reports show you exactly who owes what.</p>
    </div>
    <div style="background:#f8fafc;border-left:4px solid #800020;padding:16px 20px;margin:16px 0;border-radius:0 8px 8px 0">
      <p style="color:#800020;font-weight:700;margin:0 0 4px">📦 Tip 2: Keep Your Inventory Updated</p>
      <p style="color:#555;font-size:14px;margin:0">Low stock means lost sales. Check your inventory alerts in AmalERP and reorder before it's too late.</p>
    </div>
    <div style="background:#f8fafc;border-left:4px solid #800020;padding:16px 20px;margin:16px 0;border-radius:0 8px 8px 0">
      <p style="color:#800020;font-weight:700;margin:0 0 4px">📊 Tip 3: Check Your Profit Margins</p>
      <p style="color:#555;font-size:14px;margin:0">Use the analytics dashboard to identify your highest-margin products and focus on selling more of them.</p>
    </div>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Open AmalERP Dashboard</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0">To your success,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'festival_greeting',
      name: 'Festival / Holiday Greetings',
      subject: 'Warm wishes from AmalERP to {{companyName}}!',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:linear-gradient(135deg,#800020,#b91c4c);padding:32px 24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
    <p style="color:#fecdd3;margin:8px 0 0;font-size:14px">🌙 Season's Greetings 🌙</p>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Dear {{companyName}},</h2>
    <div style="text-align:center;margin:24px 0">
      <p style="font-size:40px;margin:0">🌙✨🎉</p>
    </div>
    <p style="color:#555;font-size:15px;line-height:1.7;text-align:center">Wishing you and your team joy, prosperity, and success on this special occasion!</p>
    <p style="color:#555;font-size:15px;line-height:1.7;text-align:center">May this festive season bring new opportunities for your business. We are grateful to have you as a valued member of the AmalERP family.</p>
    <div style="background:linear-gradient(135deg,#fdf2f8,#fff1f2);border-radius:12px;padding:20px;margin:24px 0;text-align:center">
      <p style="color:#800020;font-size:16px;font-weight:600;margin:0">Thank you for trusting AmalERP with your business!</p>
    </div>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Visit AmalERP</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0;text-align:center">Warm regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'feedback_request',
      name: 'Feedback Request',
      subject: '{{companyName}}, how is your AmalERP experience?',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Hi {{companyName}},</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">We'd love to hear from you! Your feedback helps us make AmalERP better for everyone.</p>
    <p style="color:#555;font-size:15px;line-height:1.7">How has your experience been so far? Is there anything we can improve?</p>
    <div style="text-align:center;margin:24px 0">
      <p style="color:#555;font-size:15px;margin-bottom:16px">Rate your experience:</p>
      <span style="font-size:32px;margin:0 8px;cursor:pointer">😍</span>
      <span style="font-size:32px;margin:0 8px;cursor:pointer">😊</span>
      <span style="font-size:32px;margin:0 8px;cursor:pointer">😐</span>
      <span style="font-size:32px;margin:0 8px;cursor:pointer">😕</span>
      <span style="font-size:32px;margin:0 8px;cursor:pointer">😞</span>
    </div>
    <p style="color:#555;font-size:15px;line-height:1.7">Simply reply to this email with your thoughts, suggestions, or any issues you've faced. Every piece of feedback matters to us!</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Go to AmalERP</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0">Thank you for being part of AmalERP!<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
    {
      id: 'custom',
      name: 'Custom Email',
      subject: '',
      body: `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
  <div style="background:#800020;padding:24px;text-align:center;border-radius:8px 8px 0 0">
    <h1 style="color:#fff;margin:0;font-size:24px">AMAL ERP</h1>
  </div>
  <div style="padding:32px 24px;background:#fff;border:1px solid #e0e0e0">
    <h2 style="color:#333;margin-top:0">Hello {{companyName}},</h2>
    <p style="color:#555;font-size:15px;line-height:1.7">Write your message here...</p>
    <div style="text-align:center;margin:32px 0">
      <a href="https://amalerp.vercel.app/login" style="background:#800020;color:#fff;padding:14px 36px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px">Go to AmalERP</a>
    </div>
    <p style="color:#555;font-size:15px;margin-bottom:0">Best regards,<br><strong>AmalERP Team</strong></p>
  </div>
  <div style="padding:16px 24px;text-align:center;font-size:12px;color:#999">
    AmalERP — Business Management Made Simple
  </div>
</div>`
    },
  ];

  // Delivery totals across the whole mail queue (sent / failed / pending).
  const loadEmailStats = async () => {
    setEmailStatsLoading(true);
    try {
      const mqRef = collection(db, 'mailQueue');
      const [sent, error, pending] = await Promise.all([
        getCountFromServer(query(mqRef, where('status', '==', 'sent'))),
        getCountFromServer(query(mqRef, where('status', '==', 'error'))),
        getCountFromServer(query(mqRef, where('status', '==', 'pending'))),
      ]);
      setEmailStats({ sent: sent.data().count, error: error.data().count, pending: pending.data().count });
    } catch (e) {
      console.error('Email stats failed:', e);
      setEmailStats(null);
    } finally {
      setEmailStatsLoading(false);
    }
  };

  const openEmailModal = (mode, company = null) => {
    setEmailModal(mode === 'single' ? { mode: 'single', company } : { mode: 'bulk' });
    setEmailSubject('');
    setEmailBody('');
    setEmailStatus('');
    setSelectedTemplate('');
    setEmailFilter('all');
    loadEmailStats();
  };

  const closeEmailModal = () => {
    if (emailSending) return;
    setEmailModal(null);
    setEmailSubject('');
    setEmailBody('');
    setEmailStatus('');
    setSelectedTemplate('');
  };

  const applyTemplate = (templateId) => {
    const tpl = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (tpl) {
      setSelectedTemplate(templateId);
      setEmailSubject(tpl.subject);
      setEmailBody(tpl.body);
    }
  };

  const personalizeContent = (text, companyName) => {
    return text.replace(/\{\{companyName\}\}/g, companyName || 'Valued Customer');
  };

  const getFilteredRecipients = () => {
    return companies.filter(c => {
      // Use the user login email (owner-first) so companies without a company
      // email are still reachable. contactEmail resolves user email → company email.
      if (!c.contactEmail) return false;
      // Suspended companies are included in 'all', excluded from paid/trial buckets.
      if (emailFilter === 'paid') return c.status !== 'suspended' && c.subscription && isPaidPlan(c.subscription.plan);
      if (emailFilter === 'trial') return c.status !== 'suspended' && (!c.subscription || c.subscription.plan === 'trial');
      return true;
    });
  };

  const sendEmail = async () => {
    if (!isOwner) return;
    if (!emailSubject.trim() || !emailBody.trim()) {
      setEmailStatus('Please fill in subject and body');
      return;
    }

    setEmailSending(true);
    setEmailStatus('');

    try {
      let recipients = [];
      if (emailModal.mode === 'single') {
        const c = emailModal.company;
        const email = c.contactEmail || c.email || c.companyEmail;
        if (!email) {
          setEmailStatus('This company has no user or company email on file');
          setEmailSending(false);
          return;
        }
        recipients = [{ email, companyName: c.companyName, companyId: c.id }];
      } else {
        // Bulk — filtered active companies with an email
        recipients = getFilteredRecipients()
          .map(c => ({ email: c.contactEmail, companyName: c.companyName, companyId: c.id }));
      }

      if (recipients.length === 0) {
        setEmailStatus('No recipients with email addresses found');
        setEmailSending(false);
        return;
      }

      // Write to mailQueue — Cloud Function picks up and sends
      let sentCount = 0;
      for (const r of recipients) {
        await addDoc(collection(db, 'mailQueue'), {
          to: r.email,
          subject: personalizeContent(emailSubject, r.companyName),
          html: personalizeContent(emailBody, r.companyName),
          companyId: r.companyId,
          companyName: r.companyName,
          status: 'pending',
          createdAt: serverTimestamp(),
        });
        sentCount++;
      }

      setEmailStatus(`Queued ${sentCount} email${sentCount > 1 ? 's' : ''}. Sending is throttled (~1/sec) — click Refresh to track delivery.`);
      // Refresh delivery totals shortly after queueing (sending is async).
      setTimeout(() => loadEmailStats(), 3000);
    } catch (error) {
      console.error('Error queuing emails:', error);
      setEmailStatus('Failed to queue emails: ' + error.message);
    } finally {
      setEmailSending(false);
    }
  };

  // ── Bulk WhatsApp (Super Admin) ────────────────────────────────────────────
  // Approx per-message cost (PKR) by template category — for the estimate only.
  const WA_RATE = { UTILITY: 3.5, MARKETING: 14, AUTHENTICATION: 3 };
  const sanitizePhone = (p) => String(p || '').replace(/[^\d]/g, '');

  // Optional subscription-date targeting. When a field is chosen, only companies
  // whose date falls in [from, to] are included; ones missing that date are
  // skipped. Lets you message just this month's renewals → far fewer, cheaper.
  const waDateOk = (c) => {
    if (waDateField === 'none') return true;
    let d = null;
    if (waDateField === 'signup') {
      d = c.createdAt ? new Date(c.createdAt) : null; // company creation = first signup
    } else if (waDateField === 'lastlogin') {
      // company.lastLogin is already a Date object (or null) on the loaded row.
      d = c.lastLogin instanceof Date ? c.lastLogin : (c.lastLogin ? new Date(c.lastLogin) : null);
    }
    if (!d || isNaN(d.getTime())) return false;
    if (waFrom) { const f = new Date(waFrom); if (!isNaN(f.getTime()) && d < f) return false; }
    if (waTo) { const t = new Date(waTo); if (!isNaN(t.getTime())) { t.setHours(23, 59, 59, 999); if (d > t) return false; } }
    return true;
  };
  const waPlanOk = (c, f) => {
    if (f === 'paid') return c.status !== 'suspended' && c.subscription && isPaidPlan(c.subscription.plan);
    if (f === 'trial') return c.status !== 'suspended' && (!c.subscription || c.subscription.plan === 'trial');
    return true;
  };
  const waMatch = (c, f) => sanitizePhone(c.whatsappNumber).length >= 8 && waPlanOk(c, f) && waDateOk(c);
  // Recipients from the already-loaded companies (0 extra reads).
  const waRecipients = () => companies.filter(c => waMatch(c, waFilter));
  const waCountFor = (f) => companies.filter(c => waMatch(c, f)).length;
  const setWaThisMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (waDateField === 'none') setWaDateField('signup');
    setWaFrom(fmt(first)); setWaTo(fmt(last));
  };

  const loadWhatsappTemplates = async () => {
    setWaTemplatesLoading(true);
    try {
      const fn = httpsCallable(functions, 'listWhatsappTemplates');
      const res = await fn({});
      setWaTemplates((res && res.data && res.data.templates) || []);
    } catch (e) {
      setWaStatus('Could not load templates: ' + (e && e.message));
      setWaTemplates([]);
    } finally {
      setWaTemplatesLoading(false);
    }
  };

  const loadWhatsappStats = async () => {
    setWaStatsLoading(true);
    try {
      const ref = collection(db, 'whatsappQueue');
      const [sent, error, pending, skipped, delivered, deliveryFailed] = await Promise.all([
        getCountFromServer(query(ref, where('status', '==', 'sent'))),
        getCountFromServer(query(ref, where('status', '==', 'error'))),
        getCountFromServer(query(ref, where('status', '==', 'pending'))),
        getCountFromServer(query(ref, where('status', '==', 'skipped'))),
        getCountFromServer(query(ref, where('deliveryStatus', 'in', ['delivered', 'read']))),
        getCountFromServer(query(ref, where('deliveryStatus', '==', 'failed'))),
      ]);
      setWaStats({
        sent: sent.data().count, error: error.data().count, pending: pending.data().count, skipped: skipped.data().count,
        delivered: delivered.data().count, deliveryFailed: deliveryFailed.data().count,
      });
    } catch (e) {
      setWaStats(null);
    } finally {
      setWaStatsLoading(false);
    }
  };

  // Recent failures with their reason (send-time rejections + delivery failures),
  // so the super admin sees WHY a message didn't land — not just a count.
  const loadWhatsappFailures = async () => {
    try {
      const ref = collection(db, 'whatsappQueue');
      const [errSnap, delSnap] = await Promise.all([
        getDocs(query(ref, where('status', '==', 'error'), limit(15))),
        getDocs(query(ref, where('deliveryStatus', '==', 'failed'), limit(15))),
      ]);
      const rows = [];
      errSnap.forEach(d => { const x = d.data(); rows.push({ id: d.id, companyName: x.companyName || '', to: x.to || '', reason: x.error || 'Send rejected' }); });
      delSnap.forEach(d => { const x = d.data(); rows.push({ id: d.id, companyName: x.companyName || '', to: x.to || '', reason: x.deliveryError || 'Delivery failed' }); });
      setWaFailures(rows);
    } catch (e) { setWaFailures([]); }
  };

  const openWhatsappModal = (mode, company = null) => {
    setWaModal(mode === 'single' ? { mode: 'single', company } : { mode: 'bulk' });
    setWaTemplateName(''); setWaParams([]); setWaStatus(''); setWaFilter('all');
    setWaDateField('none'); setWaFrom(''); setWaTo('');
    setOptOutInput(''); setOptOutMsg(''); setWaFailures([]);
    loadWhatsappTemplates();
    loadWhatsappStats();
    loadWhatsappFailures();
  };
  const closeWhatsappModal = () => {
    if (waSending) return;
    setWaModal(null);
  };

  const selectedWaTemplate = () => waTemplates.find(t => t.name === waTemplateName) || null;

  const applyWaTemplate = (name) => {
    setWaTemplateName(name);
    const tpl = waTemplates.find(t => t.name === name);
    const count = tpl ? tpl.variableCount : 0;
    // Prefill {{1}} with the company-name token so it personalizes per recipient.
    setWaParams(Array.from({ length: count }, (_, i) => (i === 0 ? '{{companyName}}' : '')));
  };

  const waPreview = (companyName) => {
    const tpl = selectedWaTemplate();
    if (!tpl) return '';
    let text = tpl.bodyText || '';
    waParams.forEach((val, i) => {
      const resolved = (val || '').replace(/\{\{companyName\}\}/g, companyName || 'Your Company');
      text = text.replace(new RegExp('\\{\\{\\s*' + (i + 1) + '\\s*\\}\\}', 'g'), resolved || `{{${i + 1}}}`);
    });
    return text;
  };

  const addOptOut = async () => {
    const phone = sanitizePhone(optOutInput);
    if (phone.length < 8) { setOptOutMsg('Enter a valid number (with country code, digits only).'); return; }
    try {
      await setDoc(doc(db, 'whatsappOptOuts', phone), {
        addedAt: serverTimestamp(), addedBy: saUser?.email || 'superadmin', reason: 'manual',
      });
      setOptOutMsg(`Added ${phone} to the do-not-message list.`);
      setOptOutInput('');
    } catch (e) {
      setOptOutMsg('Failed: ' + (e && e.message));
    }
  };

  const sendWhatsapp = async () => {
    if (!isOwner) return;
    const tpl = selectedWaTemplate();
    if (!tpl) { setWaStatus('Please select a template.'); return; }

    let recipients = [];
    if (waModal.mode === 'single') {
      const c = waModal.company;
      const phone = sanitizePhone(c.whatsappNumber);
      if (phone.length < 8) { setWaStatus('This company has no valid WhatsApp number.'); return; }
      recipients = [{ phone, companyName: c.companyName, companyId: c.id }];
    } else {
      recipients = waRecipients().map(c => ({ phone: sanitizePhone(c.whatsappNumber), companyName: c.companyName, companyId: c.id }));
    }
    if (recipients.length === 0) { setWaStatus('No recipients with a WhatsApp number found.'); return; }

    // Cost estimate + confirm before blasting.
    const rate = WA_RATE[tpl.category] || 5;
    const estPkr = Math.round(recipients.length * rate);
    const ok = window.confirm(
      `Send template "${tpl.name}" (${tpl.category}) to ${recipients.length} recipient(s)?\n\n` +
      `Estimated cost ≈ PKR ${estPkr.toLocaleString()} (~${rate}/msg).\n\n` +
      `Note: a new number has a daily send cap — any excess shows as "Failed" and can be re-sent later.`
    );
    if (!ok) return;

    setWaSending(true); setWaStatus('');
    try {
      let n = 0;
      for (const r of recipients) {
        const params = waParams.map(v => (v || '').replace(/\{\{companyName\}\}/g, r.companyName || 'Valued Customer'));
        await addDoc(collection(db, 'whatsappQueue'), {
          to: r.phone,
          templateName: tpl.name,
          language: tpl.language || 'en',
          category: tpl.category,
          params,
          companyId: r.companyId,
          companyName: r.companyName,
          status: 'pending',
          createdAt: serverTimestamp(),
        });
        n++;
      }
      setWaStatus(`Queued ${n} message${n > 1 ? 's' : ''}. Sending is paced — click Refresh to track delivery.`);
      setTimeout(() => { loadWhatsappStats(); loadWhatsappFailures(); }, 3000);
    } catch (e) {
      setWaStatus('Failed to queue: ' + (e && e.message));
    } finally {
      setWaSending(false);
    }
  };

  // Auto re-auth on page load if session persisted
  useEffect(() => {
    const reAuth = async () => {
      try {
        const stored = sessionStorage.getItem(SA_AUTH_KEY);
        if (stored === 'true') {
          // If Firebase user is already signed in, just load role
          if (auth.currentUser) {
            const saRef = doc(db, 'superAdmins', auth.currentUser.uid);
            const saDoc = await getDoc(saRef);
            if (saDoc.exists() && saDoc.data().isSuperAdmin && saDoc.data().isActive !== false) {
              const saData = saDoc.data();
              const role = saData.role || 'superadmin';
              setSaRole(role);
              try { sessionStorage.setItem('sa_role', role); } catch {}
              setSaUser({ email: saData.email, displayName: saData.displayName || '', role });
              setIsAuthenticated(true);
              return;
            }
          }
          // No current user — can't re-auth without password, force re-login
          sessionStorage.removeItem(SA_AUTH_KEY);
          setIsAuthenticated(false);
        }
      } catch {
        sessionStorage.removeItem(SA_AUTH_KEY);
        setIsAuthenticated(false);
      }
    };
    reAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadCompanies();
      loadSalesPersons();
      loadSalesUsers();
    }
  }, [isAuthenticated, loadCompanies, loadSalesPersons, loadSalesUsers]);

  // Load usage data when usage tab is active and companies are loaded
  useEffect(() => {
    if (activeTab === 'usage' && companies.length > 0 && usageData.length === 0) {
      loadUsageData();
    }
  }, [activeTab, companies, usageData.length, loadUsageData]);

  // Load read-telemetry data when the Reads tab is active
  useEffect(() => {
    if (activeTab === 'reads' && companies.length > 0 && readsData.length === 0) {
      loadReadsData();
    }
  }, [activeTab, companies, readsData.length, loadReadsData]);


  // Usage sort handler
  const handleUsageSort = (column) => {
    if (usageSortColumn === column) {
      setUsageSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setUsageSortColumn(column);
      setUsageSortDirection('desc');
    }
  };

  // Filter and sort usage data
  const filteredUsageData = usageData
    .filter(item => {
      if (!usageSearchTerm) return true;
      return item.companyName?.toLowerCase().includes(usageSearchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const aVal = a[usageSortColumn] || 0;
      const bVal = b[usageSortColumn] || 0;
      return usageSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

  // Usage summary stats
  const usageSummary = {
    totalViewsToday: usageData.reduce((sum, c) => sum + c.today, 0),
    activeToday: usageData.filter(c => c.today > 0).length,
    activeWeek: usageData.filter(c => c.week > 0).length,
    mostActive: usageData.length > 0 ? [...usageData].sort((a, b) => b[usagePeriod === 'today' ? 'today' : usagePeriod === 'week' ? 'week' : 'month'] - a[usagePeriod === 'today' ? 'today' : usagePeriod === 'week' ? 'week' : 'month'])[0] : null
  };

  // Chart data (top 15 companies by selected period)
  const usageChartData = [...usageData]
    .sort((a, b) => {
      const key = usagePeriod === 'today' ? 'today' : usagePeriod === 'week' ? 'week' : 'month';
      return b[key] - a[key];
    })
    .slice(0, 15)
    .filter(c => c[usagePeriod === 'today' ? 'today' : usagePeriod === 'week' ? 'week' : 'month'] > 0)
    .map(c => ({
      name: c.companyName.length > 15 ? c.companyName.substring(0, 15) + '...' : c.companyName,
      views: c[usagePeriod === 'today' ? 'today' : usagePeriod === 'week' ? 'week' : 'month']
    }));

  // Sort handler
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Helper to classify subscription
  const getSubCategory = (company) => {
    const sub = company.subscription;
    if (!sub || sub.plan === 'trial') {
      const trialEnd = sub?.trialEndDate ? new Date(sub.trialEndDate) : new Date(new Date(company.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
      return trialEnd < new Date() ? 'expired' : 'trial';
    }
    if (sub.nextPaymentDate && new Date(sub.nextPaymentDate) < new Date()) return 'expired';
    return sub.plan; // 'monthly' | 'yearly'
  };

  // Export the CURRENTLY FILTERED client list to Excel. Honors the on-screen
  // Status / Subscription (paid, trial, expired…) / City / search filters, since
  // it reads `filteredCompanies`. Columns: client details, user count, and the
  // active (toggleable) modules each company has enabled.
  const handleExportCompaniesExcel = () => {
    const fmtD = (v) => {
      if (!v) return '';
      try {
        const d = v?.toDate ? v.toDate() : new Date(v);
        return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
      } catch { return ''; }
    };
    const rows = filteredCompanies.map(c => {
      const sub = c.subscription || {};
      const activeMods = ALL_MODULES
        .filter(m => !ALWAYS_ENABLED_IDS.has(m.id) && isModuleEnabledForCompany(m.id, c.enabledModules))
        .map(m => m.name);
      return {
        'Company Name': c.companyName || '',
        'Owner': c.ownerName || c.contactPerson || '',
        'Email': c.email || '',
        'Phone': c.phone || '',
        'Mobile': c.mobile || '',
        'WhatsApp': c.whatsappNumber || '',
        'City': c.city || '',
        'Status': c.status === 'suspended' ? 'Suspended' : 'Active',
        'Subscription': getSubCategory(c),
        'Plan Amount': sub.amount || '',
        'Trial End': fmtD(sub.trialEndDate),
        'Next Payment': fmtD(sub.nextPaymentDate),
        'Users': c.userCount ?? '',
        'Active Modules Count': activeMods.length,
        'Active Modules': activeMods.join(', '),
        'Created': fmtD(c.createdAt),
      };
    });

    if (rows.length === 0) { alert('No companies match the current filter to export.'); return; }

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 26 }, { wch: 18 }, { wch: 26 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 11 }, { wch: 12 }, { wch: 12 },
      { wch: 7 }, { wch: 10 }, { wch: 70 }, { wch: 12 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const tag = subscriptionFilter !== 'all' ? `-${subscriptionFilter}`
      : statusFilter !== 'all' ? `-${statusFilter}` : '';
    XLSX.writeFile(wb, `amalerp-clients${tag}-${ts}.xlsx`);
  };

  const getSortValue = (company, column) => {
    if (column === 'created') return company.createdAt ? new Date(company.createdAt).getTime() : 0;
    if (column === 'lastLogin') return company.lastLogin ? company.lastLogin.getTime() : 0;
    if (column === 'status') return company.status === 'suspended' ? 0 : 1;
    if (column === 'subscription') {
      const cat = getSubCategory(company);
      const order = { expired: 0, trial: 1, monthly: 2, yearly: 3 };
      return order[cat] ?? 0;
    }
    if (column === 'users') return company.userCount || 0;
    if (column === 'totalDocs') return company.totalDocs || 0;
    if (column === 'aiUsage') return company.aiUsage?.callCount || 0;
    return 0;
  };

  // Derive unique cities for filter dropdowns
  const companyCities = [...new Set(companies.map(c => c.city).filter(Boolean))].sort();

  // Filter and sort companies
  const filteredCompanies = companies
    .filter(company => {
      // Text search — name, email, city, phone numbers, and any user name/email/phone
      const q = searchTerm.toLowerCase();
      const matchesSearch = !q ||
        company.companyName?.toLowerCase().includes(q) ||
        company.email?.toLowerCase().includes(q) ||
        company.city?.toLowerCase().includes(q) ||
        company.phone?.toLowerCase().includes(q) ||
        company.mobile?.toLowerCase().includes(q) ||
        company.whatsappNumber?.toLowerCase().includes(q) ||
        company.userSearchText?.includes(q);
      if (!matchesSearch) return false;

      // Status filter
      if (statusFilter === 'active' && company.status === 'suspended') return false;
      if (statusFilter === 'suspended' && company.status !== 'suspended') return false;

      // Subscription filter
      if (subscriptionFilter !== 'all') {
        const subCat = getSubCategory(company);
        if (subscriptionFilter !== subCat) return false;
      }

      // City filter
      if (cityFilter !== 'all' && (company.city || '') !== cityFilter) return false;

      // "Heard about us" filter. Known channels match exactly; any other free-text
      // (from the Signup "Other" box) groups under 'Other'; blank = 'Not specified'.
      if (heardFromFilter !== 'all') {
        const h = (company.heardFrom || '').trim();
        const KNOWN = ['WhatsApp', 'Facebook / Instagram', 'Google', 'YouTube', 'Reference'];
        const cat = !h ? 'Not specified' : (KNOWN.includes(h) ? h : 'Other');
        if (cat !== heardFromFilter) return false;
      }

      // Sales person filter
      if (salesPersonFilter !== 'all') {
        const spId = company.subscription?.salesPersonId || '';
        if (salesPersonFilter === 'none' && spId) return false;
        if (salesPersonFilter !== 'none' && spId !== salesPersonFilter) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;
      const aVal = getSortValue(a, sortColumn);
      const bVal = getSortValue(b, sortColumn);
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

  // ── Pagination (50 per page) over the filtered+sorted list ──
  const COMPANIES_PER_PAGE = 50;
  const companyTotalPages = Math.max(1, Math.ceil(filteredCompanies.length / COMPANIES_PER_PAGE));
  const safeCompanyPage = Math.min(companyPage, companyTotalPages);
  const pagedCompanies = filteredCompanies.slice(
    (safeCompanyPage - 1) * COMPANIES_PER_PAGE,
    safeCompanyPage * COMPANIES_PER_PAGE
  );

  // Reset to page 1 whenever the filter/search criteria change.
  useEffect(() => {
    setCompanyPage(1);
  }, [searchTerm, statusFilter, subscriptionFilter, cityFilter, salesPersonFilter]);

  // Count active filters
  const activeCompanyFilterCount = [statusFilter !== 'all', subscriptionFilter !== 'all', cityFilter !== 'all', salesPersonFilter !== 'all'].filter(Boolean).length;

  // ═══════════════════════════════════════════════════════════════════
  // REVENUE METRICS — lifecycle-aware, first-year vs renewal split
  // ═══════════════════════════════════════════════════════════════════
  // Lifecycle buckets:
  //   trial   — never paid (no plan, or plan === 'trial')
  //   active  — paying, not suspended, payment not >30 days overdue
  //   lapsed  — was paying but: payment >30 days overdue OR suspended for non-payment
  //
  // Only `active` counts toward MRR/ARR. `lapsed` is shown separately as
  // "lost revenue" so suspended/non-paying customers stop inflating run-rate.
  //
  // Payments are read from subscription.paymentHistory[] when present. For
  // legacy companies without paymentHistory, we synthesize entries from
  // setupFee + firstPaymentDate and amount + lastPaymentDate. This is a
  // best-effort approximation — anything older than the most recent payment
  // is not visible. Once Super Admin starts adding paymentHistory entries
  // in the Subscription modal, accuracy improves.
  const revenueMetrics = React.useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const LAPSED_GRACE_DAYS = 30;

    const realCompanies = companies.filter(c => !c.replicateFrom);

    // Lifecycle classification
    const getLifecycle = (c) => {
      const sub = c.subscription;
      if (!sub || sub.plan === 'trial') return 'trial';
      const nextDue = sub.nextPaymentDate ? new Date(sub.nextPaymentDate) : null;
      const daysOverdue = nextDue ? Math.floor((now - nextDue) / (1000 * 60 * 60 * 24)) : -Infinity;
      if (c.status === 'suspended') return 'lapsed';
      if (daysOverdue > LAPSED_GRACE_DAYS) return 'lapsed';
      return 'active';
    };

    const byLifecycle = { trial: [], active: [], lapsed: [] };
    realCompanies.forEach(c => {
      const lc = getLifecycle(c);
      byLifecycle[lc].push({ ...c, _lifecycle: lc });
    });

    // Best-effort payment history extractor — uses paymentHistory[] when set,
    // otherwise synthesizes from setupFee/firstPaymentDate + amount/lastPaymentDate.
    const getPayments = (c) => {
      const sub = c.subscription || {};
      if (Array.isArray(sub.paymentHistory) && sub.paymentHistory.length > 0) {
        return sub.paymentHistory.map(p => ({
          ...p,
          date: p.date,
          amount: Number(p.amount) || 0,
          type: p.type || 'renewal',
        }));
      }
      const out = [];
      if (sub.setupFee && Number(sub.setupFee) > 0 && sub.firstPaymentDate) {
        out.push({ date: sub.firstPaymentDate, amount: Number(sub.setupFee), type: 'setup' });
      }
      if (sub.lastPaymentDate && Number(sub.amount) > 0) {
        const sameAsFirst = sub.firstPaymentDate && new Date(sub.firstPaymentDate).getTime() === new Date(sub.lastPaymentDate).getTime();
        out.push({ date: sub.lastPaymentDate, amount: Number(sub.amount), type: sameAsFirst ? 'setup' : 'renewal' });
      }
      return out;
    };

    // Flatten all payments across all companies for lifetime / yearly metrics
    const allPayments = realCompanies.flatMap(c =>
      getPayments(c).map(p => ({ ...p, companyId: c.id, companyName: c.companyName, lifecycle: getLifecycle(c) }))
    );

    const sumByType = (type) => allPayments.filter(p => p.type === type).reduce((s, p) => s + p.amount, 0);
    const lifetimeSetup = sumByType('setup');
    const lifetimeRenewal = sumByType('renewal');
    const lifetimeDiscount = sumByType('discount');
    const lifetimeRefund = sumByType('refund');
    const lifetimeEarned = lifetimeSetup + lifetimeRenewal - lifetimeRefund - lifetimeDiscount;

    // Earnings by calendar year, split by payment type (for stacked bar chart)
    const byYearMap = {};
    allPayments.forEach(p => {
      if (!p.date) return;
      const y = new Date(p.date).getFullYear();
      if (!byYearMap[y]) byYearMap[y] = { year: y, setup: 0, renewal: 0, discount: 0, refund: 0 };
      byYearMap[y][p.type] = (byYearMap[y][p.type] || 0) + p.amount;
    });
    const earningsByYear = Object.values(byYearMap)
      .map(y => ({ ...y, total: y.setup + y.renewal - y.refund - y.discount }))
      .sort((a, b) => a.year - b.year);

    // This year's split (first-year customer revenue vs existing-customer renewals)
    const thisYear = byYearMap[currentYear] || { setup: 0, renewal: 0, discount: 0, refund: 0 };
    const thisYearSetup = thisYear.setup;
    const thisYearRenewal = thisYear.renewal;
    const thisYearTotal = thisYearSetup + thisYearRenewal - thisYear.refund - thisYear.discount;

    // MRR / ARR — from active companies ONLY
    const monthlyAmount = (c) => {
      const sub = c.subscription || {};
      const amt = Number(sub.amount) || 0;
      const m = planMonths(sub.plan);   // 1 / 3 / 6 / 12 (0 for trial)
      return m ? amt / m : 0;
    };
    const mrr = byLifecycle.active.reduce((s, c) => s + monthlyAmount(c), 0);
    const arr = mrr * 12;

    const monthlyActive = byLifecycle.active.filter(c => c.subscription?.plan === 'monthly');
    const yearlyActive = byLifecycle.active.filter(c => c.subscription?.plan === 'yearly');
    const monthlyMRR = monthlyActive.reduce((s, c) => s + (Number(c.subscription?.amount) || 0), 0);
    const yearlyTotalCommitted = yearlyActive.reduce((s, c) => s + (Number(c.subscription?.amount) || 0), 0);

    // Expected next 12 months — sum of yearly equivalents from active companies
    const expectedNext12 = byLifecycle.active.reduce((s, c) => s + monthlyAmount(c) * 12, 0);

    // Lost revenue — what we WOULD be making if lapsed companies were still paying
    const lostAnnualRevenue = byLifecycle.lapsed.reduce((s, c) => s + monthlyAmount(c) * 12, 0);

    // Renewed: companies that have paid more than once (lastPaymentDate != firstPaymentDate)
    const isRenewed = (c) => {
      const sub = c.subscription || {};
      // If they have payment history, count renewals explicitly
      if (Array.isArray(sub.paymentHistory) && sub.paymentHistory.length > 0) {
        return sub.paymentHistory.filter(p => p.type === 'renewal').length >= 1;
      }
      if (!sub.firstPaymentDate || !sub.lastPaymentDate) return false;
      return new Date(sub.lastPaymentDate).getTime() > new Date(sub.firstPaymentDate).getTime();
    };
    const renewedActive = byLifecycle.active.filter(isRenewed);
    const renewedLapsed = byLifecycle.lapsed.filter(isRenewed);
    const renewedAll = [...renewedActive, ...renewedLapsed];

    // Renewal rate = renewed (from those who reached renewal eligibility) /
    // (renewed + lapsed). Trials are excluded since they never had a chance.
    const renewalEligible = renewedAll.length + byLifecycle.lapsed.length;
    const renewalRate = renewalEligible > 0 ? (renewedAll.length / renewalEligible) * 100 : 0;

    // Trials breakdown
    const trialsActive = byLifecycle.trial.filter(c => {
      const trialEnd = c.subscription?.trialEndDate
        ? new Date(c.subscription.trialEndDate)
        : new Date(new Date(c.createdAt || Date.now()).getTime() + 7 * 24 * 60 * 60 * 1000);
      return trialEnd > now;
    });
    const trialsExpired = byLifecycle.trial.length - trialsActive.length;

    // Renewing this month (active companies whose nextPaymentDate is this calendar month)
    const renewingThisMonth = byLifecycle.active.filter(c => {
      const d = c.subscription?.nextPaymentDate ? new Date(c.subscription.nextPaymentDate) : null;
      if (!d) return false;
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    });
    const renewingThisMonthAmount = renewingThisMonth.reduce((s, c) => s + (Number(c.subscription?.amount) || 0), 0);

    // Upcoming renewals: next 6 months (active only)
    const next6Months = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(currentYear, currentMonth + i, 1);
      next6Months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-PK', { month: 'short', year: 'numeric' }),
        year: d.getFullYear(),
        month: d.getMonth(),
        companies: [],
        amount: 0,
      });
    }
    byLifecycle.active.forEach(c => {
      const d = c.subscription?.nextPaymentDate ? new Date(c.subscription.nextPaymentDate) : null;
      if (!d) return;
      const bucket = next6Months.find(b => b.year === d.getFullYear() && b.month === d.getMonth());
      if (bucket) {
        bucket.companies.push(c);
        bucket.amount += Number(c.subscription?.amount) || 0;
      }
    });

    // Revenue Book — group ACTIVE companies by renewal year. Lapsed go in
    // their own panel below so they don't inflate "active revenue" numbers.
    const activeByYearMap = {};
    byLifecycle.active.forEach(c => {
      const due = c.subscription?.nextPaymentDate;
      const y = due ? new Date(due).getFullYear() : 'No due date';
      if (!activeByYearMap[y]) activeByYearMap[y] = { year: y, companies: [], totalAmount: 0 };
      activeByYearMap[y].companies.push(c);
      activeByYearMap[y].totalAmount += Number(c.subscription?.amount) || 0;
    });
    const revenueBook = Object.values(activeByYearMap).sort((a, b) => {
      if (typeof a.year === 'string') return 1;
      if (typeof b.year === 'string') return -1;
      return a.year - b.year;
    });

    // Top 10 active companies by annualized recurring revenue
    const topCompanies = [...byLifecycle.active]
      .map(c => ({ ...c, _annualized: monthlyAmount(c) * 12 }))
      .sort((a, b) => b._annualized - a._annualized)
      .slice(0, 10);

    // Top-5 concentration (active only)
    const top5MRR = topCompanies.slice(0, 5).reduce((s, c) => s + (c._annualized / 12), 0);
    const concentrationPct = mrr > 0 ? (top5MRR / mrr) * 100 : 0;

    // Lapsed list with days overdue (for follow-up)
    const lapsedList = byLifecycle.lapsed.map(c => {
      const due = c.subscription?.nextPaymentDate ? new Date(c.subscription.nextPaymentDate) : null;
      const daysOverdue = due ? Math.floor((now - due) / (1000 * 60 * 60 * 24)) : null;
      const reason = c.status === 'suspended' ? 'Suspended' : `Overdue ${daysOverdue}d`;
      return { ...c, _daysOverdue: daysOverdue, _lapseReason: reason };
    }).sort((a, b) => (b._daysOverdue || 0) - (a._daysOverdue || 0));

    // Flat, sortable client list for the Revenue table — one row per PAYING
    // client (active + lapsed). Everything the table sorts on is precomputed.
    const clientRows = [...byLifecycle.active, ...byLifecycle.lapsed].map(c => {
      const sub = c.subscription || {};
      const nextDue = sub.nextPaymentDate ? new Date(sub.nextPaymentDate) : null;
      const daysLeft = nextDue ? Math.ceil((nextDue - now) / (1000 * 60 * 60 * 24)) : null;
      const suspended = c.status === 'suspended';
      const overdue = nextDue ? nextDue < now : false;
      return {
        id: c.id,
        company: c.companyName || 'Unknown',
        salesPerson: sub.salesPersonName || '—',
        plan: sub.plan || '',
        planLabel: planLabel(sub.plan),
        perBill: Number(sub.amount) || 0,
        perMonth: monthlyAmount(c),
        nextDueTs: nextDue ? nextDue.getTime() : null,
        nextDueLabel: nextDue
          ? nextDue.toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: '2-digit' })
          : '—',
        daysLeft,
        status: suspended ? 'Suspended' : (overdue ? 'Overdue' : 'Active'),
      };
    });

    return {
      clientRows,
      // Lifecycle buckets
      active: byLifecycle.active,
      lapsed: byLifecycle.lapsed,
      trials: byLifecycle.trial,
      trialsActive, trialsExpired,
      // Renewal metrics
      renewedActive, renewedAll,
      renewalRate,
      // MRR/ARR (active only)
      mrr, arr,
      monthlyActive, yearlyActive,
      monthlyMRR, yearlyTotalCommitted,
      // First-year vs renewal split
      lifetimeEarned, lifetimeSetup, lifetimeRenewal, lifetimeDiscount, lifetimeRefund,
      thisYearSetup, thisYearRenewal, thisYearTotal,
      earningsByYear,
      // Forward-looking
      expectedNext12,
      lostAnnualRevenue,
      renewingThisMonth, renewingThisMonthAmount,
      next6Months,
      // Tables
      revenueBook,
      topCompanies,
      lapsedList,
      concentrationPct,
    };
  }, [companies]);

  // Revenue table rows after search + status filter + column sort.
  const revTableRows = React.useMemo(() => {
    const q = revSearch.trim().toLowerCase();
    let rows = revenueMetrics.clientRows.filter(r => {
      if (revStatusFilter === 'active' && r.status !== 'Active') return false;
      if (revStatusFilter === 'overdue' && r.status !== 'Overdue') return false;
      if (revStatusFilter === 'suspended' && r.status !== 'Suspended') return false;
      if (q && !(`${r.company} ${r.salesPerson} ${r.planLabel}`.toLowerCase().includes(q))) return false;
      return true;
    });
    const { field, dir } = revSort;
    const mul = dir === 'asc' ? 1 : -1;
    rows = rows.slice().sort((a, b) => {
      let av = a[field], bv = b[field];
      // Nulls (e.g. no due date) always sort to the bottom regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') { av = av.toLowerCase(); bv = String(bv).toLowerCase(); return av < bv ? -1 * mul : av > bv ? 1 * mul : 0; }
      return (av - bv) * mul;
    });
    return rows;
  }, [revenueMetrics.clientRows, revSearch, revStatusFilter, revSort]);

  const clearCompanyFilters = () => {
    setStatusFilter('all');
    setSubscriptionFilter('all');
    setCityFilter('all');
    setHeardFromFilter('all');
    setSalesPersonFilter('all');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-PK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ---- Backup Entire Firebase ----
  // Schema version is bumped when the backup file format changes in a way
  // that the restore path needs to know about.
  // v3 — Firestore Timestamps are tagged on the way out and rebuilt on the way
  //      in. Before v3 a plain JSON.stringify turned every Timestamp into a
  //      {seconds, nanoseconds} map, so a restored createdAt/updatedAt came back
  //      as a lifeless object: date sorting and "created on" columns broke.
  const BACKUP_SCHEMA_VERSION = 3;

  // Tag Timestamps/Dates so JSON can't flatten them into anonymous maps.
  const TS_TAG = '__amalTimestamp__';
  const encodeForBackup = (val) => {
    if (val === null || val === undefined) return val;
    if (val instanceof Timestamp) return { [TS_TAG]: { seconds: val.seconds, nanoseconds: val.nanoseconds } };
    if (val instanceof Date) {
      const ms = val.getTime();
      return { [TS_TAG]: { seconds: Math.floor(ms / 1000), nanoseconds: (ms % 1000) * 1e6 } };
    }
    if (Array.isArray(val)) return val.map(encodeForBackup);
    if (typeof val === 'object') {
      const out = {};
      for (const [k, v] of Object.entries(val)) out[k] = encodeForBackup(v);
      return out;
    }
    return val;
  };
  const decodeFromBackup = (val) => {
    if (val === null || val === undefined) return val;
    if (Array.isArray(val)) return val.map(decodeFromBackup);
    if (typeof val === 'object') {
      const tagged = val[TS_TAG];
      if (tagged && typeof tagged.seconds === 'number') {
        return new Timestamp(tagged.seconds, tagged.nanoseconds || 0);
      }
      // Pre-v3 backups: a Timestamp had already collapsed to a bare
      // {seconds, nanoseconds} map. Rebuild those too so old ZIPs restore clean.
      const keys = Object.keys(val);
      const isLegacyTs =
        typeof val.seconds === 'number' &&
        typeof val.nanoseconds === 'number' &&
        keys.every(k => k === 'seconds' || k === 'nanoseconds' || k === 'type');
      if (isLegacyTs) return new Timestamp(val.seconds, val.nanoseconds);
      const out = {};
      for (const [k, v] of Object.entries(val)) out[k] = decodeFromBackup(v);
      return out;
    }
    return val;
  };
  const snapToItems = (snap) => snap.docs.map(d => ({ id: d.id, ...encodeForBackup(d.data()) }));

  // Per-company subcollection list. Anything written under
  // companies/{id}/{name} MUST be in here, otherwise the backup
  // silently misses it.
  const BACKUP_SUBCOLLECTIONS = [
    'users', 'roles', 'financialYears', 'accounts',
    'customers', 'vendors', 'banks', 'employees',
    'inventoryCategories', 'inventorySubCategories', 'inventoryItems', 'inventoryLocations',
    'regions', 'areas', 'jobs',
    'transactions', 'subTransactions', 'stockFlow',
    'documentCounters', 'auditLog', 'invites',
    'settings',
    // Added 2026-04-28 — gap-fill audit:
    'branches', 'units', 'salaryStaff', 'importChargeTemplates',
    'stockTransactions', 'stockLevels', 'journalEntries', 'usageStats',
    // Added 2026-07-14 — gap-fill audit. These were being SILENTLY MISSED from
    // every backup, i.e. real customer data that could never have been restored.
    'fixedAssets', 'fixedAssetCategories',      // Fixed Assets module
    'posCounters', 'posShifts',                 // Multi-POS: counters + shift history
    'customerItemPricing',                      // Customer price lists
    'vendorItemPricing',                        // Vendor price lists
    'whatsappMessages', 'whatsappTemplates',    // WhatsApp
    'stockAudits',                              // Stock Audit (see NESTED_SUBCOLLECTIONS)
    'locations',
    'featureAccessLog', 'featureSuggestions', 'featureSuggestionsState',
  ];

  // Sub-collections that live one level DEEPER (companies/{id}/{parent}/{docId}/{child}).
  // The old backup only walked one level, so these were invisible — for a Stock
  // Audit that meant the audit header was saved but every counted line was lost.
  // Stored in the file under the key `parent/{docId}/child`, which the restore's
  // path-splitting handles as-is.
  const NESTED_SUBCOLLECTIONS = { stockAudits: ['lines'] };

  // NOTE: `mailQueue` is deliberately NOT backed up — restoring it would re-send
  // every queued email. It is a transient queue, not data.
  const TOP_LEVEL_COLLECTIONS = [
    'users', 'superAdmins', 'pendingSignups', 'signupNotifications',
    // Added 2026-07-14 — were missing entirely:
    'platformConfig',    // new-company defaults + signup sources
    'qrCampaigns',       // marketing QR campaigns + scan counts
    'surveys',           // "Ask Users" surveys (responses handled below)
    'salesPersons', 'featureRequests', 'platformActivity',
  ];

  // Top-level collections with a nested sub-collection.
  const TOP_LEVEL_NESTED = { surveys: ['responses'] };

  // Platform-wide collections: only included in a FULL backup. A scoped backup
  // (one company / paid / active) must not carry platform config, or restoring it
  // would overwrite global settings.
  const PLATFORM_ONLY = new Set([
    'superAdmins', 'pendingSignups', 'signupNotifications',
    'platformConfig', 'qrCampaigns', 'surveys', 'salesPersons',
    'featureRequests', 'platformActivity',
  ]);

  // Recursively list every file under a Storage prefix. Returns array of full
  // storage paths. Heavy on round-trips for deep folder trees, but Firebase
  // Storage gives no other way without a Cloud Function.
  const listAllStorageFiles = async (prefix) => {
    const out = [];
    const folder = prefix ? ref(storage, prefix) : ref(storage);
    const page = await listAll(folder);
    out.push(...page.items.map(it => it.fullPath));
    for (const sub of page.prefixes) {
      const nested = await listAllStorageFiles(sub.fullPath);
      out.push(...nested);
    }
    return out;
  };

  /**
   * @param {'all'|'company'|'paid'|'active'} scope
   * @param {string} onlyCompanyId  used when scope === 'company'
   */
  const handleBackup = async (scope = 'all', onlyCompanyId = '') => {
    if (backupRunning) return;
    setBackupRunning(true);
    setBackupProgress('Starting backup...');
    setBackupResult(null);

    try {
      const startedAt = new Date().toISOString();
      const stats = { topLevel: {}, perCompany: {}, storage: { files: 0, bytes: 0 } };
      const errors = [];
      let totalDocs = 0;
      const zip = new JSZip();
      const isFull = scope === 'all';

      // Manifest is finalized at the end — placeholder added now so it's
      // always the first entry the restore path inspects.
      const manifest = {
        schemaVersion: BACKUP_SCHEMA_VERSION,
        appVersion: packageJson.version || 'unknown',
        appName: packageJson.name || 'amalerp',
        firebaseProjectId: db?.app?.options?.projectId || 'unknown',
        timestamp: startedAt,
        backedUpBy: saUser?.email || saUser?.uid || 'unknown',
        includeStorage,
        scope,                 // 'all' | 'company' | 'paid' | 'active'
        scopeCompanyId: scope === 'company' ? onlyCompanyId : null,
        collections: [],
        subcollections: BACKUP_SUBCOLLECTIONS.slice(),
        nestedSubcollections: NESTED_SUBCOLLECTIONS,
        companies: [],
        stats,
        errors,
      };

      // ─── Which companies are we backing up? ────────────────────────────
      setBackupProgress('Loading companies...');
      const companiesSnap = await getDocs(query(collection(db, 'companies'), orderBy('companyName')));
      const isPaid = (c) => c.subscription && isPaidPlan(c.subscription.plan);
      const targetDocs = companiesSnap.docs.filter(d => {
        const c = d.data();
        if (scope === 'company') return d.id === onlyCompanyId;
        if (scope === 'paid') return c.status !== 'suspended' && isPaid(c);
        if (scope === 'active') return c.status !== 'suspended';
        return true; // all
      });
      if (targetDocs.length === 0) throw new Error('No companies matched the selected backup scope.');
      const targetIds = new Set(targetDocs.map(d => d.id));

      // ─── Top-level collections ─────────────────────────────────────────
      // A SCOPED backup carries only the users belonging to the chosen companies
      // and skips platform-wide config, so restoring it can never clobber global
      // settings or other clients.
      const topLevelData = {};
      for (const col of TOP_LEVEL_COLLECTIONS) {
        if (!isFull && PLATFORM_ONLY.has(col)) continue;
        setBackupProgress(`Backing up ${col}...`);
        try {
          const snap = await getDocs(collection(db, col));
          let items = snapToItems(snap);
          if (!isFull && col === 'users') {
            items = items.filter(u => (u.companies || []).some(cid => targetIds.has(cid)));
          }
          topLevelData[col] = items;
          stats.topLevel[col] = items.length;
          totalDocs += items.length;
          manifest.collections.push(col);

          // Nested (e.g. surveys/{id}/responses) — stored with a slash key so the
          // restore's path-splitting writes it straight back to the right place.
          for (const child of (TOP_LEVEL_NESTED[col] || [])) {
            for (const parent of items) {
              const cSnap = await getDocs(collection(db, col, parent.id, child));
              if (cSnap.size > 0) {
                const key = `${col}/${parent.id}/${child}`;
                topLevelData[key] = snapToItems(cSnap);
                stats.topLevel[key] = cSnap.size;
                totalDocs += cSnap.size;
              }
            }
          }
        } catch (e) {
          console.warn(`Top-level ${col} failed:`, e);
          errors.push(`${col}: ${e?.message || 'unknown'}`);
        }
      }
      zip.file('top-level.json', JSON.stringify(topLevelData, null, 2));

      // ─── Companies index (only the ones in scope) ──────────────────────
      const companiesIndex = targetDocs.map(d => ({ id: d.id, ...encodeForBackup(d.data()) }));
      zip.file('companies-index.json', JSON.stringify(companiesIndex, null, 2));
      stats.topLevel.companies = companiesIndex.length;
      totalDocs += companiesIndex.length;

      // ─── Per-company subcollections ────────────────────────────────────
      // One file per company keeps each JSON small enough to parse without
      // OOM, and the ZIP folder structure is human-browsable.
      for (let i = 0; i < targetDocs.length; i++) {
        const compDoc = targetDocs[i];
        const compId = compDoc.id;
        const compName = compDoc.data().companyName || compId;
        setBackupProgress(`Backing up ${compName} (${i + 1}/${targetDocs.length})...`);

        const compData = { _company: { id: compId, ...encodeForBackup(compDoc.data()) } };
        const compStats = { _company: 1 };

        for (const sub of BACKUP_SUBCOLLECTIONS) {
          try {
            const subSnap = await getDocs(collection(db, 'companies', compId, sub));
            if (subSnap.size > 0) {
              compData[sub] = snapToItems(subSnap);
              compStats[sub] = subSnap.size;
              totalDocs += subSnap.size;

              // Walk one level deeper where needed (e.g. stockAudits/{id}/lines).
              for (const child of (NESTED_SUBCOLLECTIONS[sub] || [])) {
                for (const parent of subSnap.docs) {
                  const cSnap = await getDocs(collection(db, 'companies', compId, sub, parent.id, child));
                  if (cSnap.size > 0) {
                    const key = `${sub}/${parent.id}/${child}`;
                    compData[key] = snapToItems(cSnap);
                    compStats[key] = cSnap.size;
                    totalDocs += cSnap.size;
                  }
                }
              }
            }
          } catch (e) {
            console.warn(`Skipped ${compId}/${sub}: ${e?.message || e}`);
            compStats[sub] = `error: ${e?.message || 'unknown'}`;
            errors.push(`${compName}/${sub}: ${e?.message || 'unknown'}`);
          }
        }

        zip.file(`companies/${compId}.json`, JSON.stringify(compData, null, 2));
        manifest.companies.push({ id: compId, name: compName });
        stats.perCompany[compId] = { name: compName, counts: compStats };
      }

      // ─── Storage files (optional) ─────────────────────────────────────
      if (includeStorage) {
        setBackupProgress('Listing storage files...');
        let paths = [];
        try {
          paths = await listAllStorageFiles('');
        } catch (e) {
          console.warn('Storage listAll failed:', e);
          stats.storage.error = e?.message || 'listAll failed';
        }

        // A scoped backup only carries the files of the companies it contains.
        if (!isFull) {
          paths = paths.filter(p => [...targetIds].some(cid => p.includes(cid)));
        }

        for (let i = 0; i < paths.length; i++) {
          const p = paths[i];
          setBackupProgress(`Storage ${i + 1}/${paths.length}: ${p.slice(-40)}`);
          try {
            const url = await getDownloadURL(ref(storage, p));
            const resp = await fetch(url);
            const blob = await resp.blob();
            zip.file(`storage/${p}`, blob);
            stats.storage.files += 1;
            stats.storage.bytes += blob.size;
          } catch (e) {
            console.warn(`Storage skip ${p}: ${e?.message}`);
          }
        }
      }

      // ─── Finalize manifest ─────────────────────────────────────────────
      manifest.totalDocuments = totalDocs;
      manifest.companiesCount = targetDocs.length;
      manifest.completedAt = new Date().toISOString();
      zip.file('manifest.json', JSON.stringify(manifest, null, 2));

      // ─── Build + download ZIP ──────────────────────────────────────────
      setBackupProgress('Compressing...');
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      // Name the file after what's inside it, so a partial backup is never mistaken
      // for a full one months later.
      const scopeTag = scope === 'company'
        ? `company-${(targetDocs[0]?.data().companyName || onlyCompanyId).replace(/[^\w-]+/g, '_').slice(0, 30)}`
        : scope === 'paid' ? 'paid-only'
        : scope === 'active' ? 'active-only'
        : 'full';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `amalerp-backup-${scopeTag}-${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // ─── Result panel — per-collection counts ─────────────────────────
      setBackupResult({
        ok: true,
        totalDocs,
        companiesCount: targetDocs.length,
        scope,
        stats,
        errors,                 // surfaced, not swallowed
        sizeMB: (blob.size / 1024 / 1024).toFixed(2),
        manifest,
      });
      setBackupProgress(
        `Backup complete · ${targetDocs.length} company(s) · ${totalDocs} docs · ${(blob.size / 1024 / 1024).toFixed(1)} MB` +
        (errors.length ? ` · ⚠ ${errors.length} warning(s)` : '')
      );
      setTimeout(() => setBackupProgress(''), 8000);
    } catch (error) {
      console.error('Backup error:', error);
      setBackupResult({ ok: false, error: error.message });
      setBackupProgress(`Backup failed: ${error.message}`);
      setTimeout(() => setBackupProgress(''), 8000);
    } finally {
      setBackupRunning(false);
    }
  };

  // ─── Restore from ZIP/JSON ───────────────────────────────────────────
  // Two modes:
  //   append   — write only docs that don't already exist (safe default)
  //   replace  — overwrite existing docs by id (still does NOT delete docs
  //              that exist in the live DB but not in the backup)
  const validateRestoreFile = async (file) => {
    setRestoreManifestError('');
    setRestoreManifest(null);
    if (!file) return;
    try {
      let manifest;
      if (file.name.toLowerCase().endsWith('.zip')) {
        const buf = await file.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        const m = zip.file('manifest.json');
        if (!m) throw new Error('manifest.json missing — not a valid AmalERP backup ZIP');
        manifest = JSON.parse(await m.async('string'));
      } else if (file.name.toLowerCase().endsWith('.json')) {
        const text = await file.text();
        const parsed = JSON.parse(text);
        // Legacy v1 single-file format — synthesize a manifest from it.
        manifest = {
          schemaVersion: parsed.schemaVersion || 1,
          appVersion: parsed.appVersion || 'unknown (legacy)',
          firebaseProjectId: parsed.firebaseProjectId || null,
          timestamp: parsed.timestamp,
          companies: Object.keys(parsed.companyData || {}).map(id => ({ id, name: parsed.companyData[id]?._company?.companyName })),
          totalDocuments: parsed.totalDocuments,
          companiesCount: parsed.companiesCount,
          _legacyPayload: parsed, // keep for the importer
        };
      } else {
        throw new Error('Unsupported file — choose .zip or .json');
      }

      // Project-id guard
      const currentProject = db?.app?.options?.projectId;
      if (manifest.firebaseProjectId && currentProject && manifest.firebaseProjectId !== currentProject) {
        setRestoreManifestError(`Backup was taken from project "${manifest.firebaseProjectId}" but this app is running on "${currentProject}". Restore is BLOCKED to prevent cross-project corruption.`);
        setRestoreManifest(manifest);
        return;
      }
      setRestoreManifest(manifest);
    } catch (e) {
      setRestoreManifestError(e?.message || 'Failed to read backup file');
    }
  };

  const writeBatchedDocs = async (collPath, items, mode, progressLabel) => {
    if (!items || items.length === 0) return { written: 0, skipped: 0 };
    let written = 0, skipped = 0;
    // 400 ops/batch leaves headroom under Firestore's 500 limit.
    for (let i = 0; i < items.length; i += 400) {
      const slice = items.slice(i, i + 400);
      const batch = writeBatch(db);
      for (const it of slice) {
        if (!it.id) continue;
        const { id, ...rest } = it;
        const target = doc(db, ...collPath.split('/'), id);
        if (mode === 'append') {
          // Skip if exists. Read first.
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDoc(target);
          if (snap.exists()) { skipped += 1; continue; }
        }
        // Turn the tagged date maps back into real Firestore Timestamps.
        batch.set(target, decodeFromBackup(rest), { merge: mode === 'replace' ? false : true });
        written += 1;
      }
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
      setRestoreProgress(`${progressLabel}: ${i + slice.length}/${items.length}`);
    }
    return { written, skipped };
  };

  const handleRestore = async () => {
    if (!restoreFile || !restoreManifest || restoreManifestError) return;
    if (!window.confirm(`Restore from backup taken on ${restoreManifest.timestamp}?\n\nMode: ${restoreMode === 'replace' ? 'REPLACE existing docs' : 'APPEND only (skip existing)'}\n\nThis will write to Firestore and cannot be undone via this tool.`)) return;

    setRestoreRunning(true);
    setRestoreResult(null);
    setRestoreProgress('Reading backup...');
    const totals = { written: 0, skipped: 0, errors: [] };

    try {
      let topLevelData = {};
      let companiesIndex = [];
      let perCompany = {}; // id -> data
      let zipRef = null;   // kept in scope so storage files can be re-uploaded below

      if (restoreFile.name.toLowerCase().endsWith('.zip')) {
        const buf = await restoreFile.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        zipRef = zip;
        const tl = zip.file('top-level.json');
        if (tl) topLevelData = JSON.parse(await tl.async('string'));
        const ci = zip.file('companies-index.json');
        if (ci) companiesIndex = JSON.parse(await ci.async('string'));
        // Each per-company file
        const compFiles = Object.keys(zip.files).filter(p => p.startsWith('companies/') && p.endsWith('.json'));
        for (const path of compFiles) {
          const id = path.slice('companies/'.length, -'.json'.length);
          perCompany[id] = JSON.parse(await zip.file(path).async('string'));
        }
      } else {
        // Legacy v1 JSON
        const legacy = restoreManifest._legacyPayload;
        topLevelData = legacy.collections || {};
        companiesIndex = legacy.collections?.companies || [];
        perCompany = legacy.companyData || {};
      }

      // ─── Top-level ───
      for (const [colName, items] of Object.entries(topLevelData)) {
        if (colName === 'companies') continue; // handled below
        if (!Array.isArray(items)) continue;
        const r = await writeBatchedDocs(colName, items, restoreMode, `Top-level ${colName}`);
        totals.written += r.written;
        totals.skipped += r.skipped;
      }

      // ─── Companies index ───
      const r0 = await writeBatchedDocs('companies', companiesIndex, restoreMode, 'Companies');
      totals.written += r0.written;
      totals.skipped += r0.skipped;

      // ─── Per-company subcollections ───
      const compIds = Object.keys(perCompany);
      for (let i = 0; i < compIds.length; i++) {
        const compId = compIds[i];
        const data = perCompany[compId];
        const compName = data?._company?.companyName || compId;
        for (const [sub, items] of Object.entries(data || {})) {
          if (sub === '_company') continue;
          if (!Array.isArray(items)) continue;
          const r = await writeBatchedDocs(`companies/${compId}/${sub}`, items, restoreMode, `${compName} · ${sub}`);
          totals.written += r.written;
          totals.skipped += r.skipped;
        }
      }

      // ─── Storage files ───
      // The ZIP carries them under storage/<original path>. Until now the restore
      // only wrote Firestore, so logos, item images and uploaded documents that
      // were faithfully backed up could never actually come back.
      if (zipRef) {
        const filePaths = Object.keys(zipRef.files)
          .filter(p => p.startsWith('storage/') && !zipRef.files[p].dir);
        for (let i = 0; i < filePaths.length; i++) {
          const zipPath = filePaths[i];
          const livePath = zipPath.slice('storage/'.length);
          setRestoreProgress(`Storage ${i + 1}/${filePaths.length}: ${livePath.slice(-40)}`);
          try {
            // eslint-disable-next-line no-await-in-loop
            const blob = await zipRef.file(zipPath).async('blob');
            // eslint-disable-next-line no-await-in-loop
            await uploadBytes(ref(storage, livePath), blob);
            totals.storageUploaded = (totals.storageUploaded || 0) + 1;
          } catch (e) {
            totals.errors.push(`storage ${livePath}: ${e?.message || 'upload failed'}`);
          }
        }
      }

      setRestoreResult({ ok: true, ...totals });
      setRestoreProgress(
        `Restore complete · ${totals.written} written · ${totals.skipped} skipped` +
        (totals.storageUploaded ? ` · ${totals.storageUploaded} files` : '') +
        (totals.errors.length ? ` · ⚠ ${totals.errors.length} warning(s)` : '')
      );
    } catch (e) {
      console.error('Restore error:', e);
      setRestoreResult({ ok: false, error: e?.message || String(e), ...totals });
      setRestoreProgress(`Restore failed: ${e?.message}`);
    } finally {
      setRestoreRunning(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <LoginContainer>
        <LoginCard>
          <LoginLeftSection>
            <LoginBrandSection>
              <LoginLogo>AMAL ERP</LoginLogo>
              <LoginTagline>Platform Administration</LoginTagline>
            </LoginBrandSection>
            <LoginFeatureList>
              <LoginFeature>Manage All Companies</LoginFeature>
              <LoginFeature>Monitor Platform Activity</LoginFeature>
              <LoginFeature>Suspend / Activate Companies</LoginFeature>
              <LoginFeature>View Signup Notifications</LoginFeature>
            </LoginFeatureList>
          </LoginLeftSection>

          <LoginRightSection>
            <LoginFormContainer>
              <LoginFormHeader>
                <LoginShieldIcon><FiShield /></LoginShieldIcon>
                <LoginTitle>Super Admin</LoginTitle>
                <LoginSubtitle>Sign in to the admin panel</LoginSubtitle>
              </LoginFormHeader>

              {loginError && <LoginErrorMessage>{loginError}</LoginErrorMessage>}

              <LoginForm onSubmit={handleSuperAdminLogin}>
                <LoginInputGroup>
                  <LoginLabel>Email Address</LoginLabel>
                  <LoginInputWrapper>
                    <LoginIconWrapper><FiMail /></LoginIconWrapper>
                    <LoginInput
                      type="email"
                      placeholder="Enter super admin email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                    />
                  </LoginInputWrapper>
                </LoginInputGroup>

                <LoginInputGroup>
                  <LoginLabel>Password</LoginLabel>
                  <LoginInputWrapper>
                    <LoginIconWrapper><FiLock /></LoginIconWrapper>
                    <LoginInput
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter super admin password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                    />
                    <LoginPasswordToggle type="button" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </LoginPasswordToggle>
                  </LoginInputWrapper>
                </LoginInputGroup>

                <LoginSubmitButton type="submit" disabled={loginLoading}>
                  <FiLock />
                  {loginLoading ? 'Signing in...' : 'Sign In as Super Admin'}
                </LoginSubmitButton>
              </LoginForm>
            </LoginFormContainer>
          </LoginRightSection>
        </LoginCard>
      </LoginContainer>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Logo>
            <FiShield />
            AMAL ERP
          </Logo>
          <PageTitle>Super Admin Dashboard</PageTitle>
        </HeaderLeft>
        <HeaderRight>
          {saUser && <span style={{ fontSize: '12px', color: '#999', marginRight: 8 }}>{saUser.displayName || saUser.email} ({saRole})</span>}
          {isOwner && (
            <BulkEmailButton onClick={() => openEmailModal('bulk')}>
              <FiSend />
              Email All
            </BulkEmailButton>
          )}
          {isOwner && (
            <BulkEmailButton onClick={() => openWhatsappModal('bulk')} style={{ background: '#25D366' }}>
              <FiSend />
              WhatsApp All
            </BulkEmailButton>
          )}
          {isOwner && (
            <ToolsMenuWrap>
              <RefreshButton onClick={() => setShowToolsMenu(v => !v)} title="Admin tools" disabled={backupRunning && false}>
                <FiGrid />
                Manage
                <FiChevronDown style={{ marginLeft: -2 }} />
              </RefreshButton>
              {showToolsMenu && (
                <>
                  <MenuBackdrop onClick={() => setShowToolsMenu(false)} />
                  <ToolsDropdown onClick={(e) => e.stopPropagation()}>
                    <ToolsItem onClick={() => { setShowToolsMenu(false); setShowBackupModal(true); }} disabled={backupRunning}>
                      <FiArchive className={backupRunning ? 'spinning' : ''} />
                      {backupRunning ? (backupProgress || 'Backing up...') : 'Backup'}
                    </ToolsItem>
                    <ToolsDivider />
                    <ToolsItem onClick={() => { setShowToolsMenu(false); setShowRestoreModal(true); setRestoreFile(null); setRestoreManifest(null); setRestoreManifestError(''); setRestoreResult(null); setRestoreProgress(''); }} disabled={backupRunning}>
                      <FiUpload />
                      Restore
                    </ToolsItem>
                    <ToolsDivider />
                    <ToolsItem onClick={() => { setShowToolsMenu(false); openDefaultsModal(); }}>
                      <FiGrid />
                      New-User Defaults
                    </ToolsItem>
                    <ToolsItem onClick={() => { setShowToolsMenu(false); openSourcesModal(); }}>
                      <FiMail />
                      Signup Sources
                    </ToolsItem>
                    <ToolsItem onClick={() => { setShowToolsMenu(false); openQrModal(); }}>
                      <FiGrid />
                      QR Campaigns
                    </ToolsItem>
                    <ToolsItem onClick={() => { setShowToolsMenu(false); setShowSurveys(true); }}>
                      <FiMail />
                      Ask Users (Surveys)
                    </ToolsItem>
                  </ToolsDropdown>
                </>
              )}
            </ToolsMenuWrap>
          )}
          {/* COST: what the last company-list load actually cost, so the panel's own
              Firestore bill is visible. "cached" = served from the 10-minute cache. */}
          {(lastLoadReads !== null || lastLoadCached) && (
            <span
              title="Approximate Firestore reads used by the last company-list load. Cached loads cost nothing; Refresh always fetches fresh data."
              style={{ fontSize: '12px', color: colors.text.secondary, whiteSpace: 'nowrap' }}
            >
              {lastLoadCached ? 'cached (0 reads)' : `~${lastLoadReads.toLocaleString()} reads`}
            </span>
          )}
          {/* Explicit Refresh always bypasses the cache (force=true). */}
          <RefreshButton onClick={() => { loadCompanies(true); }} disabled={loading}>
            <FiRefreshCw className={loading ? 'spinning' : ''} />
            Refresh
          </RefreshButton>
          <LogoutButton onClick={handleSuperAdminLogout}>
            <FiLogOut />
            Logout
          </LogoutButton>
        </HeaderRight>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatIcon $color={colors.primary.main}><FiActivity /></StatIcon>
          <StatContent>
            <StatValue>{stats.totalCompanies}</StatValue>
            <StatLabel>Total Companies</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <StatIcon $color={colors.success.main}><FiCheckCircle /></StatIcon>
          <StatContent>
            <StatValue>{stats.activeCompanies}</StatValue>
            <StatLabel>Active Companies</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <StatIcon $color={colors.error.main}><FiXCircle /></StatIcon>
          <StatContent>
            <StatValue>{stats.suspendedCompanies}</StatValue>
            <StatLabel>Suspended</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <StatIcon $color={colors.info.main}><FiUsers /></StatIcon>
          <StatContent>
            <StatValue>{stats.totalUsers}</StatValue>
            <StatLabel>Total Users</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <StatIcon $color={colors.warning.main}><FiClock /></StatIcon>
          <StatContent>
            <StatValue>{stats.trialCount || 0}</StatValue>
            <StatLabel>Trial Users</StatLabel>
          </StatContent>
        </StatCard>
        <StatCard>
          <StatIcon $color="#10b981"><FiCheckCircle /></StatIcon>
          <StatContent>
            <StatValue>{stats.paidCount || 0}</StatValue>
            <StatLabel>Paid Users</StatLabel>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <TabBar>
        <Tab $active={activeTab === 'companies'} onClick={() => setActiveTab('companies')}>
          <FiActivity />
          Companies
        </Tab>
        {isOwner && (
          <Tab $active={activeTab === 'modules'} onClick={() => setActiveTab('modules')}>
            <FiGrid />
            Insights
          </Tab>
        )}
        {isOwner && (
          <Tab $active={activeTab === 'usage'} onClick={() => setActiveTab('usage')}>
            <FiBarChart2 />
            Company Usage
          </Tab>
        )}
        {isOwner && (
          <Tab $active={activeTab === 'reads'} onClick={() => setActiveTab('reads')}>
            <FiDatabase />
            Reads
          </Tab>
        )}
        {isOwner && (
          <Tab $active={activeTab === 'revenue'} onClick={() => setActiveTab('revenue')}>
            <FiTrendingUp />
            Revenue
          </Tab>
        )}
        {canWhatsappInbox && (
          <Tab $active={activeTab === 'whatsapp'} onClick={() => setActiveTab('whatsapp')}>
            <FiSend />
            WhatsApp
          </Tab>
        )}
        {/* Feature Adoption & Feature Requests tabs hidden for now (not needed).
            Re-enable by restoring these two Tab blocks. */}
      </TabBar>

      {activeTab === 'feature-adoption' && isOwner && <FeatureAdoptionTab />}
      {activeTab === 'feature-requests' && isOwner && <FeatureRequestsTab />}
      {activeTab === 'whatsapp' && canWhatsappInbox && (
        <div style={{ padding: '0 4px 24px' }}>
          <WhatsappInbox />
        </div>
      )}

      {activeTab === 'companies' && (
        <CompaniesSection>
          <SectionHeader>
            <SectionTitle>All Companies <ResultCount>({filteredCompanies.length})</ResultCount></SectionTitle>
            <HeaderControls>
              <FilterToggleButton
                $active={showFilters}
                $hasFilters={activeCompanyFilterCount > 0}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiFilter size={14} />
                Filters
                {activeCompanyFilterCount > 0 && <FilterBadge>{activeCompanyFilterCount}</FilterBadge>}
              </FilterToggleButton>
              {isOwner && (
                <FilterToggleButton onClick={() => setSalesPersonsModal(true)} title="Manage sales persons">
                  <FiUsers size={14} />
                  Sales Persons
                </FilterToggleButton>
              )}
              <FilterToggleButton
                onClick={handleExportCompaniesExcel}
                title="Export the current filtered client list to Excel (details, users, active modules)"
              >
                <FiDownload size={14} />
                Export Excel
              </FilterToggleButton>
              <SearchBox>
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search by company, phone, or user name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchBox>
            </HeaderControls>
          </SectionHeader>

          {showFilters && activeTab === 'companies' && (
            <FilterBar>
              <FilterGroup>
                <FilterLabel>Status</FilterLabel>
                <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Subscription</FilterLabel>
                <FilterSelect value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)}>
                  <option value="all">All Plans</option>
                  <option value="trial">Trial</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half-yearly">Half-Yearly</option>
                  <option value="yearly">Yearly</option>
                  <option value="expired">Expired</option>
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>City</FilterLabel>
                <FilterSelect value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}>
                  <option value="all">All Cities</option>
                  {companyCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Heard About Us</FilterLabel>
                <FilterSelect value={heardFromFilter} onChange={(e) => setHeardFromFilter(e.target.value)}>
                  <option value="all">All Sources</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Facebook / Instagram">Facebook / Instagram</option>
                  <option value="Google">Google</option>
                  <option value="YouTube">YouTube</option>
                  <option value="Reference">Reference</option>
                  <option value="Other">Other</option>
                  <option value="Not specified">Not specified</option>
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Sales Person</FilterLabel>
                <FilterSelect value={salesPersonFilter} onChange={(e) => setSalesPersonFilter(e.target.value)}>
                  <option value="all">All Sales Persons</option>
                  <option value="none">— Unassigned —</option>
                  {salesPersons.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </FilterSelect>
              </FilterGroup>
              {activeCompanyFilterCount > 0 && (
                <ClearFiltersButton onClick={clearCompanyFilters}>
                  <FiX size={12} />
                  Clear Filters
                </ClearFiltersButton>
              )}
            </FilterBar>
          )}

          <TableWrapper>
          <CompaniesTable>
            <TableHead>
              <tr>
                <Th></Th>
                <Th>Company</Th>
                <SortableTh $active={sortColumn === 'status'} onClick={() => handleSort('status')}>
                  Status {sortColumn === 'status' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <SortableTh $active={sortColumn === 'subscription'} onClick={() => handleSort('subscription')}>
                  Subscription {sortColumn === 'subscription' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <SortableTh $active={sortColumn === 'users'} onClick={() => handleSort('users')}>
                  Users {sortColumn === 'users' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <SortableTh $active={sortColumn === 'created'} onClick={() => handleSort('created')}>
                  Created {sortColumn === 'created' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <SortableTh $active={sortColumn === 'lastLogin'} onClick={() => handleSort('lastLogin')}>
                  Last Login {sortColumn === 'lastLogin' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <SortableTh $active={sortColumn === 'totalDocs'} onClick={() => handleSort('totalDocs')} title="Number of transactions (sales, purchases, receipts, payments, vouchers…)">
                  Docs {sortColumn === 'totalDocs' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <SortableTh $active={sortColumn === 'aiUsage'} onClick={() => handleSort('aiUsage')} title="AI calls used (lifetime) \u2014 Gemini free tier. Hover a value for tokens & est. cost.">
                  AI Usage {sortColumn === 'aiUsage' && (sortDirection === 'asc' ? '\u25B2' : '\u25BC')}
                </SortableTh>
                <Th>Actions</Th>
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <tr>
                  <Td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading companies...
                  </Td>
                </tr>
              ) : filteredCompanies.length === 0 ? (
                <tr>
                  <Td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                    No companies found
                  </Td>
                </tr>
              ) : (
                pagedCompanies.map(company => (
                  <React.Fragment key={company.id}>
                    <Tr
                      $selected={selectedCompany?.id === company.id}
                      onClick={() => viewCompanyDetails(company)}
                    >
                      <Td style={{ width: '32px', paddingRight: 0 }}>
                        {selectedCompany?.id === company.id ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      </Td>
                      <Td>
                        <CompanyInfo>
                          <CompanyAvatar>{company.companyName?.charAt(0) || '?'}</CompanyAvatar>
                          <CompanyDetails>
                            <CompanyName>
                              {company.companyName}
                              {company.replicateTo && <ShadowBadge $type="real">Has Shadow</ShadowBadge>}
                              {company.replicateFrom && <ShadowBadge $type="shadow">Shadow</ShadowBadge>}
                            </CompanyName>
                            <CompanyEmail>{company.email || company.companyEmail}</CompanyEmail>
                          </CompanyDetails>
                        </CompanyInfo>
                      </Td>
                      <Td>
                        <StatusBadge $active={company.status !== 'suspended'}>
                          {company.status === 'suspended' ? 'Suspended' : 'Active'}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <SubBadge $color={getSubscriptionStatus(company).color}>
                          {getSubscriptionStatus(company).label}
                        </SubBadge>
                        {company.subscription?.salesPersonName && (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                            👤 {company.subscription.salesPersonName}
                          </div>
                        )}
                      </Td>
                      <Td>{company.userCount || 0}</Td>
                      <Td>
                        <DateDisplay>
                          <FiCalendar size={12} />
                          {formatDate(company.createdAt)}
                        </DateDisplay>
                      </Td>
                      <Td>
                        <DateDisplay>
                          <FiLock size={12} />
                          {company.lastLogin ? formatDate(company.lastLogin) : 'Never'}
                        </DateDisplay>
                      </Td>
                      <Td style={{ textAlign: 'right', fontFamily: 'Consolas, monospace', fontSize: '12px' }}>
                        {(company.totalDocs || 0).toLocaleString()}
                      </Td>
                      <Td style={{ textAlign: 'right', fontFamily: 'Consolas, monospace', fontSize: '12px' }}>
                        {(() => {
                          const ai = company.aiUsage;
                          const calls = ai?.callCount || 0;
                          if (!ai || calls === 0) return <span style={{ color: colors.text.disabled }}>—</span>;
                          const tokens = (ai.inputTokens || 0) + (ai.outputTokens || 0);
                          const cost = ai.costUSD || 0;
                          const last = ai.lastUsedAt ? new Date(ai.lastUsedAt).toLocaleDateString() : '—';
                          const color = calls >= 1000 ? colors.error.main : calls >= 200 ? colors.warning.dark : colors.text.primary;
                          return (
                            <span title={`${tokens.toLocaleString()} tokens · est. ${formatUSD(cost)} · last used ${last}`} style={{ color, fontWeight: calls >= 200 ? 700 : 400 }}>
                              {calls.toLocaleString()} calls
                            </span>
                          );
                        })()}
                      </Td>
                      <Td>
                        {canToggleStatus && (
                        <ActionButtons onClick={(e) => e.stopPropagation()}>
                          {isOwner && !company.replicateFrom && !company.replicateTo && (
                            <ActionButton
                              onClick={() => setShadowModal({ companyId: company.id, companyName: company.companyName })}
                              title="Create Shadow (Tax) Company"
                              style={{ color: colors.info.main }}
                            >
                              <FiGrid />
                            </ActionButton>
                          )}
                          <ActionButton
                            $danger={company.status !== 'suspended'}
                            onClick={() => toggleCompanyStatus(company.id, company.status)}
                            title={company.status === 'suspended' ? 'Activate' : 'Suspend'}
                          >
                            <FiPower />
                          </ActionButton>
                        </ActionButtons>
                        )}
                      </Td>
                    </Tr>
                    {selectedCompany?.id === company.id && (
                      <tr>
                        <Td colSpan={10} style={{ padding: 0 }}>
                          <ExpandedDetails>
                            <DetailTabBar onClick={(e) => e.stopPropagation()}>
                              <DetailTab $active={companyDetailTab === 'overview'} onClick={() => setCompanyDetailTab('overview')}>
                                Overview
                              </DetailTab>
                              <DetailTab $active={companyDetailTab === 'subscription'} onClick={() => setCompanyDetailTab('subscription')}>
                                Subscription
                              </DetailTab>
                              <DetailTab $active={companyDetailTab === 'users'} onClick={() => setCompanyDetailTab('users')}>
                                Users ({companyUsers.length})
                              </DetailTab>
                            </DetailTabBar>

                            {companyDetailTab === 'overview' && <>
                            {/* Identity table */}
                            <StackedSection>
                              <StackedTitle>Identity</StackedTitle>
                              <SubTableWrap>
                                <SubTable>
                                  <thead>
                                    <tr>
                                      <SubTh>Company Name</SubTh>
                                      <SubTh>Trade Name</SubTh>
                                      <SubTh>Email</SubTh>
                                      <SubTh>Phone</SubTh>
                                      <SubTh>WhatsApp</SubTh>
                                      <SubTh>City</SubTh>
                                      <SubTh>Country</SubTh>
                                      <SubTh>Business Type</SubTh>
                                      <SubTh>Heard About Us</SubTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <SubTd data-label="Company Name"><strong>{company.companyName || '-'}</strong></SubTd>
                                      <SubTd data-label="Trade Name">{company.tradeName || '-'}</SubTd>
                                      <SubTd data-label="Email">{company.email || company.companyEmail || '-'}</SubTd>
                                      <SubTd data-label="Phone">{company.phone || company.companyPhone || '-'}</SubTd>
                                      <SubTd data-label="WhatsApp">{company.whatsappNumber || '-'}</SubTd>
                                      <SubTd data-label="City">{company.city || '-'}</SubTd>
                                      <SubTd data-label="Country">{company.country || '-'}</SubTd>
                                      <SubTd data-label="Business Type">
                                        {company.businessType ? <SignupBadge>{company.businessType}</SignupBadge> : '-'}
                                      </SubTd>
                                      <SubTd data-label="Heard About Us">
                                        {company.heardFrom ? <SignupBadge>{company.heardFrom}</SignupBadge> : '-'}
                                      </SubTd>
                                    </tr>
                                  </tbody>
                                </SubTable>
                              </SubTableWrap>
                            </StackedSection>

                            {/* Tax + Address */}
                            <StackedSection>
                              <StackedTitle>Tax &amp; Address</StackedTitle>
                              <SubTableWrap>
                                <SubTable>
                                  <thead>
                                    <tr>
                                      <SubTh>NTN</SubTh>
                                      <SubTh>GST / VAT No</SubTh>
                                      <SubTh>Address</SubTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <SubTd data-label="NTN">{company.ntn || '-'}</SubTd>
                                      <SubTd data-label="GST / VAT No">{company.gstNo || '-'}</SubTd>
                                      <SubTd data-label="Address">{company.address || '-'}</SubTd>
                                    </tr>
                                  </tbody>
                                </SubTable>
                              </SubTableWrap>
                            </StackedSection>
                            </>}

                            {companyDetailTab === 'subscription' && (
                            /* Subscription */
                            <StackedSection>
                              <StackedHeaderRow>
                                <StackedTitle>Subscription</StackedTitle>
                                {isOwner && (
                                  <StackedHeaderAction onClick={(e) => { e.stopPropagation(); openSubModal(company); }}>
                                    Update Subscription
                                  </StackedHeaderAction>
                                )}
                              </StackedHeaderRow>
                              <SubTableWrap>
                                <SubTable>
                                  <thead>
                                    <tr>
                                      <SubTh>Plan</SubTh>
                                      <SubTh>Amount</SubTh>
                                      <SubTh>Last Payment</SubTh>
                                      <SubTh>Next Due</SubTh>
                                      <SubTh>Trial Ends</SubTh>
                                      <SubTh>Sales Person</SubTh>
                                      <SubTh>Notes</SubTh>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const sub = company.subscription || {};
                                      const status = getSubscriptionStatus(company);
                                      const fmt = (d) => d ? new Date(d).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
                                      const trialEnd = sub.trialEndDate || (company.createdAt ? new Date(new Date(company.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString() : null);
                                      return (
                                        <tr>
                                          <SubTd data-label="Plan"><SubBadge $color={status.color}>{status.label}</SubBadge></SubTd>
                                          <SubTd data-label="Amount">{sub.amount ? `PKR ${Number(sub.amount).toLocaleString('en-PK')}` : '-'}</SubTd>
                                          <SubTd data-label="Last Payment">{fmt(sub.lastPaymentDate)}</SubTd>
                                          <SubTd data-label="Next Due">{fmt(sub.nextPaymentDate)}</SubTd>
                                          <SubTd data-label="Trial Ends">{sub.plan === 'trial' ? fmt(trialEnd) : '-'}</SubTd>
                                          <SubTd data-label="Sales Person">{sub.salesPersonName || '-'}</SubTd>
                                          <SubTd data-label="Notes">{sub.notes || '-'}</SubTd>
                                        </tr>
                                      );
                                    })()}
                                  </tbody>
                                </SubTable>
                              </SubTableWrap>
                            </StackedSection>
                            )}

                            {companyDetailTab === 'users' && (
                            /* User Activity */
                            <StackedSection>
                              <StackedTitle>User Activity ({companyUsers.length})</StackedTitle>
                              {companyUsers.length === 0 ? (
                                <EmptyUsers>No users found</EmptyUsers>
                              ) : (
                                <SubTableWrap>
                                  <SubTable>
                                    <thead>
                                      <tr>
                                        <SubTh>User</SubTh>
                                        <SubTh>Email</SubTh>
                                        <SubTh>Role</SubTh>
                                        <SubTh>Last Activity</SubTh>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {companyUsers.map(user => {
                                        return (
                                          <tr key={user.id}>
                                            <SubTd data-label="User">
                                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                <UserAvatar style={{ width: 24, height: 24, fontSize: 10 }}>{user.displayName?.charAt(0) || '?'}</UserAvatar>
                                                <strong>{user.displayName || '-'}</strong>
                                              </span>
                                            </SubTd>
                                            <SubTd data-label="Email" style={{ color: colors.text.secondary }}>{user.email || '-'}</SubTd>
                                            <SubTd data-label="Role">
                                              {user.role === 'admin' ? <AdminBadge>Admin</AdminBadge> : (user.role || '-')}
                                            </SubTd>
                                            <SubTd data-label="Last Activity" style={{ color: colors.text.secondary, fontSize: '12px' }}>
                                              {user.lastLogin ? `Login: ${formatDate(user.lastLogin)}` : 'Never'}
                                            </SubTd>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </SubTable>
                                </SubTableWrap>
                              )}
                            </StackedSection>
                            )}
                            <ExpandedFooter>
                              <FooterMetaRow>
                                <FooterMetaItem>
                                  <DetailLabel>Company ID</DetailLabel>
                                  <DetailValue style={{ fontSize: '11px', fontFamily: 'monospace' }}>{company.id}</DetailValue>
                                </FooterMetaItem>
                                <FooterMetaItem>
                                  <DetailLabel>Owner ID</DetailLabel>
                                  <DetailValue style={{ fontSize: '11px', fontFamily: 'monospace' }}>{company.ownerId || '-'}</DetailValue>
                                </FooterMetaItem>
                                <FooterMetaItem>
                                  <DetailLabel>Created</DetailLabel>
                                  <DetailValue>{formatDate(company.createdAt)}</DetailValue>
                                </FooterMetaItem>
                              </FooterMetaRow>
                              {isOwner && (
                              <>
                              <FooterActionsRow>
                                <FooterActionsLeft onClick={(e) => e.stopPropagation()}>
                                  <ManageModulesButton onClick={() => openModulesModal(company)}>
                                    <FiGrid size={12} />
                                    Manage Modules
                                  </ManageModulesButton>
                                  <ManageModulesButton onClick={() => openCurrencyModal(company)} style={{ background: '#10b981' }}>
                                    <FiDollarSign size={12} />
                                    Currency & Tax
                                  </ManageModulesButton>
                                  <SendEmailButton onClick={() => openEmailModal('single', company)}>
                                    <FiMail size={12} />
                                    Send Email
                                  </SendEmailButton>
                                  <SendEmailButton onClick={() => openWhatsappModal('single', company)} style={{ background: '#25D366' }}>
                                    <FiSend size={12} />
                                    WhatsApp
                                  </SendEmailButton>
                                  <ManageModulesButton onClick={() => openSalesAccessModal(company)} style={{ background: '#6366f1' }}>
                                    <FiUsers size={12} />
                                    Sales Access
                                  </ManageModulesButton>
                                  <ManageModulesButton onClick={() => openFbrModal(company)} style={{ background: '#6d1a2e' }}>
                                    <FiShield size={12} />
                                    FBR POS
                                  </ManageModulesButton>
                                  <ManageModulesButton onClick={() => openDiModal(company)} style={{ background: '#3a1a6d' }}>
                                    <FiShield size={12} />
                                    FBR Digital Inv.
                                  </ManageModulesButton>
                                  <ManageModulesButton onClick={() => runDataRepair(company)} style={{ background: '#0f766e' }} disabled={repairingCompanyId === company.id}>
                                    <FiShield size={12} />
                                    {repairingCompanyId === company.id ? 'Checking…' : 'Data Repair'}
                                  </ManageModulesButton>
                                </FooterActionsLeft>
                                <FooterActionsRight onClick={(e) => e.stopPropagation()}>
                                  <ClearButton
                                    onClick={() => openClearModal('transactions', company.id, company.companyName)}
                                    title="Clear all transaction data"
                                  >
                                    <FiTrash2 size={12} />
                                    Clear Transactions
                                  </ClearButton>
                                  <ClearButton
                                    onClick={() => openClearModal('master', company.id, company.companyName)}
                                    title="Clear all master data"
                                  >
                                    <FiTrash2 size={12} />
                                    Clear Master Data
                                  </ClearButton>
                                  <ClearButton
                                    $severe
                                    onClick={() => openClearModal('all', company.id, company.companyName)}
                                    title="Clear all company data"
                                  >
                                    <FiTrash2 size={12} />
                                    Clear All Data
                                  </ClearButton>
                                </FooterActionsRight>
                              </FooterActionsRow>
                              <FooterDangerRow onClick={(e) => e.stopPropagation()}>
                                <ToggleButton
                                  $danger={company.status !== 'suspended'}
                                  onClick={() => toggleCompanyStatus(company.id, company.status)}
                                >
                                  <FiPower size={12} />
                                  {company.status === 'suspended' ? 'Activate Company' : 'Suspend Company'}
                                </ToggleButton>
                                <DeleteCompanyButton
                                  onClick={() => openDeleteModal(company.id, company.companyName)}
                                >
                                  <FiTrash2 size={12} />
                                  Delete Company
                                </DeleteCompanyButton>
                              </FooterDangerRow>
                              </>
                              )}
                              {!isOwner && canToggleStatus && (
                                <FooterDangerRow onClick={(e) => e.stopPropagation()}>
                                  <ToggleButton
                                    $danger={company.status !== 'suspended'}
                                    onClick={() => toggleCompanyStatus(company.id, company.status)}
                                  >
                                    <FiPower size={12} />
                                    {company.status === 'suspended' ? 'Activate Company' : 'Suspend Company'}
                                  </ToggleButton>
                                </FooterDangerRow>
                              )}
                            </ExpandedFooter>
                          </ExpandedDetails>
                        </Td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </CompaniesTable>
          </TableWrapper>
          {!loading && filteredCompanies.length > COMPANIES_PER_PAGE && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#666' }}>
                Showing {(safeCompanyPage - 1) * COMPANIES_PER_PAGE + 1}–{Math.min(safeCompanyPage * COMPANIES_PER_PAGE, filteredCompanies.length)} of {filteredCompanies.length}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setCompanyPage(p => Math.max(1, p - 1))}
                  disabled={safeCompanyPage <= 1}
                  style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: safeCompanyPage <= 1 ? 'not-allowed' : 'pointer', opacity: safeCompanyPage <= 1 ? 0.5 : 1 }}
                >Prev</button>
                <span style={{ fontSize: 13, minWidth: 110, textAlign: 'center' }}>Page {safeCompanyPage} of {companyTotalPages}</span>
                <button
                  onClick={() => setCompanyPage(p => Math.min(companyTotalPages, p + 1))}
                  disabled={safeCompanyPage >= companyTotalPages}
                  style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: safeCompanyPage >= companyTotalPages ? 'not-allowed' : 'pointer', opacity: safeCompanyPage >= companyTotalPages ? 0.5 : 1 }}
                >Next</button>
              </div>
            </div>
          )}
        </CompaniesSection>
      )}

      {activeTab === 'modules' && isOwner && <InsightsTab companies={companies} />}

      {activeTab === 'usage' && (
        <CompaniesSection>
          <SectionHeader>
            <SectionTitle>Company Usage <ResultCount>({filteredUsageData.length})</ResultCount></SectionTitle>
            <HeaderControls>
              <UsagePeriodBar>
                <PeriodButton $active={usagePeriod === 'today'} onClick={() => setUsagePeriod('today')}>Today</PeriodButton>
                <PeriodButton $active={usagePeriod === 'week'} onClick={() => setUsagePeriod('week')}>7 Days</PeriodButton>
                <PeriodButton $active={usagePeriod === 'month'} onClick={() => setUsagePeriod('month')}>30 Days</PeriodButton>
              </UsagePeriodBar>
              {/* COST: emptying usageData already re-fires the loader through the
                  effect above (tab is active, companies loaded, length === 0), so the
                  old direct loadUsageData() call here ran all 426 queries TWICE per
                  click. Reset only — except when the list is already empty (a failed
                  load), where the effect can't re-fire and we must call it directly. */}
              <ActionButton onClick={() => { if (usageData.length === 0) { loadUsageData(); } else { setUsageData([]); } }} title="Refresh usage data">
                <FiRefreshCw size={14} />
              </ActionButton>
              <SearchBox>
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={usageSearchTerm}
                  onChange={(e) => setUsageSearchTerm(e.target.value)}
                />
              </SearchBox>
            </HeaderControls>
          </SectionHeader>

          {/* Active Users (DAU / MAU / stickiness) */}
          <UsageSummaryGrid>
            <UsageSummaryCard style={{ borderTop: `3px solid ${colors.primary.main}` }}>
              <UsageSummaryValue>{activeUserStats.dau.toLocaleString()}</UsageSummaryValue>
              <UsageSummaryLabel>Daily Active Users (DAU)</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard style={{ borderTop: `3px solid ${colors.primary.main}` }}>
              <UsageSummaryValue>{activeUserStats.mau.toLocaleString()}</UsageSummaryValue>
              <UsageSummaryLabel>Monthly Active Users (MAU)</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard style={{ borderTop: `3px solid ${colors.success.main}` }}>
              <UsageSummaryValue>{activeUserStats.ratio}%</UsageSummaryValue>
              <UsageSummaryLabel>DAU / MAU Stickiness</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue>{activeUserStats.wau.toLocaleString()}</UsageSummaryValue>
              <UsageSummaryLabel>Weekly Active Users (WAU)</UsageSummaryLabel>
            </UsageSummaryCard>
          </UsageSummaryGrid>

          {/* Summary Cards */}
          <UsageSummaryGrid>
            <UsageSummaryCard>
              <UsageSummaryValue>{usageSummary.totalViewsToday.toLocaleString()}</UsageSummaryValue>
              <UsageSummaryLabel>Page Views Today</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue>{usageSummary.activeToday}</UsageSummaryValue>
              <UsageSummaryLabel>Active Today</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue>{usageSummary.activeWeek}</UsageSummaryValue>
              <UsageSummaryLabel>Active This Week</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue style={{ fontSize: '14px' }}>
                {usageSummary.mostActive?.companyName || '-'}
              </UsageSummaryValue>
              <UsageSummaryLabel>
                Most Active ({usagePeriod === 'today' ? 'Today' : usagePeriod === 'week' ? '7 Days' : '30 Days'})
              </UsageSummaryLabel>
            </UsageSummaryCard>
          </UsageSummaryGrid>

          {/* Bar Chart */}
          {usageLoading ? (
            <UsageChartContainer>
              <div style={{ textAlign: 'center', padding: '40px', color: colors.text.secondary }}>Loading usage data...</div>
            </UsageChartContainer>
          ) : usageChartData.length > 0 ? (
            <UsageChartContainer>
              <UsageChartTitle>
                Top Companies by Page Views ({usagePeriod === 'today' ? 'Today' : usagePeriod === 'week' ? 'Last 7 Days' : 'Last 30 Days'})
              </UsageChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={usageChartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" fontSize={11} tick={{ fill: colors.text.secondary }} />
                  <YAxis fontSize={12} tick={{ fill: colors.text.secondary }} />
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: `1px solid ${colors.neutral[200]}`,
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'Page Views']}
                  />
                  <Bar dataKey="views" fill={colors.primary.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </UsageChartContainer>
          ) : !usageLoading && (
            <UsageChartContainer>
              <div style={{ textAlign: 'center', padding: '40px', color: colors.text.secondary }}>No usage data for this period</div>
            </UsageChartContainer>
          )}

          {/* Usage Table */}
          <TableWrapper>
            <CompaniesTable>
              <TableHead>
                <tr>
                  <Th style={{ width: '40px' }}>#</Th>
                  <Th>Company</Th>
                  <SortableTh $active={usageSortColumn === 'today'} $direction={usageSortDirection} onClick={() => handleUsageSort('today')}>
                    Today
                  </SortableTh>
                  <SortableTh $active={usageSortColumn === 'week'} $direction={usageSortDirection} onClick={() => handleUsageSort('week')}>
                    7 Days
                  </SortableTh>
                  <SortableTh $active={usageSortColumn === 'month'} $direction={usageSortDirection} onClick={() => handleUsageSort('month')}>
                    30 Days
                  </SortableTh>
                  <SortableTh $active={usageSortColumn === 'totalDocs'} $direction={usageSortDirection} onClick={() => handleUsageSort('totalDocs')}>
                    Docs
                  </SortableTh>
                  <Th>Last Activity</Th>
                </tr>
              </TableHead>
              <TableBody>
                {usageLoading ? (
                  <tr>
                    <Td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>Loading usage data...</Td>
                  </tr>
                ) : filteredUsageData.length === 0 ? (
                  <tr>
                    <Td colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>No usage data found</Td>
                  </tr>
                ) : (
                  filteredUsageData.map((item, idx) => (
                    <Tr key={item.id}>
                      <Td style={{ color: colors.text.secondary, fontSize: '12px' }}>{idx + 1}</Td>
                      <Td>
                        <CompanyInfo>
                          <CompanyAvatar>{item.companyName?.charAt(0) || '?'}</CompanyAvatar>
                          <CompanyName>{item.companyName}</CompanyName>
                        </CompanyInfo>
                      </Td>
                      <Td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: item.today > 0 ? 600 : 400, color: item.today > 0 ? colors.text.primary : colors.text.disabled }}>
                        {item.today.toLocaleString()}
                      </Td>
                      <Td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: item.week > 0 ? 600 : 400, color: item.week > 0 ? colors.text.primary : colors.text.disabled }}>
                        {item.week.toLocaleString()}
                      </Td>
                      <Td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: item.month > 0 ? 600 : 400, color: item.month > 0 ? colors.text.primary : colors.text.disabled }}>
                        {item.month.toLocaleString()}
                      </Td>
                      <Td style={{ fontFamily: 'monospace', textAlign: 'right', color: colors.text.secondary }}>
                        {item.totalDocs.toLocaleString()}
                      </Td>
                      <Td>
                        <DateDisplay>
                          <FiClock size={12} />
                          {item.lastActivity ? formatDate(item.lastActivity) : 'No activity'}
                        </DateDisplay>
                      </Td>
                    </Tr>
                  ))
                )}
              </TableBody>
            </CompaniesTable>
          </TableWrapper>
        </CompaniesSection>
      )}

      {activeTab === 'reads' && isOwner && (
        <CompaniesSection>
          <SectionHeader>
            <SectionTitle>Firestore Reads by Company & Screen <ResultCount>(chart: {READS_WINDOW_DAYS} days · table: 7 days)</ResultCount></SectionTitle>
            <HeaderControls>
              {/* COST: same double-fire as the Usage refresh — emptying readsData
                  re-fires the loader via the effect, so the direct call was a second,
                  redundant sweep of every company. Direct call kept only for the
                  already-empty case (failed load), where the effect won't re-fire. */}
              <ActionButton onClick={() => { if (readsData.length === 0) { loadReadsData(); } else { setReadsData([]); } }} title="Refresh read stats">
                <FiRefreshCw size={14} />
              </ActionButton>
            </HeaderControls>
          </SectionHeader>

          <UsageSummaryGrid>
            <UsageSummaryCard style={{ borderTop: `3px solid ${colors.primary.main}` }}>
              <UsageSummaryValue>{readsData.reduce((s, c) => s + c.today, 0).toLocaleString()}</UsageSummaryValue>
              <UsageSummaryLabel>Tracked Reads Today</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue>{readsData.reduce((s, c) => s + c.week, 0).toLocaleString()}</UsageSummaryValue>
              <UsageSummaryLabel>Tracked Reads (7 Days)</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue style={{ fontSize: '14px' }}>{readsData[0]?.companyName || '-'}</UsageSummaryValue>
              <UsageSummaryLabel>Heaviest Company (7 Days)</UsageSummaryLabel>
            </UsageSummaryCard>
            <UsageSummaryCard>
              <UsageSummaryValue style={{ fontSize: '14px' }}>
                {(() => {
                  const all = {};
                  readsData.forEach(c => c.routes.forEach(([r, n]) => { all[r] = (all[r] || 0) + n; }));
                  const top = Object.entries(all).sort((a, b) => b[1] - a[1])[0];
                  return top ? `/${top[0].replace(/__/g, '/')}` : '-';
                })()}
              </UsageSummaryValue>
              <UsageSummaryLabel>Heaviest Screen (Fleet, 7 Days)</UsageSummaryLabel>
            </UsageSummaryCard>
          </UsageSummaryGrid>

          {/* Day-on-day fleet reads — mirrors the Firebase console graph. */}
          <UsageChartContainer>
            <UsageChartTitle>
              Fleet Reads — Day by Day (Last {READS_WINDOW_DAYS} Days)
            </UsageChartTitle>
            {readsLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: colors.text.secondary }}>Loading…</div>
            ) : readsDaily.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: colors.text.secondary }}>No read data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={readsDaily} margin={{ top: 5, right: 30, left: 20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="label" angle={-45} textAnchor="end" fontSize={11} tick={{ fill: colors.text.secondary }} interval={0} />
                  <YAxis fontSize={12} tick={{ fill: colors.text.secondary }} />
                  <Tooltip
                    contentStyle={{
                      background: 'white',
                      border: `1px solid ${colors.neutral[200]}`,
                      borderRadius: '8px',
                      fontSize: '13px'
                    }}
                    formatter={(value) => [value.toLocaleString(), 'Reads']}
                    labelFormatter={(label) => {
                      const row = readsDaily.find(d => d.label === label);
                      return row ? row.date : label;
                    }}
                  />
                  <Bar dataKey="total" fill={colors.primary.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </UsageChartContainer>

          <div style={{ fontSize: '12px', color: colors.text.secondary, margin: '12px 0' }}>
            Counted inside the app at the data-helper level and attributed to the screen that
            caused the read. Coverage is the bulk of read volume, not a billing invoice —
            the Firebase console remains the exact total. Data starts collecting from the
            deploy of this feature onward.
          </div>

          <TableWrapper>
            <CompaniesTable>
              <TableHead>
                <tr>
                  <Th style={{ width: '40px' }}>#</Th>
                  <SortableTh $active={readsSortColumn === 'company'} onClick={() => handleReadsSort('company')}>
                    Company{readsSortColumn === 'company' ? (readsSortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                  </SortableTh>
                  <SortableTh $active={readsSortColumn === 'today'} onClick={() => handleReadsSort('today')} style={{ textAlign: 'right' }}>
                    Today{readsSortColumn === 'today' ? (readsSortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                  </SortableTh>
                  <SortableTh $active={readsSortColumn === 'week'} onClick={() => handleReadsSort('week')} style={{ textAlign: 'right' }}>
                    7 Days{readsSortColumn === 'week' ? (readsSortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                  </SortableTh>
                  <Th>Heaviest Screen</Th>
                  <Th style={{ width: '60px' }}></Th>
                </tr>
              </TableHead>
              <TableBody>
                {readsLoading ? (
                  <tr><Td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>Loading read stats...</Td></tr>
                ) : sortedReadsData.length === 0 ? (
                  <tr><Td colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>No read data yet — it starts collecting after this feature is deployed and companies use the app.</Td></tr>
                ) : (
                  sortedReadsData.map((item, idx) => (
                    <React.Fragment key={item.id}>
                      <Tr style={{ cursor: 'pointer' }} onClick={() => { const next = expandedReadsCompany === item.id ? null : item.id; setExpandedReadsCompany(next); setExpandedReadsDay(null); }}>
                        <Td style={{ color: colors.text.secondary, fontSize: '12px' }}>{idx + 1}</Td>
                        <Td>
                          <CompanyInfo>
                            <CompanyAvatar>{item.companyName?.charAt(0) || '?'}</CompanyAvatar>
                            <CompanyName>{item.companyName}</CompanyName>
                          </CompanyInfo>
                        </Td>
                        <Td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: item.today > 0 ? 600 : 400 }}>
                          {item.today.toLocaleString()}
                        </Td>
                        <Td style={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>
                          {item.week.toLocaleString()}
                        </Td>
                        <Td style={{ fontSize: '12px' }}>
                          {item.topRoute
                            ? <>/{item.topRoute.replace(/__/g, '/')} <span style={{ color: colors.text.secondary, fontFamily: 'monospace' }}>({item.topRouteReads.toLocaleString()})</span></>
                            : '-'}
                        </Td>
                        <Td>{expandedReadsCompany === item.id ? <FiChevronDown /> : <FiChevronRight />}</Td>
                      </Tr>
                      {expandedReadsCompany === item.id && (() => {
                        const byDay = item.byDay || [];
                        const view = expandedReadsDay || byDay[0]?.date || 'week';
                        const isWeek = view === 'week';
                        const dayRec = !isWeek ? byDay.find(d => d.date === view) : null;
                        const rows = isWeek ? item.routes : (dayRec?.routes || []);
                        const base = isWeek ? item.week : (dayRec?.total || 0);
                        const bg = colors.neutral?.[50] || '#fafafa';
                        return (
                          <React.Fragment>
                            {/* Day picker — choose which day (or the 7-day total) the breakdown reflects */}
                            <Tr style={{ background: bg }}>
                              <Td></Td>
                              <Td colSpan={5} style={{ paddingLeft: '48px' }}>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', padding: '8px 0' }}>
                                  {byDay.map((d, i) => (
                                    <DayChip key={d.date} $active={view === d.date} onClick={(e) => { e.stopPropagation(); setExpandedReadsDay(d.date); }}>
                                      {i === 0 ? 'Today' : d.label} · {d.total.toLocaleString()}
                                    </DayChip>
                                  ))}
                                  <DayChip $active={isWeek} onClick={(e) => { e.stopPropagation(); setExpandedReadsDay('week'); }}>
                                    7 Days · {item.week.toLocaleString()}
                                  </DayChip>
                                </div>
                              </Td>
                            </Tr>
                            {rows.length === 0 ? (
                              <Tr style={{ background: bg }}>
                                <Td></Td>
                                <Td colSpan={5} style={{ paddingLeft: '48px', fontSize: '12px', color: colors.text.secondary }}>No reads recorded for this day.</Td>
                              </Tr>
                            ) : rows.map(([route, n]) => (
                              <Tr key={`${item.id}-${view}-${route}`} style={{ background: bg }}>
                                <Td></Td>
                                <Td colSpan={2} style={{ fontSize: '12px', paddingLeft: '48px' }}>/{route.replace(/__/g, '/')}</Td>
                                <Td style={{ fontFamily: 'monospace', textAlign: 'right', fontSize: '12px' }}>{n.toLocaleString()}</Td>
                                <Td colSpan={2} style={{ fontSize: '11px', color: colors.text.secondary }}>
                                  {base > 0 ? `${Math.round((n / base) * 100)}%` : ''}
                                </Td>
                              </Tr>
                            ))}
                          </React.Fragment>
                        );
                      })()}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </CompaniesTable>
          </TableWrapper>
        </CompaniesSection>
      )}

      {activeTab === 'revenue' && isOwner && (
        <CompaniesSection>
          <SectionHeader>
            <SectionTitle>Revenue Overview</SectionTitle>
            <HeaderControls>
              {/* Explicit Refresh — force=true bypasses the company-list cache. */}
              <ActionButton onClick={() => loadCompanies(true)} title="Refresh">
                <FiRefreshCw size={14} />
              </ActionButton>
            </HeaderControls>
          </SectionHeader>

          {/* Key numbers (plain words) */}
          <RevKpiGrid>
            <RevKpiCard $accent={colors.primary.main}>
              <RevKpiLabel>Average Monthly Income</RevKpiLabel>
              <RevKpiValue>{formatCurrency(Math.round(revenueMetrics.mrr))}</RevKpiValue>
              <RevKpiSub>From active paying clients</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.success.main}>
              <RevKpiLabel>Yearly Income</RevKpiLabel>
              <RevKpiValue>{formatCurrency(Math.round(revenueMetrics.arr))}</RevKpiValue>
              <RevKpiSub>Monthly income x 12</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.info.main}>
              <RevKpiLabel>Total Earned (all-time)</RevKpiLabel>
              <RevKpiValue>{formatCurrency(Math.round(revenueMetrics.lifetimeEarned))}</RevKpiValue>
              <RevKpiSub>All payments ever (net of refunds)</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.success.main}>
              <RevKpiLabel>Active Clients</RevKpiLabel>
              <RevKpiValue>{revenueMetrics.active.length}</RevKpiValue>
              <RevKpiSub>{formatCurrency(Math.round(revenueMetrics.expectedNext12))} expected / yr</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.error.main}>
              <RevKpiLabel>Lost Clients</RevKpiLabel>
              <RevKpiValue>{revenueMetrics.lapsed.length}</RevKpiValue>
              <RevKpiSub>{formatCurrency(Math.round(revenueMetrics.lostAnnualRevenue))} / yr forgone</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.info.main}>
              <RevKpiLabel>Renewal Rate</RevKpiLabel>
              <RevKpiValue>{revenueMetrics.renewalRate.toFixed(0)}%</RevKpiValue>
              <RevKpiSub>{revenueMetrics.renewedAll.length} renewed / {revenueMetrics.lapsed.length} lost</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.warning.main}>
              <RevKpiLabel>This Year - New-Client Money</RevKpiLabel>
              <RevKpiValue>{formatCurrency(Math.round(revenueMetrics.thisYearSetup))}</RevKpiValue>
              <RevKpiSub>First payment from brand-new clients ({new Date().getFullYear()})</RevKpiSub>
            </RevKpiCard>
            <RevKpiCard $accent={colors.success.main}>
              <RevKpiLabel>This Year - Renewal Money</RevKpiLabel>
              <RevKpiValue>{formatCurrency(Math.round(revenueMetrics.thisYearRenewal))}</RevKpiValue>
              <RevKpiSub>Existing clients paying again ({new Date().getFullYear()})</RevKpiSub>
            </RevKpiCard>
          </RevKpiGrid>

          {/* Next 6 months - expected payments (active clients due each month) */}
          <RevPanel>
            <RevPanelTitle>Next 6 Months - Expected Payments</RevPanelTitle>
            <RevPanelSub>Active clients whose next payment falls in each month, and the total expected.</RevPanelSub>
            <RevRenewalGrid>
              {revenueMetrics.next6Months.map(b => (
                <RevRenewalCard key={b.key}>
                  <RevRenewalMonth>{b.label}</RevRenewalMonth>
                  <RevRenewalCount>{b.companies.length} client{b.companies.length === 1 ? '' : 's'}</RevRenewalCount>
                  <RevRenewalAmount>{formatCurrency(Math.round(b.amount))}</RevRenewalAmount>
                </RevRenewalCard>
              ))}
            </RevRenewalGrid>
          </RevPanel>

          {/* All paying clients - one sortable table */}
          <RevPanel>
            <RevPanelTitle>All Paying Clients</RevPanelTitle>
            <RevPanelSub>One row per paying client. Click any column heading to sort. Default: soonest due first.</RevPanelSub>
            <RevTableControls>
              <RevSearchInput
                placeholder="Search company, sales person, or plan..."
                value={revSearch}
                onChange={e => setRevSearch(e.target.value)}
              />
              <RevFilterSelect value={revStatusFilter} onChange={e => setRevStatusFilter(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="overdue">Overdue</option>
                <option value="suspended">Suspended</option>
              </RevFilterSelect>
              <RevTableCount>{revTableRows.length} client{revTableRows.length === 1 ? '' : 's'}</RevTableCount>
            </RevTableControls>
            {revTableRows.length === 0 ? (
              <RevEmpty>No clients match this search / filter.</RevEmpty>
            ) : (
              <TableWrapper>
                <CompaniesTable>
                  <TableHead>
                    <tr>
                      <SortTh onClick={() => toggleRevSort('company')}>Company{sortArrow('company')}</SortTh>
                      <SortTh onClick={() => toggleRevSort('salesPerson')}>Sales Person{sortArrow('salesPerson')}</SortTh>
                      <SortTh onClick={() => toggleRevSort('plan')}>Plan{sortArrow('plan')}</SortTh>
                      <SortTh style={{ textAlign: 'right' }} onClick={() => toggleRevSort('perBill')}>Amount / bill{sortArrow('perBill')}</SortTh>
                      <SortTh style={{ textAlign: 'right' }} onClick={() => toggleRevSort('perMonth')}>Per Month{sortArrow('perMonth')}</SortTh>
                      <SortTh onClick={() => toggleRevSort('nextDueTs')}>Next Due{sortArrow('nextDueTs')}</SortTh>
                      <SortTh style={{ textAlign: 'right' }} onClick={() => toggleRevSort('daysLeft')}>Days Left{sortArrow('daysLeft')}</SortTh>
                      <SortTh onClick={() => toggleRevSort('status')}>Status{sortArrow('status')}</SortTh>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {revTableRows.map(r => {
                      const statusColor = r.status === 'Active' ? colors.success.main : r.status === 'Overdue' ? colors.warning.main : colors.error.main;
                      const daysColor = r.daysLeft != null && r.daysLeft < 0 ? colors.error.main : (r.daysLeft != null && r.daysLeft <= 30 ? colors.warning.main : colors.text.secondary);
                      return (
                        <Tr key={r.id}>
                          <Td><strong>{r.company}</strong></Td>
                          <Td>{r.salesPerson}</Td>
                          <Td>{r.planLabel}</Td>
                          <Td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(Math.round(r.perBill))}</Td>
                          <Td style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.text.secondary }}>{formatCurrency(Math.round(r.perMonth))}</Td>
                          <Td>{r.nextDueLabel}</Td>
                          <Td style={{ textAlign: 'right', color: daysColor, fontWeight: (r.daysLeft != null && r.daysLeft <= 30) ? 700 : 400 }}>{r.daysLeft != null ? r.daysLeft : '-'}</Td>
                          <Td>
                            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, background: statusColor + '20', color: statusColor, fontWeight: 600, fontSize: 12 }}>{r.status}</span>
                          </Td>
                        </Tr>
                      );
                    })}
                  </TableBody>
                </CompaniesTable>
              </TableWrapper>
            )}
          </RevPanel>
        </CompaniesSection>
      )}

      {/* Shadow Company Creation Modal */}
      {shadowModal && (
        <ModalOverlay onClick={() => { if (!shadowCreating) { setShadowModal(null); setShadowProgress(''); setShadowCreating(false); } }}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <ModalHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FiGrid size={20} style={{ color: colors.info.main }} />
                <ModalTitle>Create Shadow Company</ModalTitle>
              </div>
              <ModalCloseButton onClick={() => { if (!shadowCreating) { setShadowModal(null); setShadowProgress(''); setShadowCreating(false); } }} disabled={shadowCreating}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {!shadowCreating && !shadowProgress && (
                <>
                  <p style={{ fontSize: '14px', color: colors.text.secondary, marginBottom: '16px' }}>
                    This will create a shadow (tax) company for <strong>{shadowModal.companyName}</strong>.
                  </p>
                  <div style={{ background: colors.neutral[50], padding: '14px', borderRadius: '8px', fontSize: '13px', color: colors.text.secondary, lineHeight: 1.6 }}>
                    <strong>What happens:</strong>
                    <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                      <li>A new company "{shadowModal.companyName} (Tax)" will be created</li>
                      <li>All master data, accounts, and transactions will be copied</li>
                      <li>Same users will have access to both companies</li>
                      <li>New transactions in real company will auto-replicate to shadow</li>
                      <li>Edits in shadow will NOT affect the real company</li>
                    </ul>
                  </div>
                </>
              )}
              {(shadowCreating || shadowProgress) && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  {shadowCreating && <FiRefreshCw className="spinning" size={24} style={{ color: colors.primary.main, marginBottom: '12px' }} />}
                  <p style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: shadowProgress.startsWith('Error') ? colors.error.main :
                           shadowProgress.includes('successfully') ? colors.success.main : colors.text.primary
                  }}>
                    {shadowProgress}
                  </p>
                </div>
              )}
            </ModalBody>
            {!shadowCreating && !shadowProgress.includes('successfully') && !shadowProgress.startsWith('Error') && (
              <ModalFooter>
                <ModalCancelButton onClick={() => { setShadowModal(null); setShadowProgress(''); }}>
                  Cancel
                </ModalCancelButton>
                <ModalConfirmButton
                  onClick={() => { setShadowProgress(''); createShadowCompany(); }}
                  style={{ background: colors.info.main }}
                >
                  <FiGrid size={14} />
                  Create Shadow
                </ModalConfirmButton>
              </ModalFooter>
            )}
            {!shadowCreating && (shadowProgress.includes('successfully') || shadowProgress.startsWith('Error')) && (
              <ModalFooter>
                {/* A shadow company is a brand-new row the cache has never seen and
                    cannot be patched into (user counts, doc counts and usage are all
                    server-derived), so drop the cache and let this load be a real one. */}
                <ModalConfirmButton
                  onClick={() => { setShadowModal(null); setShadowProgress(''); setShadowCreating(false); if (shadowProgress.includes('successfully')) { invalidateCompaniesCache(); loadCompanies(); } }}
                  style={{ background: shadowProgress.includes('successfully') ? colors.success.main : colors.neutral[600] }}
                >
                  Close
                </ModalConfirmButton>
              </ModalFooter>
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Clear Data Confirmation Modal */}
      {clearModal && (
        <ModalOverlay onClick={closeClearModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalWarningIcon>
                <FiAlertTriangle />
              </ModalWarningIcon>
              <ModalTitle>
                {clearModal.type === 'transactions' && 'Clear Transaction Data'}
                {clearModal.type === 'master' && 'Clear Master Data'}
                {clearModal.type === 'all' && 'Clear All Company Data'}
              </ModalTitle>
              <ModalCloseButton onClick={closeClearModal} disabled={clearing}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModalBody>
              <WarningBox>
                <strong>This action is irreversible!</strong>
                <p>
                  {clearModal.type === 'transactions' && (
                    <>You are about to delete all <b>transaction data</b> including invoices, orders, receipts, payments, journal entries, stock movements, audit logs, and reset document counters.</>
                  )}
                  {clearModal.type === 'master' && (
                    <>You are about to delete all <b>master data</b> including chart of accounts, customers, vendors, inventory items, locations, categories, banks, employees, jobs, areas, and regions.</>
                  )}
                  {clearModal.type === 'all' && (
                    <>You are about to delete <b>ALL data</b> including transactions, master records, and everything else. Only users, roles, settings, and financial years will be preserved.</>
                  )}
                </p>
              </WarningBox>

              <ModalCompanyName>
                Company: <strong>{clearModal.companyName}</strong>
              </ModalCompanyName>

              <ModalInputGroup>
                <ModalLabel>Super Admin Password</ModalLabel>
                <ModalInput
                  type="password"
                  placeholder="Re-enter your password"
                  value={clearPassword}
                  onChange={(e) => setClearPassword(e.target.value)}
                  disabled={clearing}
                />
              </ModalInputGroup>

              <ModalInputGroup>
                <ModalLabel>Type the company name to confirm: <strong>{clearModal.companyName}</strong></ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="Type company name exactly"
                  value={clearConfirmName}
                  onChange={(e) => setClearConfirmName(e.target.value)}
                  disabled={clearing}
                />
              </ModalInputGroup>

              {clearError && <ModalError>{clearError}</ModalError>}
              {clearProgress && <ModalProgress>{clearProgress}</ModalProgress>}
            </ModalBody>

            <ModalFooter>
              <ModalCancelButton onClick={closeClearModal} disabled={clearing}>
                Cancel
              </ModalCancelButton>
              <ModalConfirmButton
                onClick={handleClearData}
                disabled={clearing || !clearPassword || !clearConfirmName}
              >
                <FiTrash2 size={14} />
                {clearing ? 'Clearing...' : 'Confirm & Delete'}
              </ModalConfirmButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
      {/* Delete Company Modal */}
      {deleteModal && (
        <ModalOverlay onClick={closeDeleteModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalWarningIcon style={{ background: '#fff5f5', color: '#e53e3e' }}>
                <FiAlertTriangle />
              </ModalWarningIcon>
              <ModalTitle>Delete Entire Company</ModalTitle>
              <ModalCloseButton onClick={closeDeleteModal} disabled={deleting}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModalBody>
              <WarningBox style={{ background: '#fff5f5', borderColor: '#e53e3e' }}>
                <strong style={{ color: '#e53e3e' }}>PERMANENT DELETION - THIS CANNOT BE UNDONE!</strong>
                <p>
                  This will permanently delete <b>"{deleteModal.companyName}"</b> and ALL its data including:
                </p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '13px' }}>
                  <li>All transactions, invoices, orders, receipts, payments</li>
                  <li>All master data (accounts, customers, vendors, inventory)</li>
                  <li>All users, roles, settings, and financial years</li>
                  <li>All stored files (logo, pictures, attachments, backups) and its FBR / API keys</li>
                  {deleteModal.shadowId && (
                    <li><b>Its tax-books company "{deleteModal.shadowName || deleteModal.shadowId}" and all its data</b></li>
                  )}
                  <li>Firebase Auth accounts of users who only belong to this company</li>
                </ul>
              </WarningBox>

              <ModalCompanyName>
                Company: <strong>{deleteModal.companyName}</strong>
              </ModalCompanyName>

              <ModalInputGroup>
                <ModalLabel>Super Admin Password</ModalLabel>
                <ModalInput
                  type="password"
                  placeholder="Re-enter your password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  disabled={deleting}
                />
              </ModalInputGroup>

              <ModalInputGroup>
                <ModalLabel>Type the company name to confirm: <strong>{deleteModal.companyName}</strong></ModalLabel>
                <ModalInput
                  type="text"
                  placeholder="Type company name exactly"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  disabled={deleting}
                />
              </ModalInputGroup>

              {deleteError && <ModalError>{deleteError}</ModalError>}
              {deleteProgress && <ModalProgress>{deleteProgress}</ModalProgress>}
            </ModalBody>

            <ModalFooter>
              <ModalCancelButton onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </ModalCancelButton>
              <ModalConfirmButton
                onClick={handleDeleteCompany}
                disabled={deleting || !deletePassword || !deleteConfirmName}
                style={{ background: '#e53e3e' }}
              >
                <FiTrash2 size={14} />
                {deleting ? 'Deleting Company...' : 'Permanently Delete Company'}
              </ModalConfirmButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
      {/* Manage Modules Modal */}
      {modulesModal && (
        <ModalOverlay onClick={closeModulesModal}>
          <ModulesModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModulesIconWrapper>
                <FiGrid />
              </ModulesIconWrapper>
              <div>
                <ModalTitle>Manage Modules</ModalTitle>
                <ModulesCompanyLabel>{modulesModal.companyName}</ModulesCompanyLabel>
              </div>
              <ModulesCounter>
                {enabledCount + ALWAYS_ENABLED_IDS.size}/{totalToggleable + ALWAYS_ENABLED_IDS.size} enabled
              </ModulesCounter>
              <ModalCloseButton onClick={closeModulesModal} disabled={savingModules}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModulesBody>
              {/* Always-on section */}
              <ModulesGroupSection>
                <ModulesGroupHeader>
                  <ModulesGroupName>Always Enabled</ModulesGroupName>
                </ModulesGroupHeader>
                <ModulesGrid>
                  {Array.from(ALWAYS_ENABLED_IDS).map(id => {
                    const mod = ALL_MODULES.find(m => m.id === id);
                    return (
                      <ModuleItem key={id} $disabled>
                        <ModuleCheckbox type="checkbox" checked disabled />
                        <ModuleLabel $disabled>{mod?.name || id}</ModuleLabel>
                        <AlwaysOnBadge>Required</AlwaysOnBadge>
                      </ModuleItem>
                    );
                  })}
                </ModulesGrid>
              </ModulesGroupSection>

              {/* Toggleable groups */}
              {MODULE_GROUPS.map(group => {
                const groupEnabled = group.modules.filter(m => moduleSettings[m.id]).length;
                const allEnabled = groupEnabled === group.modules.length;
                return (
                  <ModulesGroupSection key={group.name}>
                    <ModulesGroupHeader>
                      <ModulesGroupName>{group.name}</ModulesGroupName>
                      <ModulesGroupCount>{groupEnabled}/{group.modules.length}</ModulesGroupCount>
                      <GroupToggleButtons>
                        <GroupToggleBtn
                          $active={allEnabled}
                          onClick={() => toggleGroup(group.modules, true)}
                        >
                          All
                        </GroupToggleBtn>
                        <GroupToggleBtn
                          $active={groupEnabled === 0}
                          onClick={() => toggleGroup(group.modules, false)}
                        >
                          None
                        </GroupToggleBtn>
                      </GroupToggleButtons>
                    </ModulesGroupHeader>
                    <ModulesGrid>
                      {group.modules.map(mod => (
                        <ModuleItem key={mod.id} onClick={() => toggleModule(mod.id)}>
                          <ModuleCheckbox
                            type="checkbox"
                            checked={!!moduleSettings[mod.id]}
                            onChange={() => toggleModule(mod.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <ModuleLabel>{mod.name}</ModuleLabel>
                        </ModuleItem>
                      ))}
                    </ModulesGrid>
                  </ModulesGroupSection>
                );
              })}
            </ModulesBody>

            <ModalFooter>
              <ModalCancelButton onClick={closeModulesModal} disabled={savingModules}>
                Cancel
              </ModalCancelButton>
              <ModulesSaveButton onClick={saveModules} disabled={savingModules}>
                {savingModules ? 'Saving...' : 'Save Changes'}
              </ModulesSaveButton>
            </ModalFooter>
          </ModulesModalContent>
        </ModalOverlay>
      )}

      {/* New-User Default Modules Modal */}
      <SurveyManager open={showSurveys} onClose={() => setShowSurveys(false)} saUser={saUser} isOwner={isOwner} />

      {showBackupModal && (
        <ModalOverlay onClick={() => !backupRunning && setShowBackupModal(false)}>
          <ModulesModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <ModalHeader>
              <ModulesIconWrapper><FiArchive /></ModulesIconWrapper>
              <div>
                <ModalTitle>Backup</ModalTitle>
                <ModulesCompanyLabel>Choose what to back up. The file is named after its scope.</ModulesCompanyLabel>
              </div>
              <ModalCloseButton onClick={() => !backupRunning && setShowBackupModal(false)} disabled={backupRunning}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModulesBody>
              {[
                { v: 'all', t: 'Everything (full platform backup)', d: `All ${companies.length} companies + platform settings. Use this as your master backup.` },
                { v: 'company', t: 'One specific company', d: 'Just this client\'s data — handy before a risky change or to move a client.' },
                { v: 'paid', t: 'Paid clients only', d: 'All active companies on a monthly or yearly plan.' },
                { v: 'active', t: 'Active clients only', d: 'All companies that are not suspended (includes trials).' },
              ].map(o => (
                <ScopeRow key={o.v} $on={backupScope === o.v} onClick={() => !backupRunning && setBackupScope(o.v)}>
                  <input type="radio" checked={backupScope === o.v} onChange={() => setBackupScope(o.v)} disabled={backupRunning} />
                  <div>
                    <b>{o.t}</b>
                    <small>{o.d}</small>
                  </div>
                </ScopeRow>
              ))}

              {backupScope === 'company' && (
                <div style={{ margin: '10px 0 4px' }}>
                  <select
                    value={backupCompanyId}
                    onChange={(e) => setBackupCompanyId(e.target.value)}
                    disabled={backupRunning}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}
                  >
                    <option value="">— Select a company —</option>
                    {companies.slice().sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''))
                      .map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                  </select>
                </div>
              )}

              <ToolsDivider />
              <ToolsCheckLabel title="Also backup Firebase Storage files (item images, logos). Slower & much larger.">
                <input type="checkbox" checked={includeStorage} onChange={(e) => setIncludeStorage(e.target.checked)} disabled={backupRunning} />
                Include Storage files — images &amp; logos (slower, much larger)
              </ToolsCheckLabel>

              {backupScope !== 'all' && (
                <ScopeNote>
                  A scoped backup contains only these companies. It deliberately leaves out
                  platform-wide settings, so restoring it can never overwrite your global config
                  or another client.
                </ScopeNote>
              )}
              {backupRunning && <ScopeNote>{backupProgress || 'Working…'}</ScopeNote>}
            </ModulesBody>

            <ModalFooter>
              <ModalCancelButton onClick={() => setShowBackupModal(false)} disabled={backupRunning}>Cancel</ModalCancelButton>
              <ModulesSaveButton
                onClick={async () => {
                  if (backupScope === 'company' && !backupCompanyId) { alert('Pick a company.'); return; }
                  // COST: a backup downloads EVERY document it touches straight into the
                  // browser — by far the most expensive button in this panel. Firebase
                  // bills every one of those reads, so make the cost explicit first.
                  const compCount = companies.length || 0;
                  const subCount = BACKUP_SUBCOLLECTIONS.length;
                  const fullQueries = (compCount * subCount) + TOP_LEVEL_COLLECTIONS.length;
                  const costMsg = backupScope === 'company'
                    ? `This backup reads EVERY document of the selected company (~${subCount} collection queries plus all of its documents). This is billed by Firebase and can take a while. Continue?`
                    : backupScope === 'all'
                      ? `Full backup reads EVERY document in the database (~${fullQueries.toLocaleString()} queries across ${compCount.toLocaleString()} companies, plus every document inside them). This is billed by Firebase and can take a long time. Continue?`
                      : `This backup still reads ALL ${compCount.toLocaleString()} company records and every platform user first, then EVERY document of the ${backupScope === 'paid' ? 'paid' : 'active'} companies (~${subCount} collection queries each). This is billed by Firebase and can take a long time. Continue?`;
                  if (!window.confirm(costMsg)) return;
                  await handleBackup(backupScope, backupCompanyId);
                  setShowBackupModal(false);
                }}
                disabled={backupRunning}
              >
                {backupRunning ? 'Backing up…' : 'Start Backup'}
              </ModulesSaveButton>
            </ModalFooter>
          </ModulesModalContent>
        </ModalOverlay>
      )}

      {showQrModal && (
        <ModalOverlay onClick={() => !creatingCampaign && setShowQrModal(false)}>
          <ModulesModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <ModalHeader>
              <ModulesIconWrapper><FiGrid /></ModulesIconWrapper>
              <div>
                <ModalTitle>QR Campaigns</ModalTitle>
                <ModulesCompanyLabel>Printable QR codes for banners · each scan is counted, and signups from it are tracked</ModulesCompanyLabel>
              </div>
              <ModalCloseButton onClick={() => !creatingCampaign && setShowQrModal(false)} disabled={creatingCampaign}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModulesBody>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createCampaign(); } }}
                  placeholder="New campaign name (e.g. Lucky One Mall Banner)…"
                  style={{ flex: 1, padding: '9px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}
                />
                <RefreshButton onClick={createCampaign} disabled={creatingCampaign || !newCampaignName.trim()}>
                  {creatingCampaign ? 'Creating…' : 'Create'}
                </RefreshButton>
              </div>

              {qrLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Loading…</div>
              ) : qrCampaigns.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>No campaigns yet — create one above to get a QR code.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {qrCampaigns.map(c => {
                    const scans = Number(c.scanCount || 0);
                    const signups = signupsForCampaign(c.id);
                    const conv = scans > 0 ? ((signups / scans) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={c.id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: 12, background: '#f7f7f7', border: '1px solid #eee', borderRadius: 8 }}>
                        {qrImages[c.id]
                          ? <img src={qrImages[c.id]} alt="QR" style={{ width: 92, height: 92, borderRadius: 6, background: '#fff', border: '1px solid #e5e5e5' }} />
                          : <div style={{ width: 92, height: 92, borderRadius: 6, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#999' }}>QR…</div>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', marginBottom: 6 }}>{c.name}</div>
                          <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                            <span><strong style={{ fontSize: 16, color: '#1565C0' }}>{scans}</strong> <span style={{ color: '#777' }}>scans</span></span>
                            <span><strong style={{ fontSize: 16, color: '#2E7D32' }}>{signups}</strong> <span style={{ color: '#777' }}>signups</span></span>
                            <span><strong style={{ fontSize: 16, color: '#6A1B9A' }}>{conv}%</strong> <span style={{ color: '#777' }}>conversion</span></span>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <RefreshButton onClick={() => downloadQr(c.id, c.name)} disabled={!qrImages[c.id]} style={{ padding: '7px 12px', fontSize: 12 }}>
                              <FiDownload /> Download QR
                            </RefreshButton>
                            <RefreshButton onClick={() => copyQrLink(c.id)} style={{ padding: '7px 12px', fontSize: 12 }}>
                              Copy link
                            </RefreshButton>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ModulesBody>

            <ModalFooter>
              <ModalCancelButton onClick={() => setShowQrModal(false)} disabled={creatingCampaign}>Close</ModalCancelButton>
              <RefreshButton onClick={openQrModal} disabled={qrLoading} title="Refresh scan counts">
                <FiRefreshCw className={qrLoading ? 'spinning' : ''} /> Refresh
              </RefreshButton>
            </ModalFooter>
          </ModulesModalContent>
        </ModalOverlay>
      )}

      {showSourcesModal && (
        <ModalOverlay onClick={() => !savingSources && setShowSourcesModal(false)}>
          <ModulesModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <ModalHeader>
              <ModulesIconWrapper><FiMail /></ModulesIconWrapper>
              <div>
                <ModalTitle>Signup Sources</ModalTitle>
                <ModulesCompanyLabel>"How did you hear about us?" options on the signup form · "Other" is always shown</ModulesCompanyLabel>
              </div>
              <ModalCloseButton onClick={() => !savingSources && setShowSourcesModal(false)} disabled={savingSources}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModulesBody>
              {sourcesLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Loading…</div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                    <input
                      value={newSource}
                      onChange={(e) => setNewSource(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSource(); } }}
                      placeholder="Add an option (e.g. TikTok, Newspaper, Friend)…"
                      style={{ flex: 1, padding: '9px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 13 }}
                    />
                    <RefreshButton onClick={addSource} title="Add option">Add</RefreshButton>
                  </div>
                  {sourceList.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>No options yet — add at least one.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {sourceList.map((s, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: '#f7f7f7', border: '1px solid #eee', borderRadius: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{s}</span>
                          <button onClick={() => removeSource(idx)} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', display: 'flex', alignItems: 'center', padding: 4 }}>
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', background: '#fafafa', border: '1px dashed #ddd', borderRadius: 6, opacity: 0.75 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#666' }}>Other (please mention)</span>
                        <span style={{ fontSize: 11, color: '#999' }}>always shown</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </ModulesBody>

            <ModalFooter>
              <ModalCancelButton onClick={() => setShowSourcesModal(false)} disabled={savingSources}>Cancel</ModalCancelButton>
              <ModulesSaveButton onClick={saveSources} disabled={savingSources || sourcesLoading}>
                {savingSources ? 'Saving…' : 'Save Options'}
              </ModulesSaveButton>
            </ModalFooter>
          </ModulesModalContent>
        </ModalOverlay>
      )}

      {showDefaultsModal && (
        <ModalOverlay onClick={() => !savingDefaults && setShowDefaultsModal(false)}>
          <ModulesModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModulesIconWrapper>
                <FiGrid />
              </ModulesIconWrapper>
              <div>
                <ModalTitle>New-User Default Modules</ModalTitle>
                <ModulesCompanyLabel>Seeded into every NEW company at signup · existing companies are not affected</ModulesCompanyLabel>
              </div>
              <ModalCloseButton onClick={() => !savingDefaults && setShowDefaultsModal(false)} disabled={savingDefaults}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <ModulesBody>
              {defaultsLoading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Loading…</div>
              ) : (
                <>
                  {/* Always-on section */}
                  <ModulesGroupSection>
                    <ModulesGroupHeader>
                      <ModulesGroupName>Always Enabled</ModulesGroupName>
                    </ModulesGroupHeader>
                    <ModulesGrid>
                      {Array.from(ALWAYS_ENABLED_IDS).map(id => {
                        const mod = ALL_MODULES.find(m => m.id === id);
                        return (
                          <ModuleItem key={id} $disabled>
                            <ModuleCheckbox type="checkbox" checked disabled />
                            <ModuleLabel $disabled>{mod?.name || id}</ModuleLabel>
                            <AlwaysOnBadge>Required</AlwaysOnBadge>
                          </ModuleItem>
                        );
                      })}
                    </ModulesGrid>
                  </ModulesGroupSection>

                  {/* Toggleable groups — new modules appear here automatically */}
                  {MODULE_GROUPS.map(group => {
                    const groupEnabled = group.modules.filter(m => defaultsSettings[m.id]).length;
                    const allEnabled = groupEnabled === group.modules.length;
                    return (
                      <ModulesGroupSection key={group.name}>
                        <ModulesGroupHeader>
                          <ModulesGroupName>{group.name}</ModulesGroupName>
                          <ModulesGroupCount>{groupEnabled}/{group.modules.length}</ModulesGroupCount>
                          <GroupToggleButtons>
                            <GroupToggleBtn $active={allEnabled} onClick={() => toggleDefaultsGroup(group.modules, true)}>All</GroupToggleBtn>
                            <GroupToggleBtn $active={groupEnabled === 0} onClick={() => toggleDefaultsGroup(group.modules, false)}>None</GroupToggleBtn>
                          </GroupToggleButtons>
                        </ModulesGroupHeader>
                        <ModulesGrid>
                          {group.modules.map(mod => (
                            <ModuleItem key={mod.id} onClick={() => toggleDefault(mod.id)}>
                              <ModuleCheckbox
                                type="checkbox"
                                checked={!!defaultsSettings[mod.id]}
                                onChange={() => toggleDefault(mod.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <ModuleLabel>{mod.name}</ModuleLabel>
                            </ModuleItem>
                          ))}
                        </ModulesGrid>
                      </ModulesGroupSection>
                    );
                  })}
                </>
              )}
            </ModulesBody>

            <ModalFooter>
              <ModalConfirmButton
                onClick={applyDefaultsToExisting}
                disabled={savingDefaults || defaultsLoading || applyingDefaults}
                style={{ marginRight: 'auto' }}
                title="Overwrite module settings on ALL existing companies with this template"
              >
                {applyingDefaults ? 'Applying…' : 'Apply to All Existing Companies'}
              </ModalConfirmButton>
              <ModalCancelButton onClick={() => setShowDefaultsModal(false)} disabled={savingDefaults || applyingDefaults}>
                Cancel
              </ModalCancelButton>
              <ModulesSaveButton onClick={saveDefaults} disabled={savingDefaults || defaultsLoading || applyingDefaults}>
                {savingDefaults ? 'Saving…' : 'Save Defaults (new companies)'}
              </ModulesSaveButton>
            </ModalFooter>
          </ModulesModalContent>
        </ModalOverlay>
      )}

      {/* Currency & Tax Settings Modal */}
      {currencyModal && (
        <ModalOverlay onClick={() => !savingCurrency && setCurrencyModal(null)}>
          <SubModalContent onClick={e => e.stopPropagation()}>
            <SubModalHeader>
              <SubModalTitle><FiDollarSign /> Currency & Tax — {currencyModal.companyName}</SubModalTitle>
              <ModalCloseButton onClick={() => !savingCurrency && setCurrencyModal(null)} disabled={savingCurrency}><FiX /></ModalCloseButton>
            </SubModalHeader>
            <SubModalBody>
              <SubFieldGroup>
                <SubFieldLabel>Tax Label</SubFieldLabel>
                <SubSelect value={currencyForm.taxLabel} onChange={e => setCurrencyForm(prev => ({ ...prev, taxLabel: e.target.value }))}>
                  <option value="GST">GST (Pakistan)</option>
                  <option value="VAT">VAT (UAE, Saudi, EU)</option>
                  <option value="Sales Tax">Sales Tax</option>
                  <option value="Tax">Tax (Generic)</option>
                </SubSelect>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Currency Code</SubFieldLabel>
                <SubSelect value={currencyForm.currencyCode} onChange={e => setCurrencyForm(prev => ({ ...prev, currencyCode: e.target.value }))}>
                  <option value="PKR">PKR - Pakistani Rupee</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="AED">AED - UAE Dirham</option>
                  <option value="SAR">SAR - Saudi Riyal</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="BDT">BDT - Bangladeshi Taka</option>
                  <option value="OMR">OMR - Omani Rial</option>
                  <option value="QAR">QAR - Qatari Riyal</option>
                  <option value="KWD">KWD - Kuwaiti Dinar</option>
                  <option value="BHD">BHD - Bahraini Dinar</option>
                </SubSelect>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Currency Symbol</SubFieldLabel>
                <SubSelect value={currencyForm.currencySymbol} onChange={e => setCurrencyForm(prev => ({ ...prev, currencySymbol: e.target.value }))}>
                  <option value="Rs.">Rs.</option>
                  <option value="₨">₨</option>
                  <option value="$">$</option>
                  <option value="€">€</option>
                  <option value="£">£</option>
                  <option value="د.إ">د.إ (AED)</option>
                  <option value="﷼">﷼ (SAR)</option>
                  <option value="₹">₹ (INR)</option>
                  <option value="৳">৳ (BDT)</option>
                  <option value="ر.ع.">ر.ع. (OMR)</option>
                  <option value="ر.ق">ر.ق (QAR)</option>
                  <option value="د.ك">د.ك (KWD)</option>
                  <option value="د.ب">د.ب (BHD)</option>
                </SubSelect>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Brand Name (optional — shown on dashboard sidebar)</SubFieldLabel>
                <SubInput
                  type="text"
                  value={currencyForm.brandName}
                  onChange={e => setCurrencyForm(prev => ({ ...prev, brandName: e.target.value }))}
                  placeholder="Leave empty for default (AMAL ERP)"
                  maxLength={40}
                />
                <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                  White-label the dashboard for this company. Empty = AMAL ERP.
                </div>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Brand Logo (optional)</SubFieldLabel>
                {currencyForm.brandLogoUrl ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', background: '#f9f9f9', borderRadius: '6px' }}>
                    <img src={currencyForm.brandLogoUrl} alt="Brand logo" style={{ maxHeight: '40px', maxWidth: '140px', objectFit: 'contain' }} />
                    <button
                      type="button"
                      onClick={() => setCurrencyForm(prev => ({ ...prev, brandLogoUrl: '' }))}
                      style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid #e53e3e', color: '#e53e3e', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      style={{ fontSize: '12px' }}
                      disabled={uploadingLogo}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2MB'); return; }
                        setUploadingLogo(true);
                        try {
                          const storageRef = ref(storage, `brands/${currencyModal.companyId}/logo_${Date.now()}`);
                          await uploadBytes(storageRef, file);
                          const url = await getDownloadURL(storageRef);
                          if (!url) throw new Error('Storage returned an empty URL.');
                          setCurrencyForm(prev => ({ ...prev, brandLogoUrl: url }));
                        } catch (err) {
                          console.error('Upload error:', err);
                          alert('Failed to upload logo: ' + (err?.message || err?.code || 'unknown error'));
                        } finally {
                          setUploadingLogo(false);
                        }
                      }}
                    />
                    {uploadingLogo && (
                      <div style={{ fontSize: '11px', color: '#10b981', marginTop: '6px', fontWeight: 600 }}>
                        Uploading logo… please wait before saving.
                      </div>
                    )}
                    <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                      PNG, JPG, SVG or WebP. Max 2MB. Leave empty for default logo.
                    </div>
                  </div>
                )}
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>AmalERP line on prints</SubFieldLabel>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    aria-label="Hide Powered by AmalERP on prints"
                    checked={currencyForm.hidePrintBranding === true}
                    onChange={e => setCurrencyForm(prev => ({ ...prev, hidePrintBranding: e.target.checked }))}
                  />
                  Hide "Powered by AmalERP · www.amalerp.com" on this company's prints
                </label>
                <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>
                  Normally shown at the bottom of every invoice, report, receipt and PDF. Only you can change this — the company cannot.
                </div>
              </SubFieldGroup>
              <div style={{ padding: '10px 0', fontSize: '12px', color: '#666', borderTop: '1px solid #eee', marginTop: '10px' }}>
                Preview: <strong>{currencyForm.currencySymbol}1,234,567.00</strong> ({currencyForm.currencyCode})
                <br />Tax label: <strong>{currencyForm.taxLabel}</strong>
              </div>
            </SubModalBody>
            <SubModalFooter>
              <ModalCancelButton onClick={() => setCurrencyModal(null)} disabled={savingCurrency || uploadingLogo}>Cancel</ModalCancelButton>
              <SubSaveButton onClick={saveCurrencySettings} disabled={savingCurrency || uploadingLogo}>
                {uploadingLogo ? 'Uploading logo...' : savingCurrency ? 'Saving...' : 'Save Settings'}
              </SubSaveButton>
            </SubModalFooter>
          </SubModalContent>
        </ModalOverlay>
      )}

      {/* FBR POS Config Modal */}
      {fbrModal && (
        <ModalOverlay onClick={() => !savingFbr && setFbrModal(null)}>
          <SubModalContent onClick={e => e.stopPropagation()}>
            <SubModalHeader>
              <SubModalTitle><FiShield /> FBR POS — {fbrModal.companyName}</SubModalTitle>
              <ModalCloseButton onClick={() => !savingFbr && setFbrModal(null)} disabled={savingFbr}><FiX /></ModalCloseButton>
            </SubModalHeader>
            <SubModalBody>
              <SubFieldGroup>
                <SubFieldLabel>POS ID (from FBR registration)</SubFieldLabel>
                <SubInput type="text" value={fbrForm.posId} onChange={e => setFbrForm(prev => ({ ...prev, posId: e.target.value }))} placeholder="e.g. 123456" />
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Environment</SubFieldLabel>
                <SubSelect value={fbrForm.env} onChange={e => setFbrForm(prev => ({ ...prev, env: e.target.value }))}>
                  <option value="sandbox">Sandbox (testing)</option>
                  <option value="live">Live (production)</option>
                </SubSelect>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>FBR Endpoint URL (optional)</SubFieldLabel>
                <SubInput type="text" value={fbrForm.endpoint} onChange={e => setFbrForm(prev => ({ ...prev, endpoint: e.target.value }))} placeholder="https://gw.fbr.gov.pk/imsp/v1/api/Live/PostData" />
                <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>Leave blank to use the default FBR gateway URL.</div>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Bearer Token (secret)</SubFieldLabel>
                <SubInput type="password" value={fbrForm.token} onChange={e => setFbrForm(prev => ({ ...prev, token: e.target.value }))} placeholder={fbrForm.hasToken ? '•••••• (leave blank to keep existing)' : 'Paste the FBR POS token'} />
                <div style={{ fontSize: '10px', color: fbrForm.hasToken ? '#10b981' : '#999', marginTop: '4px' }}>
                  {fbrForm.hasToken ? 'A token is already saved. Leave blank to keep it, or paste a new one to replace it.' : 'Stored securely on the server — never shown again.'}
                </div>
              </SubFieldGroup>
              <div style={{ padding: '10px 0', fontSize: '12px', color: '#666', borderTop: '1px solid #eee', marginTop: '10px' }}>
                After saving, enable <strong>FBR POS</strong> for this company in <strong>Manage Modules</strong> so the "Send to FBR" button appears on its Sale Invoices.
              </div>
            </SubModalBody>
            <SubModalFooter>
              <ModalCancelButton onClick={() => setFbrModal(null)} disabled={savingFbr}>Cancel</ModalCancelButton>
              <SubSaveButton onClick={saveFbrConfig} disabled={savingFbr || !fbrForm.posId.trim()}>{savingFbr ? 'Saving…' : 'Save FBR Config'}</SubSaveButton>
            </SubModalFooter>
          </SubModalContent>
        </ModalOverlay>
      )}

      {/* FBR Digital Invoicing Config Modal (standalone) */}
      {diModal && (
        <ModalOverlay onClick={() => !savingDi && setDiModal(null)}>
          <SubModalContent onClick={e => e.stopPropagation()}>
            <SubModalHeader>
              <SubModalTitle><FiShield /> FBR Digital Invoicing — {diModal.companyName}</SubModalTitle>
              <ModalCloseButton onClick={() => !savingDi && setDiModal(null)} disabled={savingDi}><FiX /></ModalCloseButton>
            </SubModalHeader>
            <SubModalBody>
              <SubFieldGroup>
                <SubFieldLabel>Seller NTN / STRN</SubFieldLabel>
                <SubInput type="text" value={diForm.sellerNtn} onChange={e => setDiForm(prev => ({ ...prev, sellerNtn: e.target.value }))} placeholder="Defaults to the company's NTN" />
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Production Endpoint URL (optional)</SubFieldLabel>
                <SubInput type="text" value={diForm.endpoint} onChange={e => setDiForm(prev => ({ ...prev, endpoint: e.target.value }))} placeholder="https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata" />
                <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>Leave blank to use the default production URL. This module is production-only (no sandbox).</div>
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Default Sale Type (optional)</SubFieldLabel>
                <SubInput type="text" value={diForm.saleType} onChange={e => setDiForm(prev => ({ ...prev, saleType: e.target.value }))} placeholder="Goods at standard rate (default)" />
              </SubFieldGroup>
              <SubFieldGroup>
                <SubFieldLabel>Production Token (secret)</SubFieldLabel>
                <SubInput type="password" value={diForm.token} onChange={e => setDiForm(prev => ({ ...prev, token: e.target.value }))} placeholder={diForm.hasToken ? '•••••• (leave blank to keep existing)' : 'Paste the FBR production token'} />
                <div style={{ fontSize: '10px', color: diForm.hasToken ? '#10b981' : '#999', marginTop: '4px' }}>
                  {diForm.hasToken ? 'A token is already saved. Leave blank to keep it, or paste a new one to replace it.' : 'Stored securely on the server — never shown again. Obtain it from FBR IRIS after passing sandbox scenario testing.'}
                </div>
              </SubFieldGroup>
              <div style={{ padding: '10px 0', fontSize: '12px', color: '#666', borderTop: '1px solid #eee', marginTop: '10px' }}>
                After saving, enable <strong>FBR Digital Invoicing</strong> for this company in <strong>Manage Modules</strong> so the "Send to FBR (Digital Invoice)" button appears on its Sale Invoices.
              </div>
            </SubModalBody>
            <SubModalFooter>
              <ModalCancelButton onClick={() => setDiModal(null)} disabled={savingDi}>Cancel</ModalCancelButton>
              <SubSaveButton onClick={saveDiConfig} disabled={savingDi}>{savingDi ? 'Saving…' : 'Save DI Config'}</SubSaveButton>
            </SubModalFooter>
          </SubModalContent>
        </ModalOverlay>
      )}

      {/* Sales-user Company Access Modal */}
      {salesAccessModal && (
        <ModalOverlay onClick={() => !savingSalesAccess && setSalesAccessModal(null)}>
          <SubModalContent onClick={e => e.stopPropagation()}>
            <SubModalHeader>
              <SubModalTitle><FiUsers /> Sales Access — {salesAccessModal.companyName}</SubModalTitle>
              <ModalCloseButton onClick={() => !savingSalesAccess && setSalesAccessModal(null)} disabled={savingSalesAccess}><FiX /></ModalCloseButton>
            </SubModalHeader>
            <SubModalBody>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                A <strong>Sales</strong> login is view-only. Tick which sales persons can see <strong>this company</strong>
                (unticking hides it from that login; sales users see all companies by default). Use <strong>Make Sales</strong>
                to convert a full super-admin into a view-only sales login. Role changes save immediately; company access saves on "Save Access".
              </div>
              {(() => {
                const currentUid = (() => { try { return sessionStorage.getItem('sa_uid') || auth.currentUser?.uid; } catch { return auth.currentUser?.uid; } })();
                const others = salesUsers.filter(u => u.uid !== currentUid);
                if (others.length === 0) {
                  return (
                    <div style={{ fontSize: '13px', color: '#999', padding: '16px', textAlign: 'center', border: '1px dashed #ddd', borderRadius: '8px' }}>
                      No other super-admin users found. Create the login in Firebase (a superAdmins doc), then convert it to Sales here.
                    </div>
                  );
                }
                return others.map(u => {
                  const isSales = u.role === 'sales';
                  const canSee = !salesAccessHidden.includes(u.uid);
                  return (
                    <div key={u.uid} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #eee', borderRadius: '8px', marginBottom: '8px' }}>
                      <input
                        type="checkbox"
                        checked={isSales ? canSee : false}
                        disabled={!isSales || savingSalesAccess}
                        title={isSales ? 'Can see this company' : 'Set role to Sales first'}
                        onChange={() => toggleSalesUserAccess(u.uid)}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>{u.displayName || u.email || u.uid}</span>
                        {u.email && u.displayName && <span style={{ fontSize: '11px', color: '#888' }}>{u.email}</span>}
                      </div>
                      <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', color: isSales ? '#6366f1' : '#b45309', background: isSales ? '#eef2ff' : '#fef3c7' }}>
                        {isSales ? 'SALES' : 'OWNER'}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSalesUserRole(u.uid, isSales ? 'superadmin' : 'sales')}
                        style={{ fontSize: '11px', fontWeight: 600, padding: '5px 10px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {isSales ? 'Make Owner' : 'Make Sales'}
                      </button>
                    </div>
                  );
                });
              })()}
            </SubModalBody>
            <SubModalFooter>
              <ModalCancelButton onClick={() => setSalesAccessModal(null)} disabled={savingSalesAccess}>Cancel</ModalCancelButton>
              <SubSaveButton onClick={saveSalesAccess} disabled={savingSalesAccess}>
                {savingSalesAccess ? 'Saving...' : 'Save Access'}
              </SubSaveButton>
            </SubModalFooter>
          </SubModalContent>
        </ModalOverlay>
      )}

      {/* Subscription Update Modal */}
      {subModal && (
        <ModalOverlay onClick={() => !savingSub && setSubModal(null)}>
          <SubModalContent onClick={(e) => e.stopPropagation()}>
            <SubModalHeader>
              <SubModalTitle>Update Subscription — {subModal.companyName}</SubModalTitle>
              <ModalCloseButton onClick={() => !savingSub && setSubModal(null)} disabled={savingSub}>
                <FiX />
              </ModalCloseButton>
            </SubModalHeader>
            <SubModalBody>
              {/* ── Plan & Owner ── */}
              <SubSectionTitle>Plan</SubSectionTitle>
              <SubFieldRow>
                <SubFieldGroup>
                  <SubFieldLabel>Plan</SubFieldLabel>
                  <SubSelect value={subForm.plan} onChange={(e) => setSubForm(f => ({ ...f, plan: e.target.value }))}>
                    <option value="trial">Trial (7 days)</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly (3 months)</option>
                    <option value="half-yearly">Half-Yearly (6 months)</option>
                    <option value="yearly">Yearly</option>
                  </SubSelect>
                </SubFieldGroup>
                <SubFieldGroup>
                  <SubFieldLabel>Sales Person</SubFieldLabel>
                  <SubSelect value={subForm.salesPersonId} onChange={(e) => setSubForm(f => ({ ...f, salesPersonId: e.target.value }))}>
                    <option value="">— None —</option>
                    {salesPersons.filter(p => p.isActive !== false).map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </SubSelect>
                </SubFieldGroup>
              </SubFieldRow>

              {subForm.plan !== 'trial' && (
                <>
                  {/* ── Pricing ── */}
                  <SubSectionTitle>Pricing</SubSectionTitle>
                  <SubFieldRow>
                    <SubFieldGroup>
                      <SubFieldLabel>Setup Fee (one-time)</SubFieldLabel>
                      <SubInput type="number" min="0" placeholder="0 for none" value={subForm.setupFee} onChange={(e) => setSubForm(f => ({ ...f, setupFee: e.target.value }))} />
                    </SubFieldGroup>
                    <SubFieldGroup>
                      <SubFieldLabel>Recurring / {PLAN_PERIOD_WORD[subForm.plan] || 'year'}</SubFieldLabel>
                      <SubInput type="number" placeholder="e.g. 5000" value={subForm.amount} onChange={(e) => setSubForm(f => ({ ...f, amount: e.target.value }))} />
                    </SubFieldGroup>
                  </SubFieldRow>

                  {/* ── Dates ── */}
                  <SubSectionTitle>Dates</SubSectionTitle>
                  <SubFieldRow>
                    <SubFieldGroup>
                      <SubFieldLabel>First Payment</SubFieldLabel>
                      <SubInput type="date" value={subForm.firstPaymentDate} onChange={(e) => setSubForm(f => ({ ...f, firstPaymentDate: e.target.value }))} />
                    </SubFieldGroup>
                    <SubFieldGroup>
                      <SubFieldLabel>Last Payment</SubFieldLabel>
                      <SubInput type="date" value={subForm.lastPaymentDate} onChange={(e) => setSubForm(f => ({ ...f, lastPaymentDate: e.target.value }))} />
                    </SubFieldGroup>
                  </SubFieldRow>
                  {subForm.lastPaymentDate && (
                    <div style={{ fontSize: '12px', color: colors.text.secondary, marginTop: '-4px' }}>
                      Next due (auto):{' '}
                      <strong style={{ color: colors.primary.main }}>
                        {(() => {
                          const paid = new Date(subForm.lastPaymentDate);
                          const months = planMonths(subForm.plan) || 12;
                          const next = new Date(paid.getFullYear(), paid.getMonth() + months, paid.getDate());
                          return next.toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' });
                        })()}
                      </strong>
                    </div>
                  )}

                  {/* ── Payment History ── */}
                  <SubSectionTitle>Payment History ({subForm.paymentHistory.length})</SubSectionTitle>
                  <PaymentHistoryWrap>
                    {subForm.paymentHistory.length === 0 ? (
                      <div style={{ padding: '8px', color: '#888', fontSize: '11px', fontStyle: 'italic', textAlign: 'center', background: '#fafafa', borderRadius: '4px' }}>
                        No entries yet. Add setup, renewal, discount, or refund payments below.
                      </div>
                    ) : (
                      <PaymentHistoryList>
                        {subForm.paymentHistory.map((p, idx) => (
                          <PaymentHistoryRow key={idx}>
                            <PaymentTypeBadge $type={p.type}>{p.type}</PaymentTypeBadge>
                            <PaymentDate>{p.date ? new Date(p.date).toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</PaymentDate>
                            <PaymentAmount>PKR {Number(p.amount || 0).toLocaleString('en-PK')}</PaymentAmount>
                            {p.notes && <PaymentNotes title={p.notes}>{p.notes}</PaymentNotes>}
                            <PaymentDelete onClick={() => setSubForm(f => ({ ...f, paymentHistory: f.paymentHistory.filter((_, i) => i !== idx) }))}>
                              <FiTrash2 size={12} />
                            </PaymentDelete>
                          </PaymentHistoryRow>
                        ))}
                      </PaymentHistoryList>
                    )}
                    <PaymentDraftRow>
                      <SubInput
                        type="date"
                        value={paymentDraft.date}
                        onChange={(e) => setPaymentDraft(d => ({ ...d, date: e.target.value }))}
                        style={{ minWidth: 130 }}
                      />
                      <SubSelect
                        value={paymentDraft.type}
                        onChange={(e) => setPaymentDraft(d => ({ ...d, type: e.target.value }))}
                        style={{ minWidth: 100 }}
                      >
                        <option value="setup">Setup</option>
                        <option value="renewal">Renewal</option>
                        <option value="discount">Discount</option>
                        <option value="refund">Refund</option>
                      </SubSelect>
                      <SubInput
                        type="number"
                        placeholder="Amount"
                        value={paymentDraft.amount}
                        onChange={(e) => setPaymentDraft(d => ({ ...d, amount: e.target.value }))}
                        style={{ minWidth: 90 }}
                      />
                      <SubInput
                        type="text"
                        placeholder="Notes (optional)"
                        value={paymentDraft.notes}
                        onChange={(e) => setPaymentDraft(d => ({ ...d, notes: e.target.value }))}
                        style={{ flex: 1, minWidth: 120 }}
                      />
                      <SubSaveButton
                        onClick={() => {
                          if (!paymentDraft.date || !paymentDraft.amount) return;
                          setSubForm(f => ({
                            ...f,
                            paymentHistory: [...f.paymentHistory, { ...paymentDraft }],
                          }));
                          setPaymentDraft({ date: '', amount: '', type: 'renewal', notes: '' });
                        }}
                        disabled={!paymentDraft.date || !paymentDraft.amount}
                        style={{ padding: '6px 12px', fontSize: 12 }}
                      >
                        + Add
                      </SubSaveButton>
                    </PaymentDraftRow>
                  </PaymentHistoryWrap>
                </>
              )}

              {/* ── Package limits ── */}
              <SubSectionTitle>Package Limits</SubSectionTitle>
              <SubFieldRow>
                <SubFieldGroup>
                  <SubFieldLabel>Max Branches (blank = unlimited)</SubFieldLabel>
                  <SubInput type="number" min="0" placeholder="e.g. 2" value={subForm.maxBranches} onChange={(e) => setSubForm(f => ({ ...f, maxBranches: e.target.value }))} />
                </SubFieldGroup>
                <SubFieldGroup>
                  <SubFieldLabel>Max Users (blank = unlimited)</SubFieldLabel>
                  <SubInput type="number" min="0" placeholder="e.g. 5" value={subForm.maxUsers} onChange={(e) => setSubForm(f => ({ ...f, maxUsers: e.target.value }))} />
                </SubFieldGroup>
              </SubFieldRow>
              <SubFieldRow>
                <SubFieldGroup>
                  <SubFieldLabel>Max POS Counters (blank/0 = none)</SubFieldLabel>
                  <SubInput type="number" min="0" placeholder="e.g. 1" value={subForm.maxPosCounters} onChange={(e) => setSubForm(f => ({ ...f, maxPosCounters: e.target.value }))} />
                </SubFieldGroup>
                <SubFieldGroup />
              </SubFieldRow>

              {/* ── Notes ── */}
              <SubFieldGroup>
                <SubFieldLabel>Notes (optional)</SubFieldLabel>
                <SubInput type="text" placeholder="Any notes..." value={subForm.notes} onChange={(e) => setSubForm(f => ({ ...f, notes: e.target.value }))} />
              </SubFieldGroup>
            </SubModalBody>
            <SubModalFooter>
              <ModalCancelButton onClick={() => setSubModal(null)} disabled={savingSub}>Cancel</ModalCancelButton>
              <SubSaveButton onClick={saveSubscription} disabled={savingSub || (subForm.plan !== 'trial' && !subForm.lastPaymentDate)}>
                {savingSub ? 'Saving...' : 'Save Subscription'}
              </SubSaveButton>
            </SubModalFooter>
          </SubModalContent>
        </ModalOverlay>
      )}

      {/* Manage Sales Persons Modal */}
      {salesPersonsModal && (
        <ModalOverlay onClick={() => !savingSalesPerson && setSalesPersonsModal(false)}>
          <SubModalContent onClick={(e) => e.stopPropagation()}>
            <SubModalHeader>
              <SubModalTitle>Manage Sales Persons</SubModalTitle>
              <ModalCloseButton onClick={() => !savingSalesPerson && setSalesPersonsModal(false)} disabled={savingSalesPerson}>
                <FiX />
              </ModalCloseButton>
            </SubModalHeader>
            <SubModalBody>
              <SubFieldGroup>
                <SubFieldLabel>Add new sales person</SubFieldLabel>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <SubInput
                    type="text"
                    placeholder="Full name"
                    value={newSalesPersonName}
                    onChange={(e) => setNewSalesPersonName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addSalesPerson(); }}
                    disabled={savingSalesPerson}
                  />
                  <SubSaveButton
                    onClick={addSalesPerson}
                    disabled={savingSalesPerson || !newSalesPersonName.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {savingSalesPerson ? 'Adding...' : 'Add'}
                  </SubSaveButton>
                </div>
              </SubFieldGroup>

              <SubFieldGroup>
                <SubFieldLabel>Existing sales persons ({salesPersons.length})</SubFieldLabel>
                {salesPersons.length === 0 ? (
                  <div style={{ padding: '12px', color: '#888', fontSize: '13px', fontStyle: 'italic', textAlign: 'center', border: '1px dashed #ddd', borderRadius: '6px' }}>
                    No sales persons yet. Add one above.
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e5e5e5', borderRadius: '6px', maxHeight: '280px', overflowY: 'auto' }}>
                    {salesPersons.map((p, idx) => (
                      <div
                        key={p.id}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 12px',
                          borderBottom: idx < salesPersons.length - 1 ? '1px solid #f0f0f0' : 'none',
                          fontSize: '13px'
                        }}
                      >
                        <span>{p.name}</span>
                        <button
                          onClick={() => deleteSalesPerson(p.id)}
                          title="Delete"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#c62828', padding: '4px 6px', borderRadius: '4px',
                            display: 'flex', alignItems: 'center'
                          }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </SubFieldGroup>
            </SubModalBody>
            <SubModalFooter>
              <ModalCancelButton onClick={() => setSalesPersonsModal(false)} disabled={savingSalesPerson}>Close</ModalCancelButton>
            </SubModalFooter>
          </SubModalContent>
        </ModalOverlay>
      )}

      {/* Email Compose Modal */}
      {emailModal && (
        <ModalOverlay onClick={closeEmailModal}>
          <EmailModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <EmailIconWrapper>
                <FiMail />
              </EmailIconWrapper>
              <div>
                <ModalTitle>
                  {emailModal.mode === 'single'
                    ? `Email to ${emailModal.company?.companyName}`
                    : 'Email Active Companies'}
                </ModalTitle>
                <ModulesCompanyLabel>
                  {emailModal.mode === 'single'
                    ? (emailModal.company?.contactEmail || emailModal.company?.email || emailModal.company?.companyEmail || 'No email')
                    : `${getFilteredRecipients().length} recipients`}
                </ModulesCompanyLabel>
              </div>
              <ModalCloseButton onClick={closeEmailModal} disabled={emailSending}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <EmailBody>
              {emailModal.mode === 'bulk' && (
                <EmailFieldGroup>
                  <EmailLabel>Filter Recipients</EmailLabel>
                  <EmailSelect value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} disabled={emailSending}>
                    <option value="all">All Users ({companies.filter(c => c.contactEmail).length})</option>
                    <option value="paid">Paid Users ({companies.filter(c => c.status !== 'suspended' && c.contactEmail && c.subscription && isPaidPlan(c.subscription.plan)).length})</option>
                    <option value="trial">Trial Users ({companies.filter(c => c.status !== 'suspended' && c.contactEmail && (!c.subscription || c.subscription.plan === 'trial')).length})</option>
                  </EmailSelect>
                </EmailFieldGroup>
              )}

              <EmailFieldGroup>
                <EmailLabel>Template</EmailLabel>
                <EmailSelect value={selectedTemplate} onChange={(e) => applyTemplate(e.target.value)}>
                  <option value="">— Select a template —</option>
                  {EMAIL_TEMPLATES.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </EmailSelect>
              </EmailFieldGroup>

              <EmailFieldGroup>
                <EmailLabel>Subject</EmailLabel>
                <EmailInput
                  type="text"
                  placeholder="Email subject..."
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={emailSending}
                />
                <EmailHint>Use {'{{companyName}}'} for personalization</EmailHint>
              </EmailFieldGroup>

              <EmailFieldGroup>
                <EmailLabel>Body (HTML)</EmailLabel>
                <EmailTextarea
                  placeholder="Email body HTML..."
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  disabled={emailSending}
                  rows={12}
                />
              </EmailFieldGroup>

              {/* Preview */}
              {emailBody && (
                <EmailFieldGroup>
                  <EmailLabel>Preview</EmailLabel>
                  <EmailPreview
                    dangerouslySetInnerHTML={{
                      __html: personalizeContent(
                        emailBody,
                        emailModal.mode === 'single' ? emailModal.company?.companyName : 'Your Company Name'
                      )
                    }}
                  />
                </EmailFieldGroup>
              )}

              {emailStatus && (
                <EmailStatusMsg $success={emailStatus.includes('Queued')}>
                  {emailStatus}
                </EmailStatusMsg>
              )}

              {/* Delivery totals across the whole mail queue */}
              <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Email Delivery (all-time)</span>
                  <button type="button" onClick={loadEmailStats} disabled={emailStatsLoading}
                    style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                    {emailStatsLoading ? '…' : 'Refresh'}
                  </button>
                </div>
                {emailStats ? (
                  <div style={{ display: 'flex', gap: 18, fontSize: 13 }}>
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ Sent: {emailStats.sent}</span>
                    <span style={{ color: '#dc2626', fontWeight: 600 }}>✕ Failed: {emailStats.error}</span>
                    <span style={{ color: '#d97706', fontWeight: 600 }}>⏳ Pending: {emailStats.pending}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{emailStatsLoading ? 'Loading…' : 'No data'}</div>
                )}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  Sending is throttled to ~1/sec, so totals update gradually after queueing — hit Refresh. Failed rows carry the reason in the <code>mailQueue</code> collection.
                </div>
              </div>
            </EmailBody>

            <ModalFooter>
              <ModalCancelButton onClick={closeEmailModal} disabled={emailSending}>
                Cancel
              </ModalCancelButton>
              <EmailSendButton onClick={sendEmail} disabled={emailSending || !emailSubject.trim() || !emailBody.trim()}>
                <FiSend size={14} />
                {emailSending ? 'Sending...' : emailModal.mode === 'single' ? 'Send Email' : 'Send to All'}
              </EmailSendButton>
            </ModalFooter>
          </EmailModalContent>
        </ModalOverlay>
      )}

      {/* Bulk WhatsApp modal */}
      {waModal && (
        <ModalOverlay onClick={closeWhatsappModal}>
          <EmailModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>
                  {waModal.mode === 'single'
                    ? `WhatsApp to ${waModal.company?.companyName}`
                    : 'WhatsApp Update to Users'}
                </ModalTitle>
                <ModulesCompanyLabel>
                  {waModal.mode === 'single'
                    ? (sanitizePhone(waModal.company?.whatsappNumber).length >= 8 ? waModal.company?.whatsappNumber : 'No WhatsApp number')
                    : `${waRecipients().length} recipients`}
                </ModulesCompanyLabel>
              </div>
              <ModalCloseButton onClick={closeWhatsappModal} disabled={waSending}>
                <FiX />
              </ModalCloseButton>
            </ModalHeader>

            <EmailBody>
              {waModal.mode === 'bulk' && (
                <>
                  <EmailFieldGroup>
                    <EmailLabel>Filter Recipients</EmailLabel>
                    <EmailSelect value={waFilter} onChange={(e) => setWaFilter(e.target.value)} disabled={waSending}>
                      <option value="all">All Users ({waCountFor('all')})</option>
                      <option value="paid">Paid Users ({waCountFor('paid')})</option>
                      <option value="trial">Trial Users ({waCountFor('trial')})</option>
                    </EmailSelect>
                  </EmailFieldGroup>

                  <EmailFieldGroup>
                    <EmailLabel>Filter by date (optional — target fewer, pay less)</EmailLabel>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <EmailSelect value={waDateField} onChange={(e) => setWaDateField(e.target.value)} disabled={waSending} style={{ flex: '1 1 180px' }}>
                        <option value="none">No date filter</option>
                        <option value="signup">Signup date (first joined)</option>
                        <option value="lastlogin">Last login date</option>
                      </EmailSelect>
                      {waDateField !== 'none' && (
                        <>
                          <input type="date" value={waFrom} onChange={(e) => setWaFrom(e.target.value)} disabled={waSending}
                            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                          <span style={{ fontSize: 13, color: '#64748b' }}>to</span>
                          <input type="date" value={waTo} onChange={(e) => setWaTo(e.target.value)} disabled={waSending}
                            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
                          <button type="button" onClick={setWaThisMonth} disabled={waSending}
                            style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>This month</button>
                        </>
                      )}
                    </div>
                    <EmailHint>
                      {waDateField === 'none'
                        ? 'e.g. message only customers who signed up this month, or who logged in recently — fewer messages, lower cost.'
                        : `Matching now: ${waRecipients().length} recipient(s). Companies without this date are skipped.`}
                    </EmailHint>
                  </EmailFieldGroup>
                </>
              )}

              <EmailFieldGroup>
                <EmailLabel>Approved Template {waTemplatesLoading && '(loading…)'}</EmailLabel>
                <EmailSelect value={waTemplateName} onChange={(e) => applyWaTemplate(e.target.value)} disabled={waSending || waTemplatesLoading}>
                  <option value="">— Select a template —</option>
                  {waTemplates.map(t => (
                    <option key={t.name} value={t.name}>{t.name} ({t.category})</option>
                  ))}
                </EmailSelect>
                {!waTemplatesLoading && waTemplates.length === 0 && (
                  <EmailHint>No approved templates found. Create one in WhatsApp Manager first (Utility category recommended).</EmailHint>
                )}
              </EmailFieldGroup>

              {selectedWaTemplate() && selectedWaTemplate().variableCount > 0 && (
                <EmailFieldGroup>
                  <EmailLabel>Template Variables</EmailLabel>
                  {Array.from({ length: selectedWaTemplate().variableCount }, (_, i) => (
                    <EmailInput
                      key={i}
                      type="text"
                      placeholder={i === 0 ? 'Variable 1 (company name)' : `Variable ${i + 1}`}
                      value={waParams[i] || ''}
                      onChange={(e) => setWaParams(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
                      disabled={waSending}
                      style={{ marginBottom: 6 }}
                    />
                  ))}
                  <EmailHint>Use {'{{companyName}}'} to insert each recipient's company name.</EmailHint>
                </EmailFieldGroup>
              )}

              {selectedWaTemplate() && (
                <EmailFieldGroup>
                  <EmailLabel>Preview</EmailLabel>
                  <div style={{ whiteSpace: 'pre-wrap', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#166534' }}>
                    {waPreview(waModal.mode === 'single' ? waModal.company?.companyName : 'Your Company Name')}
                  </div>
                </EmailFieldGroup>
              )}

              {selectedWaTemplate() && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#334155' }}>
                  Estimated cost: <b>≈ PKR {((waModal.mode === 'single' ? 1 : waRecipients().length) * (WA_RATE[selectedWaTemplate().category] || 5)).toLocaleString()}</b>
                  {' '}({waModal.mode === 'single' ? 1 : waRecipients().length} × ~{WA_RATE[selectedWaTemplate().category] || 5}/msg, {selectedWaTemplate().category})
                </div>
              )}

              {waStatus && (
                <EmailStatusMsg $success={waStatus.includes('Queued')}>
                  {waStatus}
                </EmailStatusMsg>
              )}

              {/* Opt-out manager */}
              <div style={{ marginTop: 14, padding: '12px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#9a3412', marginBottom: 8 }}>Do-not-message list (opt-outs)</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <EmailInput type="text" placeholder="Number to skip (e.g. 923001234567)" value={optOutInput} onChange={(e) => setOptOutInput(e.target.value)} style={{ flex: 1 }} />
                  <button type="button" onClick={addOptOut} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #fdba74', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Add</button>
                </div>
                {optOutMsg && <div style={{ fontSize: 12, color: '#9a3412', marginTop: 6 }}>{optOutMsg}</div>}
              </div>

              {/* Delivery totals */}
              <div style={{ marginTop: 14, padding: '12px 14px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>WhatsApp Delivery (all-time)</span>
                  <button type="button" onClick={() => { loadWhatsappStats(); loadWhatsappFailures(); }} disabled={waStatsLoading}
                    style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}>
                    {waStatsLoading ? '…' : 'Refresh'}
                  </button>
                </div>
                {waStats ? (
                  <div style={{ display: 'flex', gap: 14, fontSize: 13, flexWrap: 'wrap' }}>
                    <span style={{ color: '#16a34a', fontWeight: 600 }} title="WhatsApp accepted the message (not yet proof it reached the phone)">✓ Accepted: {waStats.sent}</span>
                    <span style={{ color: '#0ea5e9', fontWeight: 600 }} title="Confirmed delivered to the phone">📬 Delivered: {waStats.delivered ?? 0}</span>
                    <span style={{ color: '#dc2626', fontWeight: 600 }} title="Rejected at send time">✕ Send failed: {waStats.error}</span>
                    <span style={{ color: '#b91c1c', fontWeight: 600 }} title="Accepted but delivery later failed">⚠ Delivery failed: {waStats.deliveryFailed ?? 0}</span>
                    <span style={{ color: '#d97706', fontWeight: 600 }}>⏳ Pending: {waStats.pending}</span>
                    <span style={{ color: '#64748b', fontWeight: 600 }}>⤼ Skipped: {waStats.skipped}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>{waStatsLoading ? 'Loading…' : 'No data'}</div>
                )}
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  <b>Accepted</b> = WhatsApp took it; <b>Delivered</b> = it reached the phone. Delivery status arrives from Meta a few seconds later — hit Refresh.
                </div>
                {waFailures && waFailures.length > 0 && (
                  <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>Recent failures — with reason</div>
                    <div style={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {waFailures.map((f) => (
                        <div key={f.id} style={{ fontSize: 12, color: '#374151', display: 'flex', gap: 10, justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{f.companyName || f.to}</span>
                          <span style={{ color: '#b91c1c', textAlign: 'right' }}>{f.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </EmailBody>

            <ModalFooter>
              <ModalCancelButton onClick={closeWhatsappModal} disabled={waSending}>
                Cancel
              </ModalCancelButton>
              <EmailSendButton onClick={sendWhatsapp} disabled={waSending || !waTemplateName} style={{ background: '#25D366' }}>
                <FiSend size={14} />
                {waSending ? 'Queuing…' : waModal.mode === 'single' ? 'Send WhatsApp' : 'Send to All'}
              </EmailSendButton>
            </ModalFooter>
          </EmailModalContent>
        </ModalOverlay>
      )}

      {/* Backup result toast */}
      {backupResult && (
        <BackupResultPanel $ok={backupResult.ok}>
          <BackupResultHeader>
            <strong>{backupResult.ok ? 'Backup complete' : 'Backup failed'}</strong>
            <button onClick={() => setBackupResult(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}><FiX /></button>
          </BackupResultHeader>
          {backupResult.ok ? (
            <div style={{ fontSize: 12, lineHeight: 1.5 }}>
              <div>Total docs: <strong>{backupResult.totalDocs}</strong> · Size: <strong>{backupResult.sizeMB} MB</strong></div>
              <div>Companies: <strong>{backupResult.companiesCount}</strong> · Project: <strong>{backupResult.manifest.firebaseProjectId}</strong></div>
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer' }}>Per-collection counts</summary>
                <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 4, fontFamily: 'monospace', fontSize: 11 }}>
                  <div><u>Top-level</u></div>
                  {Object.entries(backupResult.stats.topLevel).map(([k, v]) => (
                    <div key={k}>&nbsp;&nbsp;{k}: {v}</div>
                  ))}
                  {Object.entries(backupResult.stats.perCompany).map(([cid, info]) => (
                    <div key={cid} style={{ marginTop: 4 }}>
                      <u>{info.name}</u>
                      {Object.entries(info.counts).filter(([k]) => k !== '_company').map(([k, v]) => (
                        <div key={k}>&nbsp;&nbsp;{k}: {String(v)}</div>
                      ))}
                    </div>
                  ))}
                  {backupResult.stats.storage.files > 0 && (
                    <div style={{ marginTop: 4 }}><u>Storage</u><div>&nbsp;&nbsp;files: {backupResult.stats.storage.files}</div><div>&nbsp;&nbsp;bytes: {backupResult.stats.storage.bytes}</div></div>
                  )}
                </div>
              </details>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#ffd' }}>{backupResult.error}</div>
          )}
        </BackupResultPanel>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <ModalOverlay onClick={() => !restoreRunning && setShowRestoreModal(false)}>
          <RestoreModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3 style={{ margin: 0 }}><FiUpload style={{ verticalAlign: -3, marginRight: 6 }} />Restore from Backup</h3>
              <button onClick={() => !restoreRunning && setShowRestoreModal(false)} disabled={restoreRunning} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: '#888' }}><FiX /></button>
            </ModalHeader>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <WarningBox>
                <FiAlertTriangle />
                <div>
                  <strong>Read carefully.</strong> Restore writes documents into Firestore using ids from the backup. <em>Append</em> skips existing docs; <em>Replace</em> overwrites them. Neither mode deletes docs that exist live but aren't in the backup. There is no undo.
                </div>
              </WarningBox>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>Backup file (.zip or .json)</label>
                <input
                  type="file"
                  accept=".zip,.json,application/zip,application/json"
                  onChange={(e) => { const f = e.target.files?.[0] || null; setRestoreFile(f); validateRestoreFile(f); }}
                  disabled={restoreRunning}
                  style={{ marginTop: 6, width: '100%' }}
                />
              </div>

              {restoreManifestError && (
                <div style={{ background: '#fee', border: '1px solid #f99', color: '#900', padding: 10, borderRadius: 4, fontSize: 12 }}>
                  <FiXCircle style={{ verticalAlign: -2, marginRight: 4 }} />
                  {restoreManifestError}
                </div>
              )}

              {restoreManifest && !restoreManifestError && (
                <div style={{ background: '#f5f9ff', border: '1px solid #bcd', padding: 10, borderRadius: 4, fontSize: 12, lineHeight: 1.6 }}>
                  <div><strong>Project:</strong> {restoreManifest.firebaseProjectId || '(not stamped — legacy)'}</div>
                  <div><strong>App version:</strong> {restoreManifest.appVersion}</div>
                  <div><strong>Schema:</strong> v{restoreManifest.schemaVersion}</div>
                  <div><strong>Taken:</strong> {restoreManifest.timestamp}</div>
                  <div><strong>Companies:</strong> {restoreManifest.companies?.length ?? restoreManifest.companiesCount ?? '?'}</div>
                  <div><strong>Total docs:</strong> {restoreManifest.totalDocuments ?? '?'}</div>
                </div>
              )}

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>Restore mode</label>
                <div style={{ display: 'flex', gap: 12 }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer', fontSize: 12 }}>
                    <input type="radio" checked={restoreMode === 'append'} onChange={() => setRestoreMode('append')} disabled={restoreRunning} />
                    <span><strong>Append</strong> — skip docs that already exist (safe)</span>
                  </label>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer', fontSize: 12 }}>
                    <input type="radio" checked={restoreMode === 'replace'} onChange={() => setRestoreMode('replace')} disabled={restoreRunning} />
                    <span><strong>Replace</strong> — overwrite existing docs by id</span>
                  </label>
                </div>
              </div>

              {restoreProgress && (
                <div style={{ background: '#fafafa', border: '1px solid #ddd', padding: 8, borderRadius: 4, fontSize: 12, fontFamily: 'monospace' }}>
                  {restoreProgress}
                </div>
              )}

              {restoreResult && (
                <div style={{ background: restoreResult.ok ? '#efffef' : '#fee', border: `1px solid ${restoreResult.ok ? '#9c9' : '#f99'}`, padding: 10, borderRadius: 4, fontSize: 12 }}>
                  {restoreResult.ok ? (
                    <div><strong>Done.</strong> Written: {restoreResult.written} · Skipped: {restoreResult.skipped}</div>
                  ) : (
                    <div><strong>Restore failed:</strong> {restoreResult.error} (Written so far: {restoreResult.written})</div>
                  )}
                </div>
              )}
            </div>
            <ModalFooter>
              <CancelBtn onClick={() => !restoreRunning && setShowRestoreModal(false)} disabled={restoreRunning}>Close</CancelBtn>
              <RefreshButton onClick={handleRestore} disabled={!restoreFile || !restoreManifest || !!restoreManifestError || restoreRunning}>
                <FiUpload className={restoreRunning ? 'spinning' : ''} />
                {restoreRunning ? 'Restoring...' : 'Run Restore'}
              </RefreshButton>
            </ModalFooter>
          </RestoreModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: ${colors.neutral[100]};
  padding: 24px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 600px) {
    gap: 12px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 24px;
  font-weight: 700;
  color: ${colors.primary.main};

  svg {
    font-size: 28px;
  }

  @media (max-width: 600px) {
    font-size: 18px;
    svg { font-size: 22px; }
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
  padding-left: 24px;
  border-left: 2px solid ${colors.neutral[300]};

  @media (max-width: 768px) {
    font-size: 16px;
    padding-left: 12px;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const ToolsMenuWrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 90;
`;

const ToolsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 100;
  min-width: 230px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.16);
  padding: 6px;
  display: flex;
  flex-direction: column;
`;

const ToolsItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: none;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: left;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: ${colors.neutral[50]}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  .spinning { animation: spin 1s linear infinite; }
`;

const ToolsCheckLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 8px 12px;
  font-size: 12px;
  color: ${colors.text.secondary};
  cursor: pointer;
  input { cursor: pointer; }
`;

const ToolsDivider = styled.div`
  height: 1px;
  background: ${colors.neutral[100]};
  margin: 4px 6px;
`;

const ScopeRow = styled.label`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 12px; margin-bottom: 8px; cursor: pointer;
  border: 1px solid ${p => (p.$on ? colors.primary.main : colors.neutral[200])};
  background: ${p => (p.$on ? colors.primary.main + '08' : '#fff')};
  border-radius: 8px;
  input { margin-top: 3px; cursor: pointer; }
  b { display: block; font-size: 14px; color: ${colors.text.primary}; }
  small { display: block; margin-top: 2px; font-size: 12px; color: ${colors.text.secondary}; line-height: 1.4; }
`;

const ScopeNote = styled.div`
  margin-top: 12px; padding: 10px 12px;
  background: ${colors.neutral[50]};
  border-left: 3px solid ${colors.primary.main};
  border-radius: 4px;
  font-size: 12px; color: ${colors.text.secondary}; line-height: 1.5;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  /* Shrink in step with the coloured buttons so the whole header row stays even. */
  @media (max-width: 1100px) {
    padding: 9px 12px;
    font-size: 13px;
  }
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }

  &:hover {
    background: ${colors.neutral[50]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinning {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    font-size: 0;
    gap: 0;
    svg { font-size: 18px; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 600px) {
    padding: 14px;
    gap: 12px;
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const StatContent = styled.div``;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${colors.text.primary};

  @media (max-width: 600px) {
    font-size: 22px;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
`;


const CompaniesSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 600px) {
    padding: 12px 14px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${colors.neutral[100]};
  border-radius: 8px;
  color: ${colors.text.secondary};

  input {
    border: none;
    background: none;
    font-size: 14px;
    width: 200px;
    outline: none;

    &::placeholder {
      color: ${colors.text.disabled};
    }

    @media (max-width: 600px) {
      width: 120px;
      font-size: 13px;
    }
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ResultCount = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.text.secondary};
`;

const FilterToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${props => props.$active ? colors.primary.main + '10' : 'white'};
  color: ${props => props.$hasFilters ? colors.primary.main : colors.text.secondary};
  border: 1px solid ${props => props.$hasFilters ? colors.primary.main + '40' : colors.neutral[300]};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${colors.primary.main}10;
    border-color: ${colors.primary.main}40;
    color: ${colors.primary.main};
  }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${colors.primary.main};
  color: white;
  font-size: 11px;
  font-weight: 600;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 10px;
    padding: 10px 14px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  padding: 6px 28px 6px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 6px;
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  min-width: 120px;

  @media (max-width: 480px) {
    min-width: 100px;
    font-size: 12px;
  }

  &:hover {
    border-color: ${colors.primary.main}60;
  }

  &:focus {
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 2px ${colors.primary.main}15;
  }
`;

const ClearFiltersButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: none;
  color: ${colors.error.main};
  border: 1px solid ${colors.error.main}30;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${colors.error.main}08;
    border-color: ${colors.error.main}60;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[300]};
    border-radius: 4px;
  }
`;

const CompaniesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;

const TableHead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const SortableTh = styled(Th)`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  transition: color 0.15s;

  &:hover {
    color: ${colors.primary.main};
  }
`;

// Day selector chips inside the expanded reads breakdown.
const DayChip = styled.button`
  padding: 3px 10px;
  font-size: 11px;
  font-family: monospace;
  white-space: nowrap;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${props => props.$active ? colors.primary.main : (colors.neutral?.[300] || '#d4d4d4')};
  background: ${props => props.$active ? colors.primary.main : 'white'};
  color: ${props => props.$active ? 'white' : colors.text.secondary};
  transition: all 0.15s;

  &:hover {
    border-color: ${colors.primary.main};
    color: ${props => props.$active ? 'white' : colors.primary.main};
  }
`;

const TableBody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  cursor: pointer;
  background: ${props => props.$selected ? colors.primary.main + '08' : 'white'};
  transition: background 0.2s;

  &:hover {
    background: ${props => props.$selected ? colors.primary.main + '12' : colors.neutral[50]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
`;

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CompanyAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
`;

const CompanyDetails = styled.div``;

const CompanyName = styled.div`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const CompanyEmail = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${props => props.$large ? '6px 12px' : '4px 10px'};
  border-radius: 20px;
  font-size: ${props => props.$large ? '13px' : '11px'};
  font-weight: 600;
  background: ${props => props.$active ? colors.success.main + '20' : colors.error.main + '20'};
  color: ${props => props.$active ? colors.success.main : colors.error.main};
`;

const ShadowBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 6px;
  vertical-align: middle;
  background: ${props => props.$type === 'shadow' ? colors.warning.main + '20' : colors.info.main + '20'};
  color: ${props => props.$type === 'shadow' ? colors.warning.dark : colors.info.main};
`;

const DateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 6px;
  background: ${props => props.$danger ? colors.error.main + '15' : colors.neutral[100]};
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '25' : colors.neutral[200]};
  }
`;


const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: ${colors.text.secondary};
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${colors.text.primary};
  font-weight: 500;
`;


const UsersList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AdminBadge = styled.span`
  padding: 2px 6px;
  background: ${colors.warning.main}20;
  color: ${colors.warning.main};
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
`;

const UserEmail = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
`;

const UserMeta = styled.div`
  text-align: right;
`;

const UserStatus = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.$active ? colors.success.main : colors.error.main};
`;

const UserLastLogin = styled.div`
  font-size: 10px;
  color: ${colors.text.disabled};
`;

const EmptyUsers = styled.div`
  padding: 20px;
  text-align: center;
  color: ${colors.text.secondary};
  font-size: 13px;
`;


const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 18px;
  background: ${props => props.$danger ? colors.error.main : colors.success.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;


const DeleteCompanyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #e53e3e;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: auto;

  &:hover {
    background: #c53030;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${colors.error.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    font-size: 0;
    gap: 0;
    svg { font-size: 18px; }
  }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 24px;
`;

const LoginCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 1100px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  min-height: 560px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LoginLeftSection = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #0f3460 0%, #1a1a2e 100%);
  padding: 48px;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 32px;
  }
`;

const LoginBrandSection = styled.div`
  margin-bottom: 40px;
`;

const LoginLogo = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
`;

const LoginTagline = styled.p`
  font-size: 18px;
  opacity: 0.8;
  margin: 0;
`;

const LoginFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LoginFeature = styled.div`
  display: flex;
  align-items: center;
  font-size: 15px;
  opacity: 0.9;

  &:before {
    content: '\\2713';
    margin-right: 12px;
    font-weight: 700;
    font-size: 16px;
    color: #4ecca3;
  }
`;

const LoginRightSection = styled.div`
  flex: 1;
  padding: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 32px;
  }
`;

const LoginFormContainer = styled.div`
  width: 100%;
  max-width: 380px;
`;

const LoginFormHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const LoginShieldIcon = styled.div`
  font-size: 48px;
  color: #0f3460;
  margin-bottom: 16px;
`;

const LoginTitle = styled.h2`
  font-size: 26px;
  font-weight: 700;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const LoginSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const LoginErrorMessage = styled.div`
  background: ${colors.error.main}15;
  color: ${colors.error.main};
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  border-left: 3px solid ${colors.error.main};
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoginInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LoginLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const LoginInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const LoginIconWrapper = styled.div`
  position: absolute;
  left: 14px;
  color: ${colors.text.secondary};
  display: flex;
  align-items: center;
  font-size: 16px;
`;

const LoginInput = styled.input`
  width: 100%;
  padding: 12px 14px 12px 42px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 10px;
  font-size: 14px;
  transition: all 0.2s;
  background: ${colors.neutral[50]};

  &:focus {
    border-color: #0f3460;
    box-shadow: 0 0 0 3px rgba(15, 52, 96, 0.15);
    background: white;
    outline: none;
  }

  &::placeholder {
    color: ${colors.text.disabled};
  }
`;

const LoginPasswordToggle = styled.button`
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 16px;
  padding: 0;

  &:hover {
    color: #0f3460;
  }
`;

const LoginSubmitButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px;
  background: linear-gradient(135deg, #0f3460 0%, #1a1a2e 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(15, 52, 96, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  color: ${props => props.$active ? 'white' : colors.text.secondary};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? colors.primary.main : colors.neutral[100]};
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 13px;
    gap: 4px;
  }
`;

const SignupsSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SignupBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  text-transform: capitalize;
`;

const ExpandedDetails = styled.div`
  padding: 20px 24px;
  background: ${colors.neutral[50]};
  border-top: 1px solid ${colors.neutral[200]};
  border-bottom: 1px solid ${colors.neutral[200]};

  @media (max-width: 768px) {
    padding: 14px 12px;
  }
`;

const ExpandedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ExpandedColumn = styled.div``;

const ExpandedTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primary.main};
  text-transform: uppercase;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid ${colors.primary.main}30;
`;

const ExpandedFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${colors.neutral[200]};
`;

const FooterMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const FooterMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FooterActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const FooterActionsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterActionsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FooterDangerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px dashed ${colors.error.main}30;
  flex-wrap: wrap;
`;






const TransactionBreakdown = styled.div`
  max-height: 320px;
  overflow-y: auto;
`;

const TransactionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  margin-bottom: 4px;
  border-bottom: 2px solid ${colors.neutral[200]};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;

  span:first-child { flex: 1; }
  span:nth-child(2) { width: 50px; text-align: center; }
  span:nth-child(3) { width: 110px; text-align: right; }
`;

const TransactionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }
`;

const TransactionType = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.text.primary};
`;


const TransactionCount = styled.div`
  width: 50px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const TransactionAmount = styled.div`
  width: 110px;
  text-align: right;
  font-size: 12px;
  color: ${colors.text.secondary};
`;



const UserTransactionCount = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.primary.main};
`;

// Clear Data Buttons

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${props => props.$severe ? colors.error.main : 'white'};
  color: ${props => props.$severe ? 'white' : colors.error.main};
  border: 1px solid ${colors.error.main};
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$severe ? colors.error.dark || '#c0392b' : colors.error.main + '15'};
  }
`;

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 12px;
    align-items: flex-start;
    padding-top: 24px;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ModalWarningIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${colors.error.main}15;
  color: ${colors.error.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
  flex: 1;
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  font-size: 20px;

  &:hover {
    color: ${colors.text.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const WarningBox = styled.div`
  background: ${colors.error.main}10;
  border: 1px solid ${colors.error.main}30;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 20px;

  strong {
    color: ${colors.error.main};
    font-size: 14px;
  }

  p {
    margin: 8px 0 0;
    font-size: 13px;
    color: ${colors.text.secondary};
    line-height: 1.5;
  }
`;

const ModalCompanyName = styled.div`
  padding: 12px 16px;
  background: ${colors.neutral[100]};
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  color: ${colors.text.secondary};

  strong {
    color: ${colors.text.primary};
  }
`;

const ModalInputGroup = styled.div`
  margin-bottom: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 6px;

  strong {
    color: ${colors.error.main};
  }
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.error.main};
    box-shadow: 0 0 0 3px ${colors.error.main}15;
    outline: none;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const ModalError = styled.div`
  background: ${colors.error.main}15;
  color: ${colors.error.main};
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 12px;
  border-left: 3px solid ${colors.error.main};
`;

const ModalProgress = styled.div`
  background: ${colors.info.main}15;
  color: ${colors.info.main};
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 12px;
  border-left: 3px solid ${colors.info.main};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
  flex-wrap: wrap;

  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`;

const ModalCancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.neutral[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalConfirmButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.error.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Manage Modules Button
const ManageModulesButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

// Modules Modal
const ModulesModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ModulesIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const ModulesCompanyLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-top: 2px;
`;

const ModulesCounter = styled.div`
  margin-left: auto;
  padding: 6px 12px;
  background: ${colors.primary.main}12;
  color: ${colors.primary.main};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const ModulesBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[300]};
    border-radius: 3px;
  }
`;

const ModulesGroupSection = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModulesGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ModulesGroupName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  text-transform: uppercase;
`;

const ModulesGroupCount = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
`;

const GroupToggleButtons = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
`;

const GroupToggleBtn = styled.button`
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid ${props => props.$active ? colors.primary.main : colors.neutral[300]};
  background: ${props => props.$active ? colors.primary.main + '15' : 'white'};
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: ${colors.primary.main};
    color: ${colors.primary.main};
  }
`;

const ModulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ModuleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  background: ${props => props.$disabled ? colors.neutral[100] : 'white'};
  border: 1px solid ${colors.neutral[200]};
  transition: all 0.15s;
  opacity: ${props => props.$disabled ? 0.7 : 1};

  &:hover {
    ${props => !props.$disabled && `
      border-color: ${colors.primary.main};
      background: ${colors.primary.main}08;
    `}
  }
`;

const ModuleCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: inherit;
  accent-color: ${colors.primary.main};
  flex-shrink: 0;
`;

const ModuleLabel = styled.span`
  font-size: 12px;
  color: ${props => props.$disabled ? colors.text.secondary : colors.text.primary};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AlwaysOnBadge = styled.span`
  padding: 2px 6px;
  background: ${colors.success.main}20;
  color: ${colors.success.main};
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const ModulesSaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Email Styled Components
// Subscription Styled Components
const SubBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$color || colors.text.secondary};
  background: ${props => (props.$color || colors.text.secondary) + '15'};
  border: 1px solid ${props => (props.$color || colors.text.secondary) + '30'};
  white-space: nowrap;
`;

const UpdateSubButton = styled.button`
  margin-top: 12px;
  padding: 8px 16px;
  background: ${colors.primary.main};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

const SubModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  overflow: hidden;

  @media (max-width: 700px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const SubModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const SubModalTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const SubModalBody = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 3px; }
`;

const SubModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
  background: #fff;
`;

// Two-column grid for paired short fields inside the modal
const SubFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

// Collapsible section header for grouping fields inside the modal
const SubSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
  padding-bottom: 4px;
  border-bottom: 1px solid ${colors.neutral[200]};
  margin-top: 4px;
`;

const SubFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SubFieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
`;

const SubFieldValue = styled.div`
  font-size: 13px;
  color: ${colors.text.primary};
  padding: 9px 12px;
  background: #f8f8f8;
  border-radius: 6px;
`;

const SubSelect = styled.select`
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }
`;

const SubInput = styled.input`
  padding: 9px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${colors.primary.main}; }
`;

const SubSaveButton = styled.button`
  padding: 9px 20px;
  background: ${colors.primary.main};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.9; }
`;

const BulkEmailButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    font-size: 0;
    gap: 0;
    svg { font-size: 18px; }
  }
`;

const SendEmailButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: white;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${colors.primary.main};
    color: white;
  }
`;

const EmailModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 750px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const EmailIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const EmailBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
`;

const EmailFieldGroup = styled.div`
  margin-bottom: 16px;
`;

const EmailLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const EmailHint = styled.div`
  font-size: 11px;
  color: ${colors.text.disabled};
  margin-top: 4px;
`;

const EmailSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    border-color: ${colors.primary.main};
    outline: none;
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.primary.main};
    outline: none;
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
  }
`;

const EmailTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 13px;
  font-family: monospace;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    border-color: ${colors.primary.main};
    outline: none;
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
  }
`;

const EmailPreview = styled.div`
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  padding: 16px;
  background: ${colors.neutral[50]};
  max-height: 300px;
  overflow-y: auto;
`;

const EmailStatusMsg = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 8px;
  border-left: 3px solid;
  background: ${props => props.$success ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$success ? colors.success.main : colors.error.main};
  border-left-color: ${props => props.$success ? colors.success.main : colors.error.main};
`;

const EmailSendButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Usage tab styled components
const UsagePeriodBar = styled.div`
  display: flex;
  gap: 4px;
  background: ${colors.neutral[100]};
  border-radius: 8px;
  padding: 3px;
`;

const PeriodButton = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  color: ${props => props.$active ? 'white' : colors.text.secondary};

  &:hover {
    background: ${props => props.$active ? colors.primary.main : colors.neutral[200]};
  }
`;

const UsageSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const UsageSummaryCard = styled.div`
  background: ${colors.neutral[50]};
  border-radius: 10px;
  padding: 16px;
  text-align: center;
`;

const UsageSummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.primary.main};
  margin-bottom: 4px;
`;

const UsageSummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  font-weight: 500;
`;

const UsageChartContainer = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const UsageChartTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 16px;
`;

// ─── Revenue Tab ──────────────────────────────────────────────────────────────
const RevKpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};

  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

const RevKpiCard = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-left: 3px solid ${props => props.$accent || colors.primary.main};
  border-radius: 8px;
  padding: 14px 16px;
`;

const RevKpiLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
`;

const RevKpiValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.text.primary};
  font-family: 'SF Mono', 'Consolas', monospace;
  line-height: 1.2;
`;

const RevKpiSub = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
  margin-top: 4px;
`;

// ── Revenue client-table controls + sortable header ──
const RevTableControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 6px 0;
  flex-wrap: wrap;
`;

const RevSearchInput = styled.input`
  flex: 1 1 260px;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 13px;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const RevFilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const RevTableCount = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  font-weight: 600;
  margin-left: auto;
`;

const SortTh = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  border-bottom: 2px solid ${colors.neutral[200]};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  &:hover { color: ${colors.primary.main}; }
`;

const RevTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};

  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const RevPanel = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 10px;
  padding: 16px 20px;
  margin: 16px 20px 0 20px;

  &:last-child { margin-bottom: 20px; }
`;

const RevPanelTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const RevPanelSub = styled.div`
  font-size: 12px;
  color: ${colors.text.tertiary};
  margin-bottom: 14px;
`;

const RevEmpty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${colors.text.secondary};
  font-size: 13px;
  background: ${colors.neutral[50]};
  border-radius: 8px;
`;

const RevPlanRow = styled.div`
  display: flex;
  gap: 6px;
  height: 44px;
  margin-top: 10px;
  margin-bottom: 16px;
`;

const RevPlanBar = styled.div`
  background: ${props => props.$color};
  color: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  padding: 0 8px;
  white-space: nowrap;
`;

const RevStatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const RevStat = styled.div`
  background: ${colors.neutral[50]};
  border-radius: 8px;
  padding: 10px 12px;
`;

const RevStatLabel = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
`;

const RevStatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.$danger ? colors.error.main : colors.text.primary};
  font-family: 'SF Mono', 'Consolas', monospace;
`;

const RevRenewalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;

const RevRenewalCard = styled.div`
  background: ${colors.neutral[50]};
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  padding: 12px;
  text-align: center;
`;

const RevRenewalMonth = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
`;

const RevRenewalCount = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.primary.main};
  font-family: 'SF Mono', 'Consolas', monospace;
`;

const RevRenewalAmount = styled.div`
  font-size: 12px;
  color: ${colors.text.primary};
  margin-top: 2px;
  font-family: 'SF Mono', 'Consolas', monospace;
`;

const RevYearBlock = styled.div`
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
`;

const RevYearHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: ${colors.primary.main}0D;
  border-left: 3px solid ${colors.primary.main};
  border-radius: 6px;
`;

const RevYearTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${colors.text.primary};
`;

const RevYearMeta = styled.div`
  font-size: 13px;
  color: ${colors.text.secondary};
  font-family: 'SF Mono', 'Consolas', monospace;
`;

// ─── Backup / Restore styled components ─────────────────────────────────
const BackupResultPanel = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  background: ${({ $ok }) => ($ok ? '#1f7a3a' : '#a13030')};
  color: white;
  padding: 12px 14px;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  max-width: 460px;
  min-width: 320px;
`;

const BackupResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
`;

const RestoreModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 16px 48px rgba(0,0,0,0.2);

  h3 {
    font-size: 16px;
    color: #222;
  }
`;

const CancelBtn = styled.button`
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  &:hover { background: #ebebeb; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ─── Drill-down sub-tabs (inside each expanded company row) ───────────

const DetailTabBar = styled.div`
  display: flex;
  gap: 2px;
  padding: 0 20px;
  margin-top: 12px;
  margin-bottom: 4px;
  border-bottom: 2px solid ${colors.neutral[200]};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { height: 3px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 3px; }

  @media (max-width: 768px) {
    padding: 0 14px;
  }
`;

const DetailTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? colors.primary.main : 'transparent'};
  margin-bottom: -2px;
  font-size: 13px;
  font-weight: ${props => props.$active ? 700 : 500};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;

  &:hover {
    color: ${colors.primary.main};
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

// ─── Stacked Tables (drill-down) ───────────────────────────────────────
// Each StackedSection is a labelled horizontal table inside the company
// drill-down panel. Below 768px the sub-table collapses to a card with
// label/value rows (uses `data-label` from the SubTd to render the label).

const StackedSection = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const StackedTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
  margin-bottom: 8px;
`;

const StackedHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 8px;
`;

const StackedHeaderAction = styled.button`
  background: ${colors.primary.main};
  color: white;
  border: none;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: ${colors.primary.dark || colors.primary.main}; opacity: 0.92; }
`;

const SubTableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 6px;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 4px; }

  @media (max-width: 768px) {
    border: none;
  }
`;

const SubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;

  /* On mobile: each row becomes a labelled card. SubTd's data-label
     attribute is rendered before the value via ::before. */
  @media (max-width: 768px) {
    display: block;
    background: transparent;

    thead {
      display: none;
    }
    tbody {
      display: block;
    }
    tr {
      display: block;
      background: white;
      border: 1px solid ${colors.neutral[200]};
      border-radius: 6px;
      padding: 10px 12px;
      margin-bottom: 8px;

      &:last-child { margin-bottom: 0; }
    }
  }
`;

const SubTh = styled.th`
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${colors.text.secondary};
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const SubTd = styled.td`
  padding: 10px 12px;
  font-size: 13px;
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.neutral[100]};
  vertical-align: middle;

  tr:last-child & { border-bottom: none; }

  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 6px 0;
    border-bottom: 1px solid ${colors.neutral[100]};
    text-align: right;

    &::before {
      content: attr(data-label);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      color: ${colors.text.secondary};
      flex-shrink: 0;
    }

    &:last-child { border-bottom: none; }
  }
`;

// ─── Revenue tab — Lifecycle cards + Split bar ────────────────────────

const LifecycleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const LifecycleCard = styled.div`
  padding: 16px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-left: 4px solid ${props => props.$accent || colors.primary.main};
  border-radius: 6px;
`;

const LifecycleValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: ${colors.text.primary};
  font-family: monospace;
  line-height: 1;
  margin-bottom: 4px;
`;

const LifecycleLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.text.primary};
  margin-bottom: 4px;
`;

const LifecycleSub = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  line-height: 1.4;
`;

const SplitBarRow = styled.div`
  display: flex;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  margin-top: 12px;
  margin-bottom: 12px;
  background: ${colors.neutral[100]};

  @media (max-width: 600px) {
    flex-direction: column;
    height: auto;
  }
`;

const SplitBarSegment = styled.div`
  background: ${props => props.$color};
  color: white;
  padding: 10px 14px;
  min-width: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: flex 0.3s;

  @media (max-width: 600px) {
    flex: 1 !important;
    padding: 8px 12px;
    text-align: center;
  }
`;

const SplitBarLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  opacity: 0.92;
`;

const SplitBarAmount = styled.div`
  font-size: 18px;
  font-weight: 700;
  font-family: monospace;
  margin-top: 2px;
`;

// ─── Payment History editor (inside Subscription modal) ──────────────

const PaymentHistoryWrap = styled.div`
  border: 1px solid ${colors.neutral[200]};
  border-radius: 6px;
  padding: 10px;
  background: white;
`;

const PaymentHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
  max-height: 140px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 3px; }
`;

const PaymentHistoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: ${colors.neutral[50]};
  border-radius: 4px;
  font-size: 12px;
`;

const PaymentTypeBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  min-width: 60px;
  text-align: center;
  background: ${props => {
    if (props.$type === 'setup') return colors.info.main;
    if (props.$type === 'renewal') return colors.success.main;
    if (props.$type === 'discount') return colors.warning.main;
    if (props.$type === 'refund') return colors.error.main;
    return colors.neutral[400] || '#9ca3af';
  }};
  color: white;
`;

const PaymentDate = styled.span`
  font-size: 12px;
  color: ${colors.text.secondary};
  min-width: 100px;
`;

const PaymentAmount = styled.span`
  font-family: monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${colors.text.primary};
  min-width: 110px;
  text-align: right;
`;

const PaymentNotes = styled.span`
  flex: 1;
  font-size: 11px;
  color: ${colors.text.secondary};
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PaymentDelete = styled.button`
  background: none;
  border: none;
  color: ${colors.error.main};
  cursor: pointer;
  padding: 4px;
  border-radius: 3px;

  &:hover { background: ${colors.error.main}15; }
`;

const PaymentDraftRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid ${colors.neutral[200]};
  flex-wrap: wrap;
`;

// ─── Module Matrix v2 — Summary + drill (By Company / By Module) ─────

const ModeToggleGroup = styled.div`
  display: inline-flex;
  background: ${colors.neutral[100]};
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
`;

const ModeToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${props => props.$active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'};
  transition: all 0.15s;

  &:hover { color: ${colors.primary.main}; }
`;

const InsightPillRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  background: ${colors.neutral[50]};
  border-bottom: 1px solid ${colors.neutral[200]};
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 10px 14px;
  }
`;

const InsightPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => {
    if (!props.$active) return 'white';
    if (props.$tone === 'universal') return colors.success.main;
    if (props.$tone === 'niche') return colors.warning.main;
    if (props.$tone === 'unused') return colors.error.main;
    return colors.primary.main;
  }};
  color: ${props => props.$active ? 'white' : colors.text.primary};
  border: 1px solid ${props => {
    if (!props.$active) return colors.neutral[300];
    if (props.$tone === 'universal') return colors.success.main;
    if (props.$tone === 'niche') return colors.warning.main;
    if (props.$tone === 'unused') return colors.error.main;
    return colors.primary.main;
  }};
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: ${props => {
      if (props.$tone === 'universal') return colors.success.main;
      if (props.$tone === 'niche') return colors.warning.main;
      if (props.$tone === 'unused') return colors.error.main;
      return colors.primary.main;
    }};
  }
`;

const PillCount = styled.span`
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  background: rgba(0,0,0,0.12);
  border-radius: 8px;
`;

// Per-group count cell inside the By Company table
const GroupCountCell = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 80px;
`;

const GroupCountLabel = styled.div`
  font-family: monospace;
  font-size: 12px;
  font-weight: 700;
  color: ${props => {
    if (props.$intensity === 'empty') return colors.text.disabled;
    if (props.$intensity === 'high') return colors.success.main;
    if (props.$intensity === 'medium') return colors.primary.main;
    return colors.text.secondary;
  }};
`;

const GroupCountBar = styled.div`
  width: 80px;
  height: 5px;
  background: ${colors.neutral[200]};
  border-radius: 3px;
  overflow: hidden;
`;

const GroupCountFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.$intensity === 'high') return colors.success.main;
    if (props.$intensity === 'medium') return colors.primary.main;
    if (props.$intensity === 'low') return colors.warning.main;
    return colors.neutral[300];
  }};
  border-radius: 3px;
  transition: width 0.2s;
`;

const TotalCount = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.primary.main};
`;

// Drill-down (inside the By Company expanded row)
const DrillDownContainer = styled.div`
  padding: 14px 20px;
  background: ${colors.neutral[50]};
  border-top: 1px solid ${colors.neutral[200]};
  border-bottom: 1px solid ${colors.neutral[200]};

  @media (max-width: 600px) {
    padding: 12px 14px;
  }
`;

const DrillDownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 12px;
  color: ${colors.text.secondary};

  strong { color: ${colors.text.primary}; }
`;

const DrillDownAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover { opacity: 0.9; }
`;

const DrillDownGroups = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
`;

const DrillDownGroup = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 6px;
  padding: 10px 12px;
`;

const DrillDownGroupTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.text.primary};
  padding-bottom: 6px;
  margin-bottom: 6px;
  border-bottom: 1px solid ${colors.neutral[100]};
`;

const DrillDownGroupCount = styled.span`
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 2px 6px;
  border-radius: 3px;
`;

const DrillDownModules = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ModuleChip = styled.span`
  display: inline-block;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 3px;
  background: ${props => props.$enabled ? `${colors.success.main}15` : colors.neutral[100]};
  color: ${props => props.$enabled ? colors.success.main : colors.text.disabled};
  border: 1px solid ${props => props.$enabled ? `${colors.success.main}40` : colors.neutral[200]};
  white-space: nowrap;
`;

// By Module view
const ByModuleContainer = styled.div`
  padding: 0;
`;

const ByModuleGroup = styled.div`
  border-bottom: 1px solid ${colors.neutral[200]};

  &:last-child { border-bottom: none; }
`;

const ByModuleGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${colors.primary.main};
  color: white;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: sticky;
  top: 0;
  z-index: 1;

  @media (max-width: 600px) {
    padding: 10px 14px;
  }
`;

const ByModuleGroupCount = styled.span`
  font-family: monospace;
  font-size: 11px;
  font-weight: 600;
  background: rgba(255,255,255,0.18);
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: none;
  letter-spacing: 0;
`;

const ByModuleRow = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};

  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.neutral[50]}; }

  @media (max-width: 600px) {
    padding: 10px 14px;
  }
`;

const ByModuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const ByModuleName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const ByModuleStats = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
`;

const ByModuleCount = styled.span`
  font-family: monospace;
  font-size: 13px;
  font-weight: 700;
  color: ${colors.primary.main};
  min-width: 50px;
  text-align: right;
`;

const ByModuleBar = styled.div`
  flex: 1;
  height: 6px;
  background: ${colors.neutral[200]};
  border-radius: 3px;
  overflow: hidden;
  min-width: 100px;
`;

const ByModuleBarFill = styled.div`
  height: 100%;
  background: ${colors.primary.main};
  border-radius: 3px;
  transition: width 0.2s;
`;

const ByModuleAdopters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const CompanyChip = styled.button`
  display: inline-block;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}30;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: ${colors.primary.main};
    color: white;
  }
`;

const InsightTag = styled.span`
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${props => {
    if (props.$tone === 'universal') return colors.success.main;
    if (props.$tone === 'niche') return colors.warning.main;
    if (props.$tone === 'unused') return colors.error.main;
    return colors.neutral[400] || '#9ca3af';
  }};
  color: white;
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: ${colors.text.secondary};
  font-size: 14px;
  background: ${colors.neutral[50]};
`;

// (Old wide-pivot Matrix styled-components removed — superseded by the
//  Summary + drill design above. See ModeToggleGroup / GroupCountCell /
//  ByModuleGroup et al.)

export default SuperAdmin;
